import React, { useEffect } from 'react'

/**
 * StyleOverrides - appends a <style data-pb-style-overrides> block
 * to document.head LAST (after Pentabay.html's inline <style>) so
 * equal-specificity rules win the source-order cascade tiebreak.
 *
 * Refactored 2026-05-20: responsive overhaul split into 4 partials
 * (typography → cards/grids → controls → topbar/footer), then the
 * preserved non-responsive baseline (text-selection, cookie notice,
 * founder monogram, Lenis fix, contact-pinned).
 *
 * No !important except where overriding inline style="" attributes
 * (contact-stage-track transform - JS writes inline style every
 * scroll frame and the only override path is !important).
 */
const OVERRIDES = `
/* ============================================================
 * Footer office blocks - icon + text rows (address pin, phone, mail).
 * Shared by the home footer (Pentabay.html) and the JS-injected sub-page
 * footers (pentabay-pages.js / pentabay-subpages.js). Icons inherit the
 * muted footer text colour via currentColor; links keep --pb-fg.
 * ============================================================ */
.pb-office { display: flex; flex-direction: column; gap: 9px; max-width: 20rem; }
/* Rigid icon | text grid so every icon sits in one clean column and every
   line of text lines up in the next - regardless of single- vs multi-line
   rows (address wraps, phone/email are one line). */
.pb-office-row { display: grid; grid-template-columns: 16px 1fr; align-items: start; column-gap: 10px; line-height: 1.5; }
.pb-office-row > svg { width: 14px; height: 14px; margin-top: 3px; opacity: 0.7; justify-self: center; }
/* The phone / email links otherwise inherit a 44px flex tap-target height that
   vertically-centres their text, dropping it ~12px below the icon (which pins
   to the row's first line). Neutralise it so single-line contact rows align
   exactly like the multi-line address rows. */
.pb-office-row a { min-height: 0; display: inline; align-items: initial; }
.pb-office-row a:hover { text-decoration: underline; }

/* CTA button rows: wrap on narrow screens so a second button (e.g.
   "See our work") never gets clipped off-screen. The source centres them
   with "flex justify-center gap-3" and NO flex-wrap; scope to flex rows that
   hold 2+ buttons so single-button rows and other flex layouts are untouched.
   (Wrap only engages when the buttons don't fit, so wider screens are safe.) */
.flex:has(> .btn ~ .btn) { flex-wrap: wrap; }

/* Mobile footer: pack the nav columns 2-up instead of one long single-column
   stack (which ran ~3200px tall). The logo / office cell (lg:col-span-2)
   spans the full width above the paired nav columns. */
@media (max-width: 639.98px) {
  footer.shell > .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1.25rem;
    row-gap: 1.75rem;
  }
  footer.shell > .grid > .lg\\:col-span-2 { grid-column: 1 / -1; }
  /* The office address text shipped as faint --pb-mist; bump it to the
     primary text colour on mobile so it's clearly legible. Icons keep their
     0.7 opacity so they stay a touch softer than the text. */
  .pb-office-row { color: var(--pb-fg); align-items: start; }
}

/* Per-client testimonial star rating (mirrors the aggregate stars). */
.testimonial .ts-stars { display: flex; gap: 3px; margin: 10px 0 2px; }
.testimonial .ts-stars svg { display: block; }

/* ============================================================
 * responsive-typography.css - generated 2026-05-20
 * Phase 2d: Fluid type ramp + section rhythm.
 *
 * No !important. CSS appended LAST via StyleOverrides → wins
 * source-order tiebreak on equal-specificity rules in Pentabay.html.
 *
 * Replaces:
 *   - Pentabay.html lines 78-88 (vh-aware .t-display/.t-lead)
 *   - Pentabay.html line 190-197 (desktop .t-* ramp)
 *   - StyleOverrides.jsx lines 702-757 (mobile !important ramp)
 *   - StyleOverrides.jsx lines 761-764 (<=380px ramp)
 *   - StyleOverrides.jsx lines 769-778 (tablet ramp)
 *   - StyleOverrides.jsx lines 859-872 (section py-* caps)
 *
 * Preserves (does not regress):
 *   - Pentabay.html lines 1793-1807 (xl+ upper bumps still win
 *     via min-width media queries that come into play at >=1440)
 *   - Animation rules on .word, .btn, .card etc. - untouched
 *   - --pb-* theme tokens - untouched
 * ============================================================ */

/* === FLUID TYPE RAMP ============================================
 * clamp(min, slope*vw + intercept, max)
 *
 * Slope chosen so the variable term hits the target SIZE at the
 * target VIEWPORT, then min/max cap the endpoints. Body fallback
 * stays system Open Sans - only sizes/leading/tracking.
 *
 * Specificity (0,1,0) matches source. Source order: this CSS is
 * loaded after every <style> block in Pentabay.html, so we win.
 * ================================================================ */
.t-display {
  font-size: clamp(1.875rem, 3.125vw + 1.25rem, 5rem);
  line-height: 1.05;
  letter-spacing: -0.02em;
  text-wrap: balance;
}
/* Home hero headline: the added "Custom" prefix pushed it to three lines.
   Cap the size a little lower and balance the wrap so it lands on two.
   2026-08-07: brought down again alongside the sub-page hero <h1> (see
   HERO HEADLINE SIZE further down this file) so every hero on the site
   reads at one scale. Still a step larger than the sub-page heroes - it
   is the display headline and it only ever runs to two lines - but the
   cap drops 3.5rem -> 2.75rem (56 -> 44px): 1440 goes 50.6 -> 43.4px,
   1024 goes 40.6 -> 35.5px. Floor lifts to 1.75rem so phones barely move
   (30 -> 28px). Only this <h1>; other .t-display headings are untouched. */
#page-home [data-hero-h1] {
  font-size: clamp(1.75rem, 1.9vw + 1rem, 2.75rem);
  text-wrap: balance;
}
.t-h1 {
  font-size: clamp(1.5rem, 2vw + 1.1rem, 3.5rem);
  line-height: 1.12;
  letter-spacing: -0.02em;
}
.t-h2 {
  font-size: clamp(1.25rem, 1.25vw + 1rem, 2.5rem);
  line-height: 1.18;
  letter-spacing: -0.015em;
}
.t-h3 {
  font-size: clamp(1.0625rem, 0.7vw + 0.92rem, 1.75rem);
  line-height: 1.28;
  letter-spacing: -0.01em;
}
.t-eyebrow {
  font-size: clamp(0.6875rem, 0.125vw + 0.65rem, 0.8125rem);
  line-height: 1;
  letter-spacing: 0.16em;
}

/* Bare h1/h2/h3 (no .t-* class) - keep modest fluid scaling so
 * embedded headings inside cards/articles stay legible. */
h1:not([class*="t-"]) { font-size: clamp(1.5rem, 1.8vw + 1.1rem, 3rem); line-height: 1.15; }
h2:not([class*="t-"]) { font-size: clamp(1.25rem, 1.2vw + 1rem, 2.25rem); line-height: 1.2; }
h3:not([class*="t-"]) { font-size: clamp(1.0625rem, 0.65vw + 0.95rem, 1.625rem); line-height: 1.3; }

/* === VERY-NARROW VIEWPORTS (iPhone SE / Android compact) =========
 * The main clamp() already lands at 30px/24px at 320 (its floors).
 * This override DOES NOT shrink further - it only tightens leading
 * and letter-spacing so 2-3 line hero copy fits inside the clip.
 * Font-size floors held at 30px (.t-display) and 24px (.t-h1) per
 * the accessibility spec.
 * ================================================================ */
@media (max-width: 380px) {
  .t-display {
    line-height: 1.04;
    letter-spacing: -0.025em;
  }
  .t-h1 {
    line-height: 1.14;
  }
  .t-h2 {
    line-height: 1.2;
  }
  .t-h3 {
    line-height: 1.3;
  }
}

/* === MOBILE TIGHTER LEADING + SAFETY WRAPS (<=640px) =============
 * Eyebrow rule preserves the 0.12em tracking from the deleted
 * StyleOverrides block (so two-line eyebrows still align). Body /
 * lead leading tightens to 1.5 so paragraphs don't sprawl on small
 * screens.
 * ================================================================ */
@media (max-width: 640px) {
  .t-eyebrow {
    font-size: 10px;
    letter-spacing: 0.04em;
    gap: 0.5rem;
    white-space: nowrap;
    word-break: normal;
  }
  .t-eyebrow::before {
    width: 12px;
  }
  .t-body { line-height: 1.55; }
  .t-lead { line-height: 1.5; }
}
/* Very-narrow viewports - ensure the longest hero eyebrow
   "PENTABAY · NORTH AMERICA · CA / US / IN" (43 chars) fits inside the
   288px content box at 320 without wrapping. */
@media (max-width: 380px) {
  .t-eyebrow {
    font-size: 9.5px;
    letter-spacing: 0.02em;
    gap: 0.375rem;
  }
  .t-eyebrow::before {
    width: 8px;
  }
}
/* Keep base eyebrow on a single line at every viewport. Mobile rules
   above re-affirm nowrap for cascade clarity. */
.t-eyebrow {
  white-space: nowrap;
}

/* === SECTION VERTICAL RHYTHM ====================================
 * Tailwind utilities py-20..py-40 = 5rem..10rem (80..160px).
 * On phones that produces 12,000-18,000px tall pages; cap to a
 * sane 2.5rem (40px). At md (768) lift to ~3.75rem (60px). At
 * lg (1024) and up, leave source defaults intact.
 *
 * Specificity (0,2,1) "section.py-20" beats source's (0,1,1)
 * ".py-20" - but we don't even need that, since the utility's
 * source rule lives in Tailwind's preflight at equal specificity
 * and we load LATER. Belt-and-braces with the section qualifier.
 * ================================================================ */
@media (max-width: 767px) {
  section.py-20,
  section.py-24,
  section.py-28,
  section.py-32,
  section.py-36,
  section.py-40 {
    padding-top: 2.5rem;
    padding-bottom: 2.5rem;
  }
  .shell.py-20,
  .shell.py-24,
  .shell.py-28,
  .shell.py-32,
  .shell.py-36,
  .shell.py-40 {
    padding-top: 2.5rem;
    padding-bottom: 2.5rem;
  }
  /* Hero shell: source sets min-height:100svh which jails the
   * landing fold. Free it, and shrink hero shell padding to keep
   * the first scroll-line above the fold on small phones. */
  .hero-shell { min-height: 0; }
  .hero-shell .shell {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }
}
@media (min-width: 768px) and (max-width: 1023px) {
  section.py-20,
  section.py-24,
  section.py-28,
  section.py-32,
  section.py-36,
  section.py-40 {
    padding-top: 3.75rem;
    padding-bottom: 3.75rem;
  }
  .shell.py-20,
  .shell.py-24,
  .shell.py-28,
  .shell.py-32,
  .shell.py-36,
  .shell.py-40 {
    padding-top: 3.75rem;
    padding-bottom: 3.75rem;
  }
}

/* === SHELL HORIZONTAL PADDING ESCALATOR =========================
 * Source has --shell-x: clamp(1.25rem, 3vw, 3rem) plus min-width
 * bumps at 1440/1600/1920/2560. Below md the source minimum
 * (1.25rem = 20px) is OK; we tighten further to 16px below sm so
 * the content meets the iOS Safari safe-area inset. From sm/md/lg
 * we step 24 → 32 → 48 then hand off to source's --shell-x.
 *
 * NOTE: we set padding-inline on the inner max-w-* containers (the
 * source uses these as the horizontal gutter inside <section>).
 * Source rule is \`.max-w-7xl { max-width: 80rem; margin-inline:
 * auto }\` (Tailwind) at (0,1,0). Our (0,2,0) selector wins on
 * specificity too - safe either way.
 * ================================================================ */
section .max-w-7xl,
section .max-w-6xl,
section .max-w-5xl {
  padding-inline: 1rem;
}
@media (min-width: 640px) {
  section .max-w-7xl,
  section .max-w-6xl,
  section .max-w-5xl {
    padding-inline: 1.5rem;
  }
}
@media (min-width: 768px) {
  section .max-w-7xl,
  section .max-w-6xl,
  section .max-w-5xl {
    padding-inline: 2rem;
  }
}
@media (min-width: 1024px) {
  section .max-w-7xl,
  section .max-w-6xl,
  section .max-w-5xl {
    padding-inline: 3rem;
  }
}
/* At xl+ we hand off entirely - let --shell-x govern parent .shell
 * gutters, and let the max-w-* containers shrink to their natural
 * inner padding (don't fight the source's xl ramp at 1440+). */
@media (min-width: 1440px) {
  section .max-w-7xl,
  section .max-w-6xl,
  section .max-w-5xl {
    padding-inline: var(--shell-x, 3rem);
  }
}

/* === AT-A-GLANCE CELL TYPE (font-only, no layout) ================
 * Source StyleOverrides lines 133-138 sets the dt font-size + leading
 * via !important. That rule is structural - cards/grids fixer owns
 * the GRID. Here we own the TYPE inside each cell with no layout
 * side-effects. Drop !important; we load LATER and source value is
 * equal-specificity (0,3,1) - we match at (0,3,1) and win on order.
 * ================================================================ */
.ataglance-wrap .ag-cell dt {
  font-size: clamp(0.6875rem, 0.18vw + 0.65rem, 0.8125rem);
  letter-spacing: 0.14em;
  line-height: 1.3;
}

/* === OPTIONAL ENHANCEMENT: balance hero display ===================
 * Already set unconditionally on .t-display above. Browsers that
 * don't understand text-wrap simply ignore it. No fallback needed.
 * ================================================================ */

/* ============================================================
 * responsive-cards-grids.css - generated 2026-05-20
 * Phase 2b - Cards + Grids responsive refactor
 *
 * Loaded last in <head>, so equal-specificity rules win on source
 * order. No !important. No transform / transition / animation /
 * scroll-behavior / scroll-snap / position:sticky / will-change /
 * mask-image / @keyframes - those are LOCKED by the audit.
 *
 * Mobile-first. @media (min-width: …) upward.
 * Targets every card + grid region from /tmp/responsive-audit.json.
 * ============================================================ */


/* ============================================================
 * SHARED - min-width:0 traps for grid children
 * Default \`min-width: auto\` on grid items lets them push past
 * their track. Pin every direct child of every section/grid to
 * minmax(0,1fr) safety so internal mono tokens, long words, or
 * images can't overflow horizontally.
 * ============================================================ */
.shell.grid > *,
.hero-shell > *,
.hero-shell .shell.grid > *,
section[class*="grid-cols"] > *,
section .grid > *,
section .frame > .grid > * { min-width: 0; }

img, picture, video, canvas, svg { max-width: 100%; }


/* ============================================================
 * PRODUCT CARDS  (.prod-card)
 * Cards live inside the .prod-track scroll/snap rail. We may
 * size + pad the cards themselves; the track's translate3d and
 * scroll-snap-* are LOCKED, so we never touch them.
 * ============================================================ */
.prod-card {
  padding: clamp(1.125rem, 4vw, 1.5rem);
  min-height: 0;
  overflow: visible;
  gap: 1rem;
}
.prod-card > * { min-width: 0; max-width: 100%; }
/* Eyebrow row + LIVE/BETA pill stays on a single visual line at
   any width; eyebrow text takes the squeeze, the pill doesn't. */
.prod-card > div.flex.items-center.justify-between:first-child {
  gap: 8px;
  flex-wrap: nowrap;
}
.prod-card .t-eyebrow {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
}
.prod-card .pill {
  flex-shrink: 0;
  white-space: nowrap;
}
/* Card foot: wrap if the link + wordmark can't share a line. */
.prod-card .card-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  row-gap: 16px;
  min-width: 0;
}
.prod-card .card-foot > * { min-width: 0; }
/* Product wordmark img: pin to a sane bound so French/longer text
   on the left can take the squeeze first. */
.prod-card .product-wordmark {
  flex-shrink: 0;
  max-width: 50%;
  max-height: 32px;
  height: auto;
  width: auto;
  object-fit: contain;
  object-position: right center;
}
/* The feature mini-grid inside each product card stays 2-col but
   relaxes to 1-col on the very narrowest viewports so the
   icon + label pair never collides. */
.prod-card .grid.grid-cols-2 {
  grid-template-columns: minmax(0, 1fr);
  gap: 0.5rem;
}
@media (min-width: 414px) {
  .prod-card .grid.grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 768px) {
  .prod-card { padding: clamp(1.5rem, 3vw, 2rem); gap: 1.25rem; }
}
@media (min-width: 1024px) {
  .prod-card { padding: clamp(1.75rem, 2.5vw, 2.5rem); }
}
@media (min-width: 1440px) {
  .prod-card { padding: clamp(2rem, 2.5vw, 2.75rem); }
}

/* PROD-TRACK card widths - we only set width on the cards,
   never the translate / snap behavior of the track itself. */
.prod-track { gap: 1rem; }
.prod-track > * { min-width: 0; }
@media (max-width: 1023px) {
  .prod-track { padding-inline: 0; }
  .prod-track > * { flex: 1 1 auto; width: 100%; max-width: 100%; }
}
/* The horizontal scroll/snap rail at >=1024 is owned by Pentabay.html
   (line 320-321); we don't override its flex-basis here. */


/* ============================================================
 * PRODUCT LOGO TILE + WORDMARK  (replaces StyleOverrides 29-50)
 * Beat the source rules at equal specificity (loaded later).
 * ============================================================ */
.product-logo-tile img {
  object-fit: contain;
  object-position: center;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  display: block;
}
.product-wordmark[src*="logo-zentra"],
.product-wordmark[src*="logo-weguard"],
.product-wordmark[src*="logo-trackme"],
.product-wordmark[src*="logo-ava"],
.product-wordmark[src*="logo-neo"],
.product-wordmark[src*="logo-mydart"] {
  background: transparent;
  padding: 0;
  height: auto;
  max-height: 32px;
  border-radius: 0;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}


/* ============================================================
 * CLIENT WALL  (.client-wall)  +  CLIENT CELL  (.client-cell)
 * 320 → 1 col, 480+ → 2 col, 640+ → 3 col, 1024+ → 4 col, 1600+ → 6 col.
 * The source already does 2→3 (640) → 4 (1024) → 6 (1600). We
 * REFINE: 1 col under 480, and rewrite the nth-child border
 * pattern so right-edge cells never carry a hanging hairline.
 * ============================================================ */
.client-wall {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}
.client-wall > .client-cell:nth-child(n) { border-right: 0; }
@media (min-width: 480px) {
  .client-wall { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .client-wall > .client-cell { border-right: 1px solid var(--pb-hairline-60); }
  .client-wall > .client-cell:nth-child(2n) { border-right: 0; }
}
@media (min-width: 640px) {
  .client-wall { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .client-wall > .client-cell { border-right: 1px solid var(--pb-hairline-60); }
  .client-wall > .client-cell:nth-child(2n) { border-right: 1px solid var(--pb-hairline-60); }
  .client-wall > .client-cell:nth-child(3n) { border-right: 0; }
}
@media (min-width: 1024px) {
  .client-wall { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .client-wall > .client-cell { border-right: 1px solid var(--pb-hairline-60); }
  .client-wall > .client-cell:nth-child(3n) { border-right: 1px solid var(--pb-hairline-60); }
  .client-wall > .client-cell:nth-child(4n) { border-right: 0; }
}
@media (min-width: 1600px) {
  .client-wall { grid-template-columns: repeat(6, minmax(0, 1fr)); }
  .client-wall > .client-cell { border-right: 1px solid var(--pb-hairline-60); }
  .client-wall > .client-cell:nth-child(4n) { border-right: 1px solid var(--pb-hairline-60); }
  .client-wall > .client-cell:nth-child(6n) { border-right: 0; }
}

/* .client-cell - centered, mobile-comfortable padding */
.client-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  padding: 18px 16px 18px;
  min-height: 92px;
  min-width: 0;
}
.client-cell .client-text {
  flex: 0 1 auto;
  align-items: center;
  text-align: center;
  min-width: 0;
}
.client-cell .client-name,
.client-cell .client-meta {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (min-width: 640px) {
  .client-cell { padding: 20px 20px 20px; gap: 14px; }
}
@media (min-width: 1024px) {
  .client-cell { padding: 22px 24px 24px; gap: 14px; min-height: 96px; }
}


/* ============================================================
 * CLIENT CARD  (.client-card)  - used on /our-clients
 * Refactor StyleOverrides 466-520 - drop the !important block,
 * use specificity (article.card.client-card) to beat the source
 * \`.card { flex-direction:column }\` (Pentabay.html ~1232).
 * ============================================================ */
article.card.client-card,
.client-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 14px;
  min-height: 84px;
  min-width: 0;
}
article.card.client-card .client-card-body,
.client-card .client-card-body {
  flex: 1 1 auto;
  min-width: 0;
}
article.card.client-card .client-card-mono,
.client-card .client-card-mono {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid var(--pb-hairline);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  font-family: 'Open Sans', monospace;
  letter-spacing: -0.02em;
  position: relative;
  overflow: hidden;
}
article.card.client-card .client-card-mono img.client-card-logo,
.client-card .client-card-mono img.client-card-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  padding: 4px;
  display: block;
  aspect-ratio: 1 / 1;
}
article.card.client-card .client-card-mono.client-card-mono--fallback,
.client-card .client-card-mono.client-card-mono--fallback {
  background: var(--pb-elevated-2);
  color: var(--pb-fg);
  font-weight: 700;
  font-size: 17px;
}
article.card.client-card .client-card-mono.client-card-mono--fallback::before,
.client-card .client-card-mono.client-card-mono--fallback::before {
  content: attr(data-fallback-letter);
}
article.card.client-card .client-card-mono.client-card-mono--fallback img.client-card-logo,
.client-card .client-card-mono.client-card-mono--fallback img.client-card-logo {
  display: none;
}
article.card.client-card .client-card-name,
.client-card .client-card-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
}


/* ============================================================
 * AT-A-GLANCE  (.ataglance / .ataglance-wrap .ag-grid)
 * Refactor StyleOverrides 107-174.
 * Reflow plan:
 *   <640    → 1 col   (was forced 6 - horizontal-scrolled)
 *   640+    → 2 col
 *   720+    → 3 col   (matches source)
 *   1080+   → 6 col   (matches source, preserves spec-sheet look)
 * Wrapper drops \`overflow-x: auto\` for narrow widths since the
 * grid now reflows instead of overflowing.
 * ============================================================ */
.ataglance,
.ataglance-wrap .ataglance {
  background: transparent;
  overflow-x: visible;
  overflow-y: visible;
  -webkit-overflow-scrolling: auto;
  max-width: 100%;
}
.ataglance-wrap .ag-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;
  min-width: 0;
}
@media (min-width: 640px) {
  .ataglance-wrap .ag-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 720px) {
  .ataglance-wrap .ag-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (min-width: 1080px) {
  .ataglance-wrap .ag-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}
/* Cell base - comfortable mobile padding, dt allowed to wrap. */
.ataglance-wrap .ag-cell {
  padding: 16px 14px 14px;
  gap: 8px;
  min-width: 0;
  min-height: 0;
  border-right: 0;
  border-bottom: 1px solid var(--pb-hairline-60);
}
.ataglance-wrap .ag-cell:last-child { border-bottom: 0; }
.ataglance-wrap .ag-cell dt {
  font-size: 12px;
  letter-spacing: 0.12em;
  line-height: 1.3;
  white-space: normal;
  word-break: normal;
}
.ataglance-wrap .ag-cell dd {
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 1px;
  font-size: clamp(1.375rem, 5vw, 1.75rem);
  line-height: 1;
  letter-spacing: -0.02em;
  min-width: 0;
}
.ataglance-wrap .ag-cell .ag-unit {
  font-size: 0.55em;
  margin-left: 1px;
  opacity: 0.85;
}
.ataglance-wrap .ag-cell .ag-foot {
  font-size: 10px;
  letter-spacing: 0.06em;
  line-height: 1.35;
  white-space: normal;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 640+ : 2-col so left+right columns get a vertical hairline */
@media (min-width: 640px) {
  .ataglance-wrap .ag-cell {
    padding: 18px 16px 16px;
    border-right: 1px solid var(--pb-hairline-60);
    border-bottom: 1px solid var(--pb-hairline-60);
  }
  .ataglance-wrap .ag-cell:nth-child(2n) { border-right: 0; }
  .ataglance-wrap .ag-cell:last-child { border-right: 0; border-bottom: 0; }
}
/* 720+ : 3-col, recolor borders to match source */
@media (min-width: 720px) {
  .ataglance-wrap .ag-cell {
    border-right: 1px solid var(--pb-hairline-60);
    border-bottom: 1px solid var(--pb-hairline-60);
  }
  .ataglance-wrap .ag-cell:nth-child(2n) { border-right: 1px solid var(--pb-hairline-60); }
  .ataglance-wrap .ag-cell:nth-child(3n) { border-right: 0; }
  .ataglance-wrap .ag-cell:nth-last-child(-n+3) { border-bottom: 0; }
  .ataglance-wrap .ag-cell dd {
    font-size: clamp(1.25rem, 2.2vw, 1.875rem);
  }
}
/* 1080+ : the canonical 6-col spec-sheet row */
@media (min-width: 1080px) {
  .ataglance-wrap .ag-cell {
    padding: 20px 18px 18px;
    border-right: 1px solid var(--pb-hairline-60);
    border-bottom: 0;
  }
  .ataglance-wrap .ag-cell:nth-child(n) { border-right: 1px solid var(--pb-hairline-60); border-bottom: 0; }
  .ataglance-wrap .ag-cell:nth-child(3n) { border-right: 1px solid var(--pb-hairline-60); }
  .ataglance-wrap .ag-cell:nth-child(6n) { border-right: 0; }
  .ataglance-wrap .ag-cell dt {
    white-space: nowrap;
  }
  .ataglance-wrap .ag-cell dd {
    font-size: clamp(1.5rem, 1.8vw, 2.25rem);
  }
  .ataglance-wrap .ag-cell .ag-foot {
    font-size: 9.5px;
    letter-spacing: 0.12em;
    white-space: nowrap;
  }
}
/* Tall-viewport bump (already in source line 1475-1478) - keep a
   matching guard so the cells aren't oversized on short screens. */
@media (max-height: 820px) and (min-width: 1080px) {
  .ataglance-wrap .ag-cell { padding: 14px 14px 12px; }
  .ataglance-wrap .ag-cell dd {
    font-size: clamp(1.125rem, 1.6vw, 1.5rem);
  }
}


/* ============================================================
 * INDUSTRIES COMPARISON TABLE  (.frame.overflow-x-auto)
 * Wrapper stays explicitly horizontally scrollable; the table
 * inside keeps min-width:max-content from the source. We just
 * make sure the wrapper itself never exceeds the viewport.
 * ============================================================ */
.frame.overflow-x-auto {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
}
.frame.overflow-x-auto > * { min-width: 0; }
.frame.overflow-x-auto > table {
  min-width: max-content;
  width: 100%;
}
.table-wrap {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
}
@media (max-width: 640px) {
  .frame.overflow-x-auto { max-width: 100vw; }
}


/* ============================================================
 * FRAMEWORKS RAIL  (.frameworks-rail)  + FRW-CARD  (.frw-card)
 * Source: 2 → 3 (640) → 5 (1024). Add a 480 step to 2 stays /
 * relax narrow padding so the absolute-positioned \`::after\`
 * arrow doesn't clip the card name on the smallest widths.
 * ============================================================ */
.frameworks-rail {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1px;
}
@media (min-width: 480px) {
  .frameworks-rail { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 640px) {
  .frameworks-rail { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (min-width: 1024px) {
  .frameworks-rail { grid-template-columns: repeat(5, minmax(0, 1fr)); }
}
.frw-card {
  padding: 18px 18px 20px;
  min-width: 0;
  display: block;
  overflow: hidden;
}
.frw-card > * { min-width: 0; max-width: 100%; }
.frw-card .frw-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: 22px;
  margin-bottom: 14px;
  min-width: 0;
}
.frw-card .frw-card-name {
  min-width: 0;
  flex: 1 1 auto;
  overflow-wrap: anywhere;
  word-break: normal;
}
@media (min-width: 640px) {
  .frw-card { padding: 20px 20px 22px; }
  .frw-card .frw-card-head { padding-right: 24px; }
}
@media (min-width: 1024px) {
  .frw-card { padding: 22px 22px 24px; }
}


/* ============================================================
 * CASE CARD  (.case-card)  + CASE OUTCOMES  (.case-outcomes)
 * Outcomes are forced 2-col always in source. On 320 the
 * polygon clip-path can crowd the corner crosshair; we relax
 * outcomes to single-col under 414 so each value gets full width.
 * ============================================================ */
.case-card {
  padding: 22px 18px 20px;
  min-width: 0;
  overflow: hidden;
}
.case-card > * { min-width: 0; max-width: 100%; }
.case-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}
.case-outcomes {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1px;
}
.case-outcomes li {
  min-width: 0;
  padding: 14px 16px 16px;
  min-height: 110px;
}
.case-outcomes .case-value {
  overflow-wrap: anywhere;
  word-break: normal;
}
@media (min-width: 414px) {
  .case-outcomes { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .case-outcomes li { padding: 16px 18px 18px; min-height: 130px; }
}
@media (min-width: 640px) {
  .case-card { padding: 28px 24px 24px; }
}
@media (min-width: 1024px) {
  .case-card { padding: 32px 28px 28px; }
}


/* ============================================================
 * TESTIMONIAL ROW  (.testimonial-row)
 * Source: 1 → 2 (768) → 4 (1280). At 4-col + 1280 each card is
 * ~220px wide and quotes feel cramped. Bump to 2-col at 768 and
 * keep 4-col only at 1440+ so cards have room to breathe.
 * Single column under 768 (unchanged).
 * ============================================================ */
.testimonial-row {
  display: grid;
  gap: 1px;
  grid-template-columns: minmax(0, 1fr);
}
@media (min-width: 768px) {
  .testimonial-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 1024px) {
  .testimonial-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 1280px) {
  .testimonial-row { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
.testimonial {
  min-width: 0;
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.testimonial > * { min-width: 0; max-width: 100%; }
.testimonial blockquote {
  overflow-wrap: anywhere;
  word-break: normal;
}
.testimonial .attrib {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
}
@media (min-width: 640px) {
  .testimonial { padding: 28px 24px 24px; }
}
@media (min-width: 1024px) {
  .testimonial { padding: 32px 28px 28px; }
}


/* ============================================================
 * POSTURE ROW  (.posture-row)
 * Source: 1-col base, 4-col grid (1.1fr 2fr 2fr 1.4fr) at 720+.
 * Hold the source semantics; just ensure each grid track can
 * shrink (minmax) and long mono jurisdictions wrap gracefully.
 * ============================================================ */
.posture-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 4px;
  padding: 16px 18px;
  min-width: 0;
}
.posture-row > * { min-width: 0; }
.posture-row .col-juris,
.posture-row .col-role {
  overflow-wrap: anywhere;
  word-break: normal;
}
@media (min-width: 720px) {
  .posture-row {
    grid-template-columns:
      minmax(0, 1.1fr)
      minmax(0, 2fr)
      minmax(0, 2fr)
      minmax(0, 1.4fr);
    align-items: baseline;
    gap: 24px;
    padding: 20px 24px;
  }
}


/* ============================================================
 * SERVICES grid  (md:grid-cols-2 → boost to lg:grid-cols-3)
 * Source has only md:grid-cols-2 on the wrapper (#services > div
 * line 3161). At 1280 each card stretches to ~620px - too wide.
 * We boost to 3-col at lg+ without renaming any class. Selector
 * uses :where() so we don't beat unrelated .md\\\\:grid-cols-2
 * Tailwind grids unintentionally - we scope to #services.
 * ============================================================ */
#services .grid.md\\:grid-cols-2 {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
}
@media (min-width: 768px) {
  #services .grid.md\\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.25rem;
  }
}
@media (min-width: 1024px) {
  #services .grid.md\\:grid-cols-2 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
@media (min-width: 1280px) {
  #services .grid.md\\:grid-cols-2 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.5rem;
  }
}
.service-card {
  min-width: 0;
  padding: 22px 20px 22px;
  min-height: 280px;
  position: relative;
}
.service-card > * { min-width: 0; max-width: 100%; }
/* Whole service card clickable -> its service details page (the card-foot
 * link's href). Service cards are a plain grid (no 3D tilt), so the CSS
 * stretched-link pattern works reliably here. */
.service-card .card-foot a.btn-link::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
}
.service-card .svc-head {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}
.service-card .svc-head h3 {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: normal;
  /* Service card titles read BRAND BLUE.
     They were red (--pb-red, Pantone 186 C) on every card, which together with
     the red/violet steps of the old accent rotation was most of the non-blue
     colour in this section. Blue keeps the titles as the loudest thing in the
     card without introducing a second hue.
     Kept as a token mix rather than a flat blue for the same contrast reason the
     red version needed one - see the dark-theme rule below.

     --pb-red is NOT a theme pair: Pentabay.html declares it as #C8102E for both
     :root and [data-theme="dark"], and the brighter #FF5A6E is only a local
     override inside #page-home section.hero-shell. Used raw it therefore puts a
     dark red on the near-black dark theme - measured 3.36:1, which squeaks past
     AA-large only because these are 24px/600. So the dark theme lifts the same
     token toward --pb-fg instead of hardcoding a second red.

     Deliberately NOT applied to the S/0N badges, the hover ring or the card
     link - those still follow the six-step --acc rotation defined further down.
     (That rotation used to be shared with the .wna-card set in WHY NORTH
     AMERICA; those are now .wna-plate and carry their own two-tone accent.)

     Scoped through .svc-head on purpose: .service-card is also used by the two
     /trust cards and by quoteBand's pull-quote figures, and neither wraps its
     heading in .svc-head, so neither picks this up. Specificity (0,2,1) beats
     the bare-heading colour rules. */
  color: var(--pb-blue);
}
:root[data-theme="dark"] .service-card .svc-head h3 {
  color: color-mix(in oklab, var(--pb-blue-3) 62%, var(--pb-fg));
}
@media (min-width: 640px) {
  .service-card { padding: 24px 22px 24px; min-height: 300px; }
}
@media (min-width: 1024px) {
  .service-card { padding: clamp(1.5rem, 2vw, 2rem); min-height: 320px; }
}

/* ------------------------------------------------------------
 * Home services: the "proven process" panel that fills the row beside the
 * Design card (the grid renders 3-col at lg, so card S/04 otherwise leaves
 * two empty columns). It spans 2 of the 3 columns at lg, full width below.
 * ------------------------------------------------------------ */
#services .svc-process { grid-column: 1 / -1; justify-content: flex-start; }
@media (min-width: 1024px) { #services .svc-process { grid-column: span 2; } }
#services .svc-process .svc-proc-title { font-size: 1.5rem; line-height: 1.2; letter-spacing: -0.01em; }
#services .svc-proc {
  margin-top: clamp(1.25rem, 2vw, 1.75rem);
  display: flex; flex-wrap: wrap; align-items: flex-start;
  gap: 18px 4px;
}
#services .svc-proc-step {
  flex: 1 1 130px; min-width: 108px;
  display: flex; flex-direction: column; align-items: center; text-align: center; gap: 8px;
}
#services .svc-proc-ico {
  width: 44px; height: 44px; border-radius: 12px; flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(11, 92, 255, 0.08); color: var(--pb-blue);
}
#services .svc-proc-ico svg { width: 22px; height: 22px; }
#services .svc-proc-name { font-weight: 700; font-size: 0.95rem; }
#services .svc-proc-desc { font-size: 0.8rem; line-height: 1.4; color: var(--pb-muted); max-width: 20ch; }
#services .svc-proc-arrow {
  flex: 0 0 auto; align-self: flex-start; margin-top: 22px;
  color: var(--pb-muted); opacity: 0.45; font-size: 1.1rem;
}
@media (max-width: 1023px) {
  #services .svc-proc-arrow { display: none; }
  #services .svc-proc-step { flex-basis: 28%; }
}
:root[data-theme="dark"] #services .svc-proc-ico { background: rgba(120, 160, 255, 0.15); color: var(--pb-blue-3, #7CA0FF); }


/* ============================================================
 * HERO SHELL  (.hero-shell .shell.grid)
 * Source declares lg:grid-cols-12 with no md/sm fallback. Force
 * 1-col below 1024 (the legacy StyleOverrides 800-816 did this
 * with !important - refactor without).
 * Specificity here matches the source (.shell.grid is 0,2,0).
 * Loaded last → wins.
 * ============================================================ */
.hero-shell .shell.grid,
section .shell.grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
}
.hero-shell .shell.grid > *,
section .shell.grid > * {
  min-width: 0;
  max-width: 100%;
}
@media (min-width: 1024px) {
  .hero-shell .shell.grid.lg\\:grid-cols-12,
  section .shell.grid.lg\\:grid-cols-12 {
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }
  .hero-shell .shell.grid.lg\\:grid-cols-6,
  section .shell.grid.lg\\:grid-cols-6 {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}


/* ============================================================
 * GENERIC TAILWIND \`lg:\` GRID COERCION  (replaces StyleOverrides
 * 810-816 + 818-823 without !important)
 * Source \`.grid.lg:grid-cols-N\` with no md/sm fallback collapses
 * to N-col at all widths because the base \`.grid\` rule has no
 * grid-template-columns. We coerce to 1-col below the relevant
 * Tailwind breakpoint where no smaller fallback exists.
 * ============================================================ */

/* Mobile-first: any .grid.lg\\:grid-cols-N that DOES NOT also
   have a md: or sm: column variant collapses to 1-col BELOW the
   lg: breakpoint. The :not() chain has specificity (0,7,0) which
   would beat the @media (min-width: 1024px) restore at (0,2,0) -
   so we wrap this entire block in a max-width: 1023.98px media
   query. At >=1024 only the source's Tailwind lg: rule applies. */
@media (max-width: 1023.98px) {
  .grid.lg\\:grid-cols-12:not(.md\\:grid-cols-2):not(.md\\:grid-cols-3):not(.md\\:grid-cols-4):not(.sm\\:grid-cols-2):not(.sm\\:grid-cols-3),
  .grid.lg\\:grid-cols-6:not(.md\\:grid-cols-2):not(.md\\:grid-cols-3):not(.sm\\:grid-cols-2),
  .grid.lg\\:grid-cols-4:not(.md\\:grid-cols-2):not(.sm\\:grid-cols-2),
  .grid.lg\\:grid-cols-3:not(.md\\:grid-cols-2):not(.sm\\:grid-cols-2),
  .grid.lg\\:grid-cols-2:not(.md\\:grid-cols-2):not(.sm\\:grid-cols-2) {
    grid-template-columns: minmax(0, 1fr);
  }
  .grid.lg\\:grid-cols-12:not(.md\\:grid-cols-2):not(.md\\:grid-cols-3):not(.md\\:grid-cols-4):not(.sm\\:grid-cols-2):not(.sm\\:grid-cols-3) > *,
  .grid.lg\\:grid-cols-6:not(.md\\:grid-cols-2):not(.md\\:grid-cols-3):not(.sm\\:grid-cols-2) > *,
  .grid.lg\\:grid-cols-4:not(.md\\:grid-cols-2):not(.sm\\:grid-cols-2) > *,
  .grid.lg\\:grid-cols-3:not(.md\\:grid-cols-2):not(.sm\\:grid-cols-2) > *,
  .grid.lg\\:grid-cols-2:not(.md\\:grid-cols-2):not(.sm\\:grid-cols-2) > * {
    min-width: 0;
    max-width: 100%;
  }
}
@media (min-width: 1024px) {
  .grid.lg\\:grid-cols-12 { grid-template-columns: repeat(12, minmax(0, 1fr)); }
  .grid.lg\\:grid-cols-6  { grid-template-columns: repeat(6, minmax(0, 1fr)); }
  .grid.lg\\:grid-cols-5  { grid-template-columns: repeat(5, minmax(0, 1fr)); }
  .grid.lg\\:grid-cols-4  { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .grid.lg\\:grid-cols-3  { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .grid.lg\\:grid-cols-2  { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

/* \`.grid.grid-cols-N\` with no \`.keep-cols-mobile\` opt-out:
   stack to 1 col under 640. Replaces StyleOverrides 818-823.
   Source's plain .grid-cols-N from Tailwind has specificity
   (0,1,0); our (0,2,0) selector wins regardless of order. */
@media (max-width: 639.98px) {
  .grid.grid-cols-2:not(.keep-cols-mobile),
  .grid.grid-cols-3:not(.keep-cols-mobile),
  .grid.grid-cols-4:not(.keep-cols-mobile) {
    grid-template-columns: minmax(0, 1fr);
  }
  .grid.grid-cols-2:not(.keep-cols-mobile) > *,
  .grid.grid-cols-3:not(.keep-cols-mobile) > *,
  .grid.grid-cols-4:not(.keep-cols-mobile) > * {
    min-width: 0;
    max-width: 100%;
  }
}


/* ============================================================
 * MIN-WIDTH:0 / MIN-W-AUTO HELPERS
 * Tailwind ships .min-w-0 (min-width:0) but applying it to every
 * grid item in source isn't practical. Add a defensive belt:
 * any direct child of a flexbox or grid that's NOT explicitly
 * marked .keep-min-content gets min-width:0. Scoped to known
 * grid wrappers so we don't trample intentional sizes elsewhere.
 * ============================================================ */
.client-wall > *,
.frameworks-rail > *,
.testimonial-row > *,
.posture-row > *,
.case-outcomes > *,
.ataglance-wrap .ag-grid > *,
.track-grid > *,
.ts-tally > *,
section[class*="grid"] > .min-w-0,
section[class*="grid"] > [class*="min-w-0"] { min-width: 0; }


/* ============================================================
 * IMAGE OBJECT-FIT GUARDS for in-card imagery
 * Maintains aspect-ratio; never stretches; cover for hero/photo
 * imagery, contain for logos / wordmarks.
 * ============================================================ */
.prod-card img:not(.product-wordmark):not(.product-logo-tile img),
.case-card img,
.frw-card img,
.testimonial img,
.posture-row img {
  max-width: 100%;
  height: auto;
  object-fit: cover;
  object-position: center;
}
.client-card-mono img,
.product-logo-tile img,
.client-cell img,
.product-wordmark {
  object-fit: contain;
  object-position: center;
}


/* ============================================================
 * SAFETY: NO HORIZONTAL OVERFLOW on card/grid containers
 * (the explicitly-scrollable .prod-track + .frame.overflow-x-auto
 * keep their scroll, declared above; everything else clips/visible.)
 * ============================================================ */
.prod-card,
.client-card,
.frw-card,
.case-card,
.testimonial,
.posture-row,
.client-wall,
.client-cell,
.frameworks-rail,
.testimonial-row,
.ataglance-wrap .ag-grid,
.case-outcomes {
  max-width: 100%;
  min-width: 0;
}


/* ============================================================
 * END
 * ============================================================ */

/* ============================================================
 * responsive-controls.css - generated 2026-05-20
 * Phase 2c of responsive overhaul: BUTTONS, FORM CONTROLS, DROPDOWNS.
 *
 * Rules of engagement:
 *   - No !important except where overriding an inline style="" that
 *     can't be reached any other way (one exception documented below).
 *   - This file is appended LAST to <head>, so equal-specificity
 *     selectors win the cascade by source order.
 *   - Mobile-first: base rules apply to all viewports; @media
 *     min-width queries add desktop refinements.
 *   - No animation, transition, transform, scroll-behavior,
 *     scroll-snap-*, position: sticky, will-change, mask-image,
 *     or @keyframes. The .btn transform/transition is locked.
 *   - No class renames. Locked names per /tmp/responsive-audit.json.
 * ============================================================ */


/* =============================================================
 * BUTTONS - base sizing & wrapping
 *
 * Replaces these earlier !important rules from StyleOverrides.jsx:
 *   - line 756: .btn { white-space: normal !important }
 *   - lines 751-755: .btn-row, .flex.flex-wrap.gap-3/4 { width: 100% }
 *   - lines 843-845: .hero-cta .btn, section .btn-primary/secondary
 *                    { width: 100% }
 *
 * The source .btn at Pentabay.html line 200 is selector (0,1,0).
 * The rules below target .btn at (0,1,0) too, and since this file
 * is appended after the source <style>, they win on source order.
 * No !important required.
 *
 * Locked: do NOT touch transform, transition, will-change.
 * Locked: do NOT touch .btn-primary:hover translateY(-2px).
 * Locked: do NOT touch .btn-link:hover .arr translateX(4px).
 * ============================================================= */
.btn {
  /* Every button stays on a SINGLE line at every viewport. Long
     labels that don't fit are truncated with an ellipsis rather
     than wrapping to multiple lines. The source declares
     height: 48px (Pentabay.html line 200) - preserve that so the
     pill silhouette is consistent across the design. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  /* If the button is inside a narrow flex parent, allow it to shrink
     past min-content rather than push the parent wider. */
  min-width: 0;
  /* Match source's 48px pill height - wrapped labels never grow
     vertically. The locked transition/transform on .btn (Pentabay.html
     line 200) continues to apply because height is the same. */
  height: 48px;
  min-height: 48px;
}

/* The topbar .btn at Pentabay.html line 2566 carries an inline
 * style="height:40px; padding:0 18px;" - the ONLY inline style on
 * any .btn in the document. Inline styles win on specificity
 * (1,0,0,0) so the only way to reach those two properties is
 * !important. Restrict to this exact selector + properties so no
 * collateral. The 40px topbar variant is intentional (compact
 * header) and we preserve it; we ONLY adjust below at coarse-pointer
 * @media where 44px is the WCAG floor. */
header .nav-shell .btn.btn-primary[style] {
  /* No override here - we accept the inline 40px in normal use.
     The coarse-pointer block below lifts it to 44px. */
}

/* Button row containers: wrap freely and span full width on mobile
 * so two CTAs (e.g. "Talk to us" + "Read the framework") don't
 * overflow a 320px viewport. At sm+ they revert to natural sizing
 * (controlled by the next @media block).
 *
 * Selector specificity: (0,2,0) matches the source's Tailwind
 * combination of flex+flex-wrap+gap-N. Appended later, this wins. */
.btn-row,
.flex.flex-wrap.gap-3,
.flex.flex-wrap.gap-4 {
  width: 100%;
  /* Ensure children are allowed to grow to their natural width
     before stacking. Gap stays from Tailwind. */
  align-items: stretch;
}

/* Hero CTA + section primary/secondary CTAs go full-width below
 * 640px so 2-button stacks read as a single column.
 * (0,2,0) specificity matches the source's class+class combinator;
 * source-order tiebreak wins. */
@media (max-width: 639.98px) {
  .hero-cta .btn,
  section .btn-primary,
  section .btn-secondary {
    width: 100%;
    flex: 1 1 auto;
  }
  /* When stacked in a button row, each button gets its own line. */
  .btn-row > .btn,
  .flex.flex-wrap.gap-3 > .btn,
  .flex.flex-wrap.gap-4 > .btn {
    flex: 1 1 100%;
  }
}

/* sm: at 640+ and above - buttons go back to natural width.
 * (0,2,0) specificity for the .btn-row reset; later in source. */
@media (min-width: 640px) {
  .btn-row,
  .flex.flex-wrap.gap-3,
  .flex.flex-wrap.gap-4 {
    width: auto;
  }
  .hero-cta .btn,
  section .btn-primary,
  section .btn-secondary {
    width: auto;
  }
}


/* =============================================================
 * BUTTONS - coarse-pointer tap targets (WCAG 2.5.5)
 *
 * Replaces the rule at StyleOverrides.jsx lines 286-295 which
 * declared min-height: 44px on coarse-pointer without !important.
 * Repeating it here (no !important) so this CSS file is the
 * single source of truth for tap-target sizing - Phase 3 can
 * delete the duplicate from StyleOverrides cleanly.
 *
 * Includes the inline-styled topbar "Talk to us" button: the
 * inline style="height:40px" needs !important on this one
 * property to lift to 44px on touch devices. Documented above.
 * ============================================================= */
@media (hover: none) and (pointer: coarse) {
  .btn,
  .btn-primary,
  .btn-secondary,
  .btn-link,
  .btn-ghost,
  .btn-icon,
  .nav-shell a,
  .nav-shell button,
  .nav-shell summary,
  .pb-nav-mobile > summary,
  .pb-nav-mobile-panel a {
    min-height: 44px;
  }
  /* The topbar inline-styled CTA: only path to override the
     inline height:40px is !important on this specific property.
     Restricted to this selector so it never bleeds. */
  header .nav-shell .btn.btn-primary[style] {
    min-height: 44px;
  }
  /* Icon-only buttons (.btn-icon, .theme-toggle, hamburger summary,
     social row links) need ≥44px in both dimensions, not just
     min-height. */
  .btn-icon,
  .theme-toggle,
  .pb-nav-mobile > summary {
    min-width: 44px;
  }
  /* .btn-link is a text-only inline link (no padding, height:auto
     from source). Give it a comfortable hit area via padding instead
     of height (which would distort inline flow). */
  .btn-link {
    padding-block: 10px;
    /* Don't change horizontal padding - link arrows must sit flush. */
  }
}


/* =============================================================
 * FORM INPUTS - base
 *
 * The 16px font-size rule at StyleOverrides.jsx lines 544-555
 * prevents iOS Safari focus auto-zoom. Re-stating here (no
 * !important) so this file owns it; Phase 3 can remove the
 * duplicate from StyleOverrides. Specificity (0,0,2) matches the
 * source's element+attribute selector - appended later, this wins.
 * ============================================================= */
input[type="text"],
input[type="email"],
input[type="search"],
input[type="tel"],
input[type="url"],
input[type="number"],
input[type="password"],
textarea,
select {
  font-size: 16px;
  /* Allow native taps to fire without 300ms delay on older mobiles. */
  touch-action: manipulation;
  /* Inputs inside grid/flex parents need this to wrap at narrow
     widths instead of pushing the parent wider. */
  min-width: 0;
  max-width: 100%;
}

/* Email-signup inputs: 48px height to align with the pill .btn.
 * Source rule (StyleOverrides line 87-92) already does this without
 * !important. The original selector form input[type="email"] is
 * specificity (0,0,1,1). Repeated here at the same specificity, this
 * file later in source order - wins by tiebreak.
 *
 * Tightened: the .rounded-full variant gets the same dimensions so
 * pill-shaped inputs match the pill button height exactly. */
form input[type="email"],
form input[type="email"].rounded-full {
  height: 48px;
  min-height: 48px;
  max-height: 48px;
}

/* Coarse-pointer tap height for ALL form fields and select dropdowns.
 * Native <select> on iOS already has a comfortable tap height, but
 * Android renders it as a tight 32-36px box - lift to 44px so it
 * matches the buttons. textarea keeps its content-driven height. */
@media (hover: none) and (pointer: coarse) {
  input[type="text"],
  input[type="email"],
  input[type="search"],
  input[type="tel"],
  input[type="url"],
  input[type="number"],
  input[type="password"],
  select {
    min-height: 44px;
  }
  textarea {
    /* Textarea sized by \`rows\` attribute. Just ensure a 44px floor
       so a single-row textarea still gets a comfortable tap target. */
    min-height: 44px;
  }
}

/* Native <select> styling: keep the platform chevron but normalize
 * the box so it reads as part of the form, not an OS widget. */
select {
  /* Avoid the macOS default 1990s gradient + drop shadow while
     leaving iOS/Android system styling intact at native sizes. */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  /* Re-add a chevron via SVG background (no animation). The image
     is static - no animation/transition involved. */
  background-image:
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 12px 8px;
  padding-right: 36px;
  /* Restore standard browser select look minus the OS chrome. */
  background-color: transparent;
  border: 1px solid var(--pb-hairline);
  border-radius: 12px;
  padding-block: 10px;
  padding-left: 14px;
  color: var(--pb-fg);
  /* Keep cursor consistent with native expectation. */
  cursor: pointer;
}
/* When parent provides border-radius via Tailwind .rounded-full,
 * honour it so a select inside a pill form looks like a pill. */
form .rounded-full select,
form select.rounded-full {
  border-radius: 9999px;
  padding-left: 18px;
  padding-right: 40px;
}


/* =============================================================
 * CONTACT FORM - card + field affordance
 *
 * CF_INPUT/CF_SELECT (public/pentabay-subpages.js) already draw a
 * 1px --pb-hairline border, but that token is only ~1.4:1 against
 * --pb-surface on the dark theme, so six question rows just read as
 * bare copy with no visible control. Scoped to #pb-contact-form (the
 * form's own id, unique on the page) so nothing outside this form -
 * e.g. the newsletter email input - inherits the change.
 * ============================================================= */

/* The form as one object: --pb-elevated-1 is the site's existing
 * "panel" token (used for .pill, dropdown panels, etc.), so the form
 * reads as a raised card next to the "Reach us directly" .card stack
 * instead of floating as loose fields on the page background. Border
 * and outer glow are blue-tinted rather than the plain hairline, per
 * FORMDESIGN.png's lit-edge card. */
#pb-contact-form {
  border: 1px solid color-mix(in oklab, var(--pb-blue) 35%, var(--pb-hairline));
  border-radius: 24px;
  background: var(--pb-elevated-1);
  padding: clamp(1.25rem, 2vw, 2.5rem);
  box-shadow:
    0 0 0 1px color-mix(in oklab, var(--pb-blue) 12%, transparent),
    0 24px 60px -24px color-mix(in oklab, var(--pb-blue) 30%, transparent);
}

/* Text inputs + select: swap the transparent/elevated fill for
 * --pb-surface (the page background, one step darker than the card),
 * so each field reads as recessed INTO the card rather than flush
 * with it - the standard "this is where you type" cue. Hover and
 * focus lift the border and add a soft glow so the state change is
 * visible before the outline (StyleOverrides focus-visible rule)
 * kicks in. */
#pb-contact-form input[type="text"],
#pb-contact-form input[type="email"],
#pb-contact-form select {
  background-color: var(--pb-surface);
  transition: border-color .18s ease, box-shadow .18s ease;
}
#pb-contact-form input[type="text"]:hover,
#pb-contact-form input[type="email"]:hover,
#pb-contact-form select:hover {
  border-color: color-mix(in oklab, var(--pb-blue) 45%, var(--pb-hairline));
}
#pb-contact-form input[type="text"]:focus,
#pb-contact-form input[type="email"]:focus,
#pb-contact-form select:focus {
  box-shadow: 0 0 0 4px color-mix(in oklab, var(--pb-blue) 18%, transparent);
}

/* Checkbox/radio rows (cfGroup() in public/pentabay-subpages.js):
 * each option is a bare <label class="flex ..."><input/><span/></label>
 * with no boundary, so a list of six choices reads as loose text, not
 * six selectable controls. Turning each row into its own chip - and
 * filling it once checked - mirrors the .pb-nav-current tint/ring
 * treatment elsewhere on the page (Pentabay.html, ~line 332) so
 * "selected" reads the same language site-wide. :has() also removes
 * the need for JS to toggle a class on every click. */
#pb-contact-form fieldset label.flex {
  border: 1px solid var(--pb-hairline);
  border-radius: 9999px;
  padding: 12px 20px;
  transition: border-color .18s ease, background-color .18s ease, box-shadow .18s ease;
}
#pb-contact-form fieldset label.flex:hover {
  border-color: color-mix(in oklab, var(--pb-blue) 45%, var(--pb-hairline));
}
#pb-contact-form fieldset label.flex:has(input:checked) {
  border-color: var(--pb-blue);
  background: color-mix(in oklab, var(--pb-blue) 16%, transparent);
  box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--pb-blue) 60%, transparent);
}
/* Light theme sits on near-white, where a 16% tint reads washed out -
 * same adjustment as the nav's current-item state. */
:root[data-theme="light"] #pb-contact-form fieldset label.flex:has(input:checked) {
  background: color-mix(in oklab, var(--pb-blue) 10%, transparent);
  box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--pb-blue) 55%, transparent);
}


/* =============================================================
 * FORM LAYOUT - flex stack on mobile, row at sm+
 *
 * Common pattern: <form class="flex flex-col sm:flex-row gap-3">
 * Tailwind handles direction switching at the sm: breakpoint.
 * We just need to ensure children align cleanly when stacked.
 *
 * The source rule form.flex { align-items: center } at
 * StyleOverrides line 84-86 only works once the form goes
 * flex-direction: row (at sm+). On mobile (flex-col), align-items
 * is cross-axis (= horizontal) - we want stretch so input + button
 * fill the form width.
 * ============================================================= */
form.flex.flex-col {
  align-items: stretch;
}
form.flex.flex-col > * {
  width: 100%;
}
@media (min-width: 640px) {
  /* At sm: Tailwind flips to flex-direction: row. align-items
     reverts to center so the 48px input and 48px button line up. */
  form.flex.sm\\:flex-row {
    align-items: center;
  }
  form.flex.sm\\:flex-row > * {
    width: auto;
  }
  /* The email input still wants flex-grow so it consumes the row. */
  form.flex.sm\\:flex-row > input[type="email"] {
    flex: 1 1 auto;
    width: auto;
  }
}

/* Generic form container: any <form> using grid layout collapses
 * to single column below sm so labels stack above inputs cleanly.
 * The contact form (#pb-contact-form, injected via subpages JS)
 * already uses display: grid - make sure it falls back nicely. */
@media (max-width: 639.98px) {
  form.grid,
  form#pb-contact-form {
    grid-template-columns: minmax(0, 1fr);
  }
  form > label,
  form > .label-block {
    width: 100%;
  }
}


/* =============================================================
 * DROPDOWNS / <details> - viewport-safe panel positioning
 *
 * Patterns in source:
 *   - .pb-nav-mobile-panel: width: min(280px, calc(100vw - 24px))
 *     - already viewport-safe (StyleOverrides line 210). Leave alone.
 *   - .pb-topbar-lang .pb-lang-menu: top: calc(100% + 8px)
 *     - keep this; we add overflow guards.
 *   - Desktop dropdowns inside <details class="relative"> in the
 *     top nav (Pentabay.html lines 2334-2528). They use
 *     "absolute left-0 top-full mt-2 w-[440px|320px|340px|280px]"
 *     - fixed pixel widths that can clip the right edge on a
 *     1280px viewport if the trigger is in the second half of
 *     the row. Add a clamp so panel width never exceeds available
 *     horizontal space.
 * ============================================================= */

/* All <details> in the nav: relative parent (source already sets
 * this via Tailwind .relative) - repeat at lower specificity as
 * a safety net for any <details> that lacks the class. */
details {
  position: relative;
}

/* Mobile nav <details>: re-state width clamp at the same specificity
 * as StyleOverrides so this file is the single source. */
.pb-nav-mobile-panel {
  width: min(280px, calc(100vw - 24px));
  max-width: calc(100vw - 24px);
}

/* Topbar language pill <details>: panel below the trigger, never
 * clipped by the viewport right edge. */
.pb-topbar-lang .pb-lang-menu {
  top: calc(100% + 8px);
  bottom: auto;
  /* Don't let the menu push past the viewport - if the trigger is
     near the right edge, clamp the menu width and let it visually
     trail upward via the natural flow inside the topbar. */
  max-width: calc(100vw - 24px);
}

/* Desktop nav dropdowns (Products / Industries / Company / etc.):
 * the source declares them as <div class="absolute left-0 top-full
 * mt-2 w-[440px]" ...>. The fixed pixel width can clip at the
 * 1024-1280px breakpoint where the dropdown sits near the right
 * edge of the nav row.
 *
 * Specificity (0,2,1): .nav-shell details .absolute matches the
 * source's class combination and wins source-order. */
.nav-shell details .absolute,
.nav-shell details > div[class*="absolute"] {
  max-width: calc(100vw - 32px);
  /* Allow the menu to shift left if it would otherwise clip the
     right viewport edge. The source uses left-0 - we keep that on
     wide viewports and let max-width clip on narrow. */
}

/* On viewports < 1280 (between lg and xl), nudge the rightmost
 * desktop dropdowns to anchor right-aligned so they don't clip
 * past the viewport. Tailwind's "left-0 top-full" on the panel
 * means the panel grows rightward from the summary; flipping to
 * right-anchored keeps it inside the viewport. */
@media (min-width: 1024px) and (max-width: 1279.98px) {
  .nav-shell details:nth-last-child(-n+4) > div[class*="absolute"] {
    /* Use right:0 instead of left:0 so the panel grows leftward.
       Specificity (0,3,1) wins against source's Tailwind left-0
       (0,1,0). */
    left: auto;
    right: 0;
  }
}

/* Generic <details> (FAQ accordions, etc.) outside the nav: any
 * panel content inside must stay inside the viewport on mobile.
 * CRITICAL: only apply at <lg viewports - desktop nav dropdowns
 * (.nav-shell details.relative > div.absolute) live inside <details>
 * and would otherwise be capped to ~95px (parent details' summary
 * text width) instead of their Tailwind w-[280|320|340|440] width.
 * The desktop nav is hidden below lg via Tailwind .hidden.lg:flex
 * so the constraint can't accidentally hit it here. */
@media (max-width: 1023.98px) {
  details:not(.pb-nav-mobile):not(.pb-topbar-lang) > *:not(summary) {
    max-width: 100%;
  }
}

/* <summary> elements: ensure they're tappable and don't show the
 * default disclosure triangle on browsers where the source has
 * already set list-style: none (most do; this is belt-and-suspenders). */
summary {
  cursor: pointer;
}
@media (hover: none) and (pointer: coarse) {
  /* Inside the nav, summaries already get min-height 44px from the
     coarse-pointer block above. Other <details> in the body still
     need a tap floor. */
  details > summary {
    min-height: 44px;
  }
}


/* =============================================================
 * .product-wordmark - replace the !important from StyleOverrides
 *
 * Source: StyleOverrides.jsx line 247-253 declared
 *   .product-wordmark { width: auto !important; ... }
 *
 * The selector .product-wordmark is (0,1,0). The Tailwind utilities
 * that set width on the img (.w-auto / .w-full / inline width="...")
 * are also (0,1,0) or an HTML width attribute (presentational, lower
 * specificity than CSS). Boost to (0,2,0) by qualifying with the
 * card-foot parent and this file's later source order wins without
 * !important.
 * ============================================================= */
.card-foot .product-wordmark,
.prod-card .product-wordmark {
  flex-shrink: 0;
  max-width: 100%;
  max-height: 36px;
  width: auto;
  height: auto;
}


/* =============================================================
 * 320px ultra-narrow refinements
 *
 * iPhone SE / Android compact (320-380px) - buttons go very tight.
 * Drop horizontal padding inside the .btn so 2-line labels don't
 * overflow the rounded pill.
 * ============================================================= */
@media (max-width: 380px) {
  .btn {
    padding-inline: 20px;
    font-size: 0.875rem;
  }
  .btn-link {
    padding-inline: 0;
  }
  /* Tighter form padding too, since rounded-full inputs at 320px
     can crowd their placeholder. */
  input[type="text"],
  input[type="email"],
  input[type="search"],
  input[type="tel"],
  input[type="url"],
  input[type="number"],
  input[type="password"] {
    padding-inline: 14px;
  }
}


/* =============================================================
 * Large viewport refinement: button padding scales up slightly so
 * .btn doesn't read as cramped at 1920+. The source declares
 * padding: 0 28px on .btn - we extend horizontal padding only,
 * keeping the locked transition/transform intact.
 * ============================================================= */
@media (min-width: 1440px) {
  .btn {
    padding-inline: 32px;
  }
}
@media (min-width: 1920px) {
  .btn {
    padding-inline: 36px;
    font-size: 1rem;
  }
}


/* =============================================================
 * Focus styles - visible outline on keyboard focus, hidden on
 * pointer focus. Buttons and form controls only (so we don't
 * paint outlines on cards / interactive divs that already manage
 * their own focus state).
 * ============================================================= */
.btn:focus-visible,
.btn-primary:focus-visible,
.btn-secondary:focus-visible,
.btn-ghost:focus-visible,
.btn-link:focus-visible,
.btn-icon:focus-visible,
input:focus-visible,
textarea:focus-visible,
select:focus-visible,
summary:focus-visible {
  outline: 2px solid var(--pb-blue);
  outline-offset: 2px;
}
.btn:focus:not(:focus-visible),
input:focus:not(:focus-visible),
textarea:focus:not(:focus-visible),
select:focus:not(:focus-visible),
summary:focus:not(:focus-visible) {
  outline: none;
}

/* ============================================================
 * responsive-topbar-footer.css - generated 2026-05-20
 * Phase 2a - Responsive overhaul: TOPBAR + FOOTER.
 *
 * Loaded LAST via StyleOverrides.jsx (appended to document.head).
 * No !important anywhere - equal-specificity rules win on
 * source-order. Where the source rule has higher specificity
 * (e.g. ":root[data-theme] .nav-shell.scrolled", 0,3,0) we
 * match or exceed it (selector annotated in-line).
 *
 * Locked properties (NEVER written here):
 *   animation, transition, transform, scroll-behavior,
 *   scroll-snap-*, position: sticky, will-change, @keyframes,
 *   backdrop-filter is only RE-set to "none" (not animated),
 *   which is a static value override - allowed.
 *
 * Region scope: .nav-shell, .pb-nav-mobile (+ panel),
 *   .pb-topbar-lang, <footer> + descendants.
 * ============================================================ */

/* ============================================================
 * TOPBAR - base (mobile-first, <768px)
 * ============================================================ */

/* Repaint sticky-nav background as solid surface so scrolling
   content does not bleed through. Source rules in Pentabay.html
   l. 260-261 are ":root[data-theme="dark"] .nav-shell.scrolled"
   (specificity 0,3,0) - we match that with a 0,3,0 selector. */
:root[data-theme="dark"]  .nav-shell,
:root[data-theme="dark"]  .nav-shell.scrolled,
:root[data-theme="light"] .nav-shell,
:root[data-theme="light"] .nav-shell.scrolled {
  background: var(--pb-surface);
  border-bottom: 0;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

/* ============================================================
 * TOPBAR - blue band on the home hero  (2026-08)
 *
 * The rule above paints the nav var(--pb-surface), which on the dark
 * theme is #060A14. Sitting immediately above the brand-blue home hero
 * that read as a separate black strip pasted over the page rather than
 * as the top of the hero. Here the nav continues the hero's blue
 * instead, so the seam disappears and the bar reads as the hero's own
 * first 80px.
 *
 * SCOPE - home only, and the two rejected alternatives, because this is
 * the part most likely to get "helpfully" widened later.
 *
 * (1) Every route. Measured across the route table, only some routes
 *     open on a blue band at all: / (the home hero) and the routes
 *     pentabay-page-imagery.js tags .pb-hero-banner. /about,
 *     /compliance, /contact-us, /blog, /our-work and /trust open on
 *     plain page surface. Applied unconditionally, that second set got a
 *     dark navy bar sitting on nothing - on the LIGHT theme a #07142A bar
 *     directly above a #FFFFFF page, measured Δ248/255 at the join. That
 *     is the same hard edge this change set out to remove, just moved to
 *     other pages.
 *
 * (2) Home + the .pb-hero-banner sub-pages, i.e. every blue-hero route.
 *     Looks right on paper and fails in practice: Breadcrumbs.jsx
 *     injects a 39px .pb-breadcrumbs strip as the first child of every
 *     non-home page, so the nav and the banner are NOT adjacent there.
 *     On the light theme the stack came out navy bar → white strip →
 *     blue banner, which is a worse edge than the one it replaced. Two
 *     of the three bands being blue does not help when the seam is
 *     against the white one in the middle.
 *
 * So: home only. Every other route keeps the nav it already had, and the
 * sub-page banners keep their existing white/near-black topbar. The
 * remaining arm is deliberately a *structural* test rather than
 * [data-route="home"] - it asks whether the home page is the mounted
 * one, which is also true on the routes that fall back to rendering it
 * (verified: /web-development, /nope-404 both mount page-home), and on
 * those the visible hero IS the home hero, so blue is correct.
 * #page-home is the right hook because the home hero is styled by that
 * ID (Pentabay.html l. 354) and never carries .pb-hero-banner.
 *
 * Crossing between home and any other route cross-fades rather than
 * cutting: .nav-shell already carries transition: background .3s ease
 * (Pentabay.html l. 304), which this inherits for free.
 *
 * Specificity: (1,4,1) - :has() contributes its argument's specificity -
 * so it clears the (0,4,0) base rule above without !important. Browsers
 * without :has() simply keep the surface-coloured nav, which is exactly
 * the pre-change look.
 *
 * WHY THE STOPS ARE SAMPLED PIXELS AND NOT THE HERO'S OWN GRADIENT.
 * Copying the hero's background declaration (Pentabay.html l. 390-395)
 * was the obvious move and it came out visibly too LIGHT - a pale bar
 * over a dark hero, the same seam in the other direction. The hero's
 * gradient is not what you see: four more layers composite over it, and
 * the dominant one is [data-route="home"] .hero-video (further down this
 * file) - a near-black video plate at opacity 0.25 covering inset: 0.
 * The .aura, the micro-grid <svg>, .grain::after and the
 * .spotlight::after radial each take a further bite. Net effect at the
 * hero's top edge is ~35-40% darker per channel than the raw gradient.
 * None of that is reproducible on an 80px bar that has no video in it.
 *
 * So the stops below are the hero's TOP EDGE as actually rendered,
 * sampled across its width. Sampled at 1440w, but every stop is a
 * percentage of the hero's own percentage-based gradient, so the ramp
 * holds at any width.
 *
 * They are MEANS over a full loop of the video, not a single frame, and
 * that distinction is the whole reason this is documented. Because the
 * dimming comes from moving imagery, the hero's top edge is not a fixed
 * colour - it breathes. Measured across hero-loop.mp4's ~9.9s loop
 * (14 samples), per-channel range at the top edge:
 *     x=0%    R 5-12   G 17-26   B 37-46    mean   7, 20, 42
 *     x=25%   R 5-7    G 22-26   B 56-65    mean   6, 24, 60
 *     x=50%   R 8-10   G 33-37   B 78-90    mean   9, 34, 84
 *     x=66%   R 6-8    G 36-41   B 84-98    mean   7, 39, 90
 *     x=80%   R 7-9    G 36-43   B 81-97    mean   8, 39, 88
 *     x=100%  R 7-23   G 32-54   B 71-104   mean  11, 38, 82
 * Sitting on the mean keeps the join inside ~±7/255 for most of the
 * width at every point in the loop, which on a soft horizontal edge with
 * no rule between the two boxes is not resolvable by eye. The far-right
 * stop is the loose one (±16) - that is where the video's brightest
 * content is - so the extreme right corner has a faint pulse. No static
 * colour can fix that; only removing the video from the hero could.
 * >> If the hero's gradient, .hero-video opacity, its overlay stack, or
 * >> hero-loop.mp4 itself is ever changed, RE-SAMPLE these stops. They
 * >> do not follow the hero on their own.
 *
 * 90deg, not the hero's 102deg: the 12° of vertical component is why the
 * hero's own band drifts lighter toward its bottom, but across 80px that
 * drift is swamped by the video's own variance - rows 30px apart differ
 * by less than the loop does. A pure horizontal ramp is what the data
 * supports.
 *
 * Tuned to the HOME hero specifically - which is the other half of why
 * the scope is home only. Sub-page .pb-hero-banner bands are the same
 * brand blue but carry no video plate, so they run ~40/255 brighter in
 * blue; these stops would be wrong for them even without the breadcrumb
 * strip in between. An earlier revision did paint the sub-pages too, on
 * the theory that a slightly deeper nav over a brighter banner reads as
 * depth rather than as a seam - which is true in isolation, and stopped
 * being true once the white breadcrumb strip was in the middle of it.
 *
 * Painted in BOTH scroll states on purpose. The .scrolled class flips at
 * scrollY > 120 (Pentabay.html l. 5618), which is still INSIDE the hero,
 * so restricting the blue to :not(.scrolled) would snap the bar back to
 * near-black while the blue hero was still behind it - a visible flicker
 * 120px into the first scroll. Identical in both states means the bar
 * never changes colour while you are on the page.
 *
 * 2026-08: DARK THEME ONLY. This block used to be un-gated, on the
 * reasoning recorded above - the hero is brand blue on light AND dark, so
 * the nav continuing it should be too. That is still true of the hero, and
 * it is why the hero is left alone; but it made the light theme's only
 * above-the-fold surface identical to the dark theme's, so clicking the
 * theme toggle on the homepage changed nothing you could see from the top
 * of the page and read as a dead button. (It was not dead: data-theme
 * flips, persists to localStorage, and everything from #trust down
 * changes.) The nav is the one element of the fold that can answer the
 * click without redesigning the hero, so on the light theme it now follows
 * the theme - see the LIGHT-THEME HOMEPAGE NAV block below.
 *
 * Trade-off accepted deliberately: on the light theme the bar no longer
 * continues the hero's top edge, so it reads as a distinct light bar ABOVE
 * a blue hero rather than as one band with it. The sampled stops below are
 * still the right values for the dark theme and are unchanged. */
:root[data-theme="dark"]:has(main#page-home.page.active) .nav-shell {
  background: linear-gradient(90deg,
    #07142A 0%,
    #06183C 25%,
    #092254 50%,
    #07275A 66%,
    #082758 80%,
    #0B2652 100%);
  background-color: #07142A;
}

/* ── Nav copy on the blue band ───────────────────────────────
 * Same :has() gate as the band itself - the copy must not go white on a
 * route where the band stayed page-surface.
 *
 * The nav row is coloured entirely from tokens: the centre <nav> is
 * text-[var(--pb-muted)], summaries hover to var(--pb-fg), and
 * .theme-toggle takes colour from --pb-muted + border from --pb-hairline
 * (Pentabay.html l. 1265-1266). Rescoping the ramp to white-on-blue here
 * - the same values the hero and .pb-hero-banner use - recolours all of
 * it in one place. Only the LIGHT theme strictly needs it (its ramp is
 * #000000, which went black-on-navy), but the dark theme's #98A2B3 muted
 * is also a grey tuned for #060A14 rather than a blue band, so both get
 * the hero's own white ramp and the two themes stay identical here -
 * which is right, because the band they sit on is identical.
 *
 * 2026-08: gated to the dark theme alongside the band itself. The light
 * theme keeps its own ink ramp now that its bar is light - white-on-white
 * is exactly what this rescope would produce there. */
:root[data-theme="dark"]:has(main#page-home.page.active) .nav-shell {
  color: #FFFFFF;
  --pb-fg: #FFFFFF;
  --pb-fg-soft: #FFFFFF;
  --pb-muted: #FFFFFF;
  --pb-hairline: rgba(255,255,255,0.26);
  --pb-hairline-60: rgba(255,255,255,0.20);
  --pb-hairline-40: rgba(255,255,255,0.13);
}

/* ── LIGHT-THEME HOMEPAGE NAV ─────────────────────────────────
 * The light counterpart to the blue band above, and the visible answer to
 * the theme toggle on the homepage fold.
 *
 * Painted in BOTH scroll states, for the same reason the blue band is:
 * .scrolled flips at scrollY > 120, which is still inside the hero, so a
 * :not(.scrolled) gap would leave the bar transparent over a blue hero for
 * the first 120px - carrying light-theme ink copy, i.e. black text on brand
 * blue. The existing [data-theme="light"] .nav-shell.scrolled rule
 * (Pentabay.html l. 306) covers only the scrolled half and is 0.80 alpha
 * over whatever is behind it; at the top of this page that "behind" is the
 * hero, so this rule is opaque and leaves the blur to the scrolled rule.
 *
 * #FBFBFD, not #FFFFFF: it is the value that rule already uses, so the two
 * states are the same colour and the bar does not shift as you scroll past
 * 120px. The hairline is the light theme's own token - the bar needs a
 * bottom edge here, because unlike the blue band it does NOT continue into
 * the hero, and without a rule the two just abut. */
:root[data-theme="light"]:has(main#page-home.page.active) .nav-shell {
  background: #FBFBFD;
  border-bottom: 1px solid var(--pb-hairline-60);
}

/* The flyouts are NOT on the blue band - they are --pb-elevated-1 cards
   floating below it, and they inherit the rescope above because they are
   header descendants. On the dark theme that is harmless (--pb-elevated-1
   is #08101F, so white copy is correct). On the LIGHT theme the card is
   #F7F8FA and white-on-white made every dropdown item invisible, so the
   ramp is put back to the light theme's own values. Same pattern the home
   hero uses for its .node-tip card (Pentabay.html l. 561-566).
   No :has() gate needed: this only ever undoes the rule above, and on an
   un-gated route it is a no-op that rewrites the tokens to the values
   they already had. */
:root[data-theme="light"] .nav-shell details > div[class*="absolute"],
:root[data-theme="light"] .nav-shell .pb-nav-mobile-panel {
  color: #000000;
  --pb-fg: #000000;
  --pb-fg-soft: #000000;
  --pb-muted: #000000;
  --pb-hairline: #DDE2EA;
  --pb-hairline-60: rgba(221,226,234,0.85);
  --pb-hairline-40: rgba(221,226,234,0.55);
}

/* Logo: .logo-img-light is the WHITE wordmark (shown on the dark theme),
   .logo-img-dark the ink one (shown on light) - see Pentabay.html
   l. 1259-1262.

   This pair used to force the WHITE mark on the light theme too, because
   the homepage band was dark blue on both themes and the ink mark would
   have sat near-invisible on #07142A. That premise is gone: the band above
   is now dark-theme-only and the light theme's bar is #FBFBFD, where the
   white mark is the invisible one. So the override is removed and the light
   theme falls back to the normal swap in Pentabay.html - ink mark on the
   light bar, white mark on the dark one.

   NOT removed: the identical pair for .pb-hero-banner routes at the bottom
   of this file (l. ~8077). Those bands ARE still blue on both themes, so
   the white mark is still correct there. */

/* The "Talk to us" CTA is deliberately NOT touched. The home hero
   inverts its own .btn-primary to a white pill (Pentabay.html l. 576-584)
   because the hero's mid-band is full --pb-blue, where a blue fill sits on
   its own colour. This band is the hero's TOP edge, which the video plate
   holds down to #07142A-#0B2652 - as dark as the #060A14 the CTA used to
   sit on - so the stock blue pill still reads as the primary action and
   needs no inversion. Left alone on purpose: it keeps the nav's most
   recognisable element unchanged. */

/* Nav inner shell layout - compact on phones. Source: .nav-shell
   inner is "shell flex items-center justify-between h-16 lg:h-20"
   so we tune the wrapper's vertical padding via .nav-shell rather
   than fight Tailwind utilities. Equal-specificity (0,1,0). */
.nav-shell {
  padding-block: 8px;
}
header.nav-shell > .shell {
  /* Allow children to shrink rather than overflow the 320px row. */
  min-width: 0;
  gap: 8px;
}
/* The right-cluster (theme toggle + lang + CTA + hamburger) - tight gap. */
header.nav-shell > .shell > .flex.items-center.gap-2 {
  gap: 6px;
  flex-wrap: nowrap;
}

/* Logo never crops on small screens. Source l. 766-767:
   .logo-img { height: 32px; } @1024 height:36px. We slim it a
   touch at <360 to clear room for the CTA pill + hamburger. */
.nav-shell .logo-img {
  height: 28px;
  max-width: 100%;
}

/* "Talk to us" CTA - let it shrink to icon-padding on phones,
   but keep the label on a single line. The .btn base rule sets
   white-space: normal (so longer multi-word labels can wrap on
   wider buttons), but the topbar CTA is intentionally tight -
   force nowrap here at specificity (0,3,0) which wins over the
   .btn (0,1,0) rule.
   Source inline style on this anchor: "height:40px; padding:0 18px".
   We do not touch height (kept for vertical centering) - only
   horizontal padding + font-size + white-space. */
.nav-shell .btn-primary.magnetic {
  padding-inline: 12px;
  font-size: 13px;
  white-space: nowrap;
}

/* Topbar language pill - hidden below lg by the source markup
   (only the footer .lang-toggle is rendered in DOM until React
   injects .pb-topbar-lang). When the injected pill is present
   below lg we hide it so it does not compete with the hamburger. */
.pb-topbar-lang {
  display: none;
}

/* ============================================================
 * TOPBAR - Mobile hamburger system (replaces lines 184-228 of
 * StyleOverrides.jsx WITHOUT !important).
 * Specificity below matches/exceeds any source rule for the
 * hamburger (none in Pentabay.html - fully owned here).
 * ============================================================ */
.pb-nav-mobile {
  position: relative;
}
.pb-nav-mobile > summary {
  list-style: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--pb-fg);
  border: 1px solid var(--pb-hairline-60);
  background: transparent;
}
.pb-nav-mobile > summary:hover {
  background: var(--pb-hairline-40);
}
.pb-nav-mobile > summary::-webkit-details-marker,
.pb-nav-mobile > summary::marker {
  display: none;
  content: '';
}
.pb-nav-mobile[open] > .pb-nav-mobile-panel {
  display: flex;
}
/* The hamburger <summary> sits mid-row in the header (theme toggle
   + lang pill + "Talk to us" CTA live to its right), so anchoring
   the panel's right edge to the .pb-nav-mobile container with
   'right: 0' pushed the panel off-screen to the LEFT on phones.
   Switch to position: fixed anchored to the viewport's right edge
   so it always sits flush with the right gutter. */
.pb-nav-mobile-panel {
  display: none;
  position: fixed;
  top: 68px;
  right: 12px;
  left: auto;
  bottom: auto;
  width: min(320px, calc(100vw - 24px));
  max-width: calc(100vw - 24px);
  /* Cap to the VISIBLE viewport so the panel never runs past the bottom of
     the screen (and its own scroll kicks in). dvh tracks the real visible
     height on mobile - 100vh would include the area behind the browser
     address bar, letting the panel overflow off-screen without scrolling.
     The plain vh line is a fallback for browsers without dvh support. */
  max-height: calc(100vh - 80px);
  max-height: calc(100dvh - 80px);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--pb-elevated-1);
  border: 1px solid var(--pb-hairline);
  border-radius: 16px;
  padding: 8px;
  flex-direction: column;
  gap: 2px;
  z-index: 100;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}
.pb-nav-mobile-panel > a {
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.3;
  color: var(--pb-fg);
  text-decoration: none;
  display: block;
  min-height: 44px;
}
.pb-nav-mobile-panel > a:hover {
  background: var(--pb-hairline-40);
}
/* The panel is a capped-height flex column. Without this, its flex children
   default to flex-shrink:1, so when an open submenu makes the content taller
   than the panel the items COMPRESS to fit (and .pb-nav-sub's overflow:hidden
   then clips the squashed submenu) instead of the panel scrolling. Pin every
   direct child's size so content overflows and overflow-y:auto scrolls. */
.pb-nav-mobile-panel > * { flex-shrink: 0; }

/* ============================================================
 * MOBILE NAV SUBMENU ACCORDIONS  (.pb-nav-sub)
 * Mirrors the desktop dropdowns (Products / Services /
 * Industries / Company). Each is a nested <details> inside
 * .pb-nav-mobile-panel. Tapping the summary expands the list
 * of children. The outer .pb-nav-mobile <details> stays open
 * while a child is open; DetailsAutoClose closes on outside
 * click. No JS required - pure native <details>.
 * ============================================================ */
.pb-nav-mobile-panel .pb-nav-sub {
  border-radius: 12px;
  overflow: hidden;
}
.pb-nav-mobile-panel .pb-nav-sub > summary {
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.3;
  color: var(--pb-fg);
  cursor: pointer;
  min-height: 44px;
  font-weight: 500;
}
.pb-nav-mobile-panel .pb-nav-sub > summary::-webkit-details-marker,
.pb-nav-mobile-panel .pb-nav-sub > summary::marker {
  display: none;
  content: '';
}
.pb-nav-mobile-panel .pb-nav-sub > summary:hover {
  background: var(--pb-hairline-40);
}
.pb-nav-mobile-panel .pb-nav-sub > summary .pb-nav-sub-chev {
  flex-shrink: 0;
  color: var(--pb-muted);
  transition: transform 0.2s ease;
}
.pb-nav-mobile-panel .pb-nav-sub[open] > summary .pb-nav-sub-chev {
  transform: rotate(180deg);
}
.pb-nav-mobile-panel .pb-nav-sub > .pb-nav-sub-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 6px 8px 14px;
  border-left: 1px solid var(--pb-hairline-60);
  margin-left: 14px;
}
.pb-nav-mobile-panel .pb-nav-sub > .pb-nav-sub-list > a {
  padding: 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.3;
  color: var(--pb-muted);
  text-decoration: none;
  display: flex;
  align-items: center;
  min-height: 44px;
}
.pb-nav-mobile-panel .pb-nav-sub > .pb-nav-sub-list > a:hover {
  background: var(--pb-hairline-40);
  color: var(--pb-fg);
}

/* Selected-page marker, mobile panel. Twin of the desktop rules in
 * Pentabay.html (nav section) - src/NavActive.jsx stamps both navs in the
 * same pass, so a visitor who noticed where they were on desktop finds the
 * same mark in the hamburger. Specificity has to clear the :hover rules
 * above (0,4,1), hence the full paths + the class. */
.pb-nav-mobile-panel > a.pb-nav-current,
.pb-nav-mobile-panel .pb-nav-sub > summary.pb-nav-group-current {
  background: color-mix(in oklab, var(--pb-blue) 16%, transparent);
  box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--pb-blue) 60%, transparent);
  color: var(--pb-fg);
}
:root[data-theme="light"] .pb-nav-mobile-panel > a.pb-nav-current,
:root[data-theme="light"] .pb-nav-mobile-panel .pb-nav-sub > summary.pb-nav-group-current {
  background: color-mix(in oklab, var(--pb-blue) 10%, transparent);
  box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--pb-blue) 55%, transparent);
}
/* Nested items get the tint + leading rail, matching the desktop dropdown.
 * The rail replaces the list's own hairline for this one row, so it reads as
 * that row claiming the guide line rather than a second line beside it. */
.pb-nav-mobile-panel .pb-nav-sub > .pb-nav-sub-list > a.pb-nav-current {
  background: color-mix(in oklab, var(--pb-blue) 12%, transparent);
  color: var(--pb-fg);
  box-shadow: inset 2px 0 0 0 var(--pb-blue);
}

/* Topbar language pill placement (when shown at lg+) - flip its
   dropdown downward (the footer placement opens upward). Source
   menu lives at .pb-topbar-lang .pb-lang-menu so we match at
   specificity 0,2,0. */
.pb-topbar-lang {
  margin-right: 4px;
}
.pb-topbar-lang .pb-lang-menu {
  top: calc(100% + 8px);
  bottom: auto;
}

/* html scroll-padding-top - accounts for sticky header so anchor
   links don't land under the nav. No !important needed
   (no source rule sets it). */
html {
  scroll-padding-top: 72px;
}

/* ============================================================
 * TOPBAR - tablet (>= 640px)
 * Slightly more breathing room; CTA shows full padding again.
 * ============================================================ */
@media (min-width: 640px) {
  .nav-shell {
    padding-block: 10px;
  }
  header.nav-shell > .shell {
    gap: 12px;
  }
  header.nav-shell > .shell > .flex.items-center.gap-2 {
    gap: 8px;
  }
  .nav-shell .logo-img {
    height: 32px;
  }
  .nav-shell .btn-primary.magnetic {
    padding-inline: 18px;
    font-size: 14px;
  }
}

/* ============================================================
 * TOPBAR - desktop (>= 1024px)
 * Hide hamburger, show desktop nav (already done by source
 * .hidden lg:flex Tailwind utility), reveal topbar lang pill.
 * ============================================================ */
@media (min-width: 1024px) {
  /* Hide hamburger AT lg without !important - equal-specificity
     (0,1,0) override + later source-order beats the base rule. */
  .pb-nav-mobile {
    display: none;
  }
  /* Reveal the React-injected topbar language pill at lg+. */
  .pb-topbar-lang {
    display: inline-flex;
  }
  .nav-shell {
    padding-block: 0;
  }
  header.nav-shell > .shell {
    gap: 16px;
  }
  /* Center nav clusters: keep dropdowns from clipping at the
     viewport edge. Source dropdown menus use 'absolute left-0'
     - we cannot reposition them (no transform/position) but we
     can ensure their parent doesn't overflow the shell. */
  header.nav-shell > .shell > nav {
    min-width: 0;
    flex: 1 1 auto;
    justify-content: center;
  }
}

/* ============================================================
 * TOPBAR - wide desktop (>= 1280px)
 * Restore generous logo + CTA sizing.
 * ============================================================ */
@media (min-width: 1280px) {
  .nav-shell .logo-img {
    height: 36px;
  }
}

/* ============================================================
 * TOPBAR - coarse pointer (touch devices)
 * WCAG 2.5.5 - 44x44 minimum tap target.
 * ============================================================ */
@media (hover: none) and (pointer: coarse) {
  .nav-shell a,
  .nav-shell button,
  .nav-shell summary,
  .pb-nav-mobile > summary,
  .pb-nav-mobile-panel a {
    min-height: 44px;
    min-width: 44px;
  }
  .nav-shell .lang-toggle .lang-opt {
    min-height: 32px;
    padding: 6px 10px;
  }
}

/* ============================================================
 * FOOTER - base (mobile-first, <640px)
 * Stack to single column; tight inter-block spacing.
 * Source: <footer class="shell pt-20 pb-12">
 *   <div class="grid sm:grid-cols-2 lg:grid-cols-6 gap-10">
 * The Tailwind base "grid" gives no template at <sm, but
 * "sm:grid-cols-2" kicks in at 640. We force 1 column below
 * sm without !important by matching specificity (0,1,1):
 * "footer .grid" vs source-order tie-breaker.
 * ============================================================ */
footer.shell {
  padding-top: clamp(2.5rem, 6vw, 5rem);
  padding-bottom: clamp(1.5rem, 3vw, 3rem);
}
footer .grid {
  /* Specificity (0,1,1) - same as StyleOverrides' replaced rule. */
  grid-template-columns: minmax(0, 1fr);
  gap: 2rem;
}
/* Make all footer columns shrinkable so long unbroken text
   (URLs, addresses) wrap. */
footer .grid > * {
  min-width: 0;
}
/* Footer logos - match topbar treatment (no crop). */
footer .logo-img {
  height: 36px;
  max-width: 100%;
}
/* Footer column lists - tighter on mobile so 9-item lists don't
   produce 12000px tall pages. */
footer ul {
  margin-top: 1rem;
}
footer ul li {
  line-height: 1.6;
}
footer ul li a {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-block: 2px;
}
/* Footer eyebrow labels - guarantee they're readable on mobile. */
footer .t-eyebrow {
  font-size: 11px;
  letter-spacing: 0.14em;
}
/* Bottom row - copyright / social / language pill - wrap nicely. */
footer .flex.flex-wrap.items-center.justify-between {
  gap: 1rem;
  row-gap: 1.25rem;
}
footer .flex.flex-wrap.items-center.justify-between > .flex.items-center.gap-5 {
  flex-wrap: wrap;
  gap: 1rem;
  row-gap: 0.75rem;
}
/* Social row - keep all 6 icons on one line at any width but
   allow wrapping if necessary. */
footer .social-row {
  flex-wrap: wrap;
  gap: 6px;
}
/* Cookies / privacy / contact link row - wrap with row-gap. */
footer .mt-8.flex.flex-wrap {
  row-gap: 0.5rem;
  gap: 0.75rem 1.25rem;
}
/* Footer language pill - same dimensions as topbar variant. */
footer .lang-toggle {
  flex-wrap: wrap;
  row-gap: 2px;
}

/* ============================================================
 * FOOTER - coarse pointer (touch devices)
 * WCAG 2.5.5 - 44x44 minimum tap target on links.
 * ============================================================ */
@media (hover: none) and (pointer: coarse) {
  footer a,
  footer button,
  footer .lang-opt {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
  footer ul li a {
    min-height: 44px;
  }
  footer .social-row a {
    min-height: 44px;
    min-width: 44px;
    width: 44px;
    height: 44px;
  }
  footer .lang-opt {
    min-height: 32px;
    padding: 6px 10px;
  }
}

/* ============================================================
 * FOOTER - tablet (>= 640px / sm:)
 * 2-column layout. Source uses "sm:grid-cols-2" Tailwind class
 * which already gives 2 columns at 640+ - we just ensure our
 * single-column override stops applying.
 * ============================================================ */
@media (min-width: 640px) {
  footer .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.5rem;
  }
}

/* ============================================================
 * FOOTER - desktop md+ (>= 768px)
 * Promote to 3-col so the 6 content blocks (HQ + Products +
 * Services + Industries + Company) sit in 2 rows of 3. The
 * HQ block (lg:col-span-2) spans 2 of 3 at this stop, which
 * keeps the address copy readable.
 * ============================================================ */
@media (min-width: 768px) {
  footer .grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2.5rem 2rem;
  }
}

/* ============================================================
 * FOOTER - desktop lg+ (>= 1024px)
 * 6-column layout - the source's "lg:grid-cols-6" intent.
 * HQ block keeps its "lg:col-span-2" (Tailwind utility - we
 * do not override). Bottom row spreads horizontally.
 * ============================================================ */
@media (min-width: 1024px) {
  footer .grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 2.5rem;
  }
  footer .flex.flex-wrap.items-center.justify-between {
    flex-wrap: nowrap;
    gap: 2rem;
  }
}

/* ============================================================
 * FOOTER - wide desktop (>= 1440px)
 * Slight extra inter-column gap to match the wider shell.
 * ============================================================ */
@media (min-width: 1440px) {
  footer .grid {
    gap: 3rem;
  }
}


/* ============================================================
 * NON-RESPONSIVE BASELINE - preserved from prior StyleOverrides.
 * Text selection, cookie notice, founder monogram, Lenis fix,
 * contact-pinned right column. These are NOT responsive concerns
 * and were intentionally NOT touched by the Phase 2 fixers.
 * ============================================================ */

/* Global safety guard - prevent horizontal scroll on any page.
 * Apply to BOTH <html> and <body>. html clip prevents the visual
 * overflow. body clip prevents body from being its own scrolling
 * context (some browsers compute scrollWidth on body even when
 * html clips). overflow: clip (NOT hidden) does NOT establish a
 * containing block for position: sticky descendants - the
 * contact-pinned right column at desktop continues to work. */
@supports (overflow: clip) {
  html, body { overflow-x: clip; }
}

/* Hero decorative blobs (.aura) are 720x720 px absolutely-positioned
 * gradient circles. On narrow viewports they exceed the viewport
 * width and push body content past the viewport (visible as a 12 px
 * horizontal scroll on /contact-us @ 320). Cap their box to the
 * viewport so they cannot expand the layout, even though their
 * visual blur extends beyond their box. The blur radius is purely
 * decorative - clipping the box does not change the visible glow. */
.aura {
  max-width: 100vw;
  max-height: 100vw;
}

/* === TEXT SELECTION (brand-coloured) === */
::selection {
  background-color: var(--pb-blue);
  color: #ffffff;
  text-shadow: none;
}
::-moz-selection {
  background-color: var(--pb-blue);
  color: #ffffff;
  text-shadow: none;
}

/* === COOKIE NOTICE TOAST === */
.pb-cookie-notice {
  position: fixed;
  bottom: 16px;
  right: 16px;
  left: 16px;
  max-width: 440px;
  margin-left: auto;
  background: var(--pb-elevated-1);
  color: var(--pb-fg);
  border: 1px solid var(--pb-hairline);
  border-radius: 16px;
  padding: 14px 16px;
  z-index: 200;
  box-shadow: 0 12px 40px rgba(0,0,0,0.28);
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  line-height: 1.5;
}
.pb-cookie-notice-text {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--pb-muted);
}
.pb-cookie-notice-text a {
  color: var(--pb-fg);
  border-bottom: 1px solid currentColor;
  white-space: nowrap;
  margin-left: 2px;
}
.pb-cookie-notice-btn {
  flex-shrink: 0;
  background: var(--pb-blue);
  color: #fff;
  border: 0;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background .15s ease;
}
.pb-cookie-notice-btn:hover { background: var(--pb-blue-2); }
.pb-cookie-notice-btn:focus-visible {
  outline: 2px solid var(--pb-fg);
  outline-offset: 2px;
}
/* Accept / Decline pair. Decline is a quieter, outlined secondary. */
.pb-cookie-notice-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pb-cookie-notice-btn--ghost {
  background: transparent;
  color: var(--pb-muted);
  border: 1px solid var(--pb-hairline);
}
.pb-cookie-notice-btn--ghost:hover {
  background: transparent;
  color: var(--pb-fg);
  border-color: var(--pb-fg);
}
@media (max-width: 640px) {
  .pb-cookie-notice {
    flex-direction: column;
    align-items: stretch;
    text-align: left;
    bottom: 12px;
    right: 12px;
    left: 12px;
    margin-left: 0;
    max-width: none;
  }
  .pb-cookie-notice-actions { width: 100%; }
  .pb-cookie-notice-btn { flex: 1; }
}

/* === FOUNDER MONOGRAM (photo frame + no-photo "VR" fallback) ===
   Lives on /about since 2026-08-05, when the standalone /founder route was
   removed and its profile became a section of /about. These selectors were
   never scoped to that route, so the move needed no change here. */
.founder-mono {
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--pb-blue), var(--pb-blue-2));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 84px;
  letter-spacing: -0.04em;
  line-height: 1;
  box-shadow: 0 24px 64px rgba(15, 79, 186, 0.32);
  position: relative;
  font-family: 'Open Sans', sans-serif;
}
.founder-mono::after {
  content: '';
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  border: 1px solid var(--pb-hairline);
  opacity: 0.6;
}
.founder-mono > span { display: inline-block; }
.founder-mono--photo {
  background: var(--pb-elevated-2);
  overflow: hidden;
  padding: 0;
}
.founder-mono--photo > span { display: none; }
.founder-mono .founder-photo {
  display: none;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  border-radius: 50%;
}
.founder-mono--photo .founder-photo {
  display: block;
}
@media (max-width: 1023px) {
  .founder-mono { width: 160px; height: 160px; font-size: 60px; margin-top: 12px; }
}
@media (max-width: 480px) {
  .founder-mono { width: 132px; height: 132px; font-size: 48px; }
}

/* === LENIS FOCUS-LOCKS-SCROLL FIX ===
 * Lenis adds .lenis-stopped to <html> on input focus (source line
 * ~4908), and source CSS pins .lenis.lenis-stopped to overflow:clip
 * (locking page scroll). html.lenis.lenis-stopped is specificity
 * (0,2,1), beating source's (0,2,0). No !important needed. */
html.lenis.lenis-stopped {
  overflow: auto;
}

/* === CONTACT PAGE - RIGHT COLUMN (static stack beside the form) ===
 * Was scroll-jacked: pinned the form column, then translated the 5
 * cards through a fixed-height masked window as the user scrolled,
 * cycling one card into view at a time behind progress dots - so 4
 * of the 5 were hidden at any moment, and the section needed 4x the
 * viewport height of empty scroll room to drive the animation.
 * Replaced with a plain stack: all 5 cards visible at once,
 * top-aligned beside the form, no scroll-driven transform. */
.contact-pinned { position: relative; padding-block: 3rem; }
.contact-pinned-grid { align-items: start; }
.contact-form-pane #pb-contact-form > button {
  justify-self: start;
  width: auto;
}
.contact-stage-track {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-block: clamp(1rem, 4vw, 1.5rem);
  padding-inline: clamp(0.75rem, 3vw, 1rem);
}
.contact-progress { display: none; }

/* ============================================================
 * Phase 4 regression - card padding tightening (mobile)
 * ============================================================
 * .client-card: bump L/R padding from 14 → 16 on mobile so the
 *   name ellipsis doesn't sit flush against the right border.
 *   Vertical stays at 14 (preserves min-height: 84).
 * .posture-row: vertical pad lifts 12 → 14 mobile / 12 → 16
 *   desktop for a calmer row rhythm. Horizontal pad unchanged.
 * Specificity (0,2,1) ties / beats existing rules; source-order
 * wins on ties since this block is appended last.
 * ============================================================ */
@media (max-width: 640px) {
  article.card.client-card,
  .client-card {
    padding: 14px 16px;
  }
}
.posture-row {
  padding-block: 16px;
}
@media (max-width: 640px) {
  .posture-row {
    padding-block: 14px;
  }
}


/* ============================================================
 * Phase 6 regression fixes
 * ============================================================ */

/* === 6.1 Desktop nav dropdown widths ========================
 * The source declares Tailwind w-[280px|320px|340px|440px] on
 * each dropdown panel. After scoping the generic max-width:100%
 * details guard above to <lg only, the source w-[N] would win.
 * Repeat the widths here at higher specificity (0,2,2 with the
 * attribute-prefix selector) so any future class-list changes
 * still get the right width - belt-and-suspenders.
 * ============================================================ */
@media (min-width: 1024px) {
  .nav-shell details > div[class~="w-[280px]"] { width: 280px; }
  .nav-shell details > div[class~="w-[320px]"] { width: 320px; }
  .nav-shell details > div[class~="w-[340px]"] { width: 340px; }
  .nav-shell details > div[class~="w-[440px]"] { width: 440px; }
  .nav-shell details > div[class*="absolute"] {
    max-width: calc(100vw - 32px);
  }
}

/* === 6.2 .btn-link variant - restore height:auto ===========
 * The new .btn { height:48px } rule above forces every button to
 * 48px tall pills, which breaks the inline .btn-link text style
 * (Pentabay.html line 209: padding:0; height:auto; background:
 * transparent; - used for "Read AVA spec →" type CTAs).
 * Restore the link variant explicitly so it stays inline-text.
 * ============================================================ */
.btn-link {
  height: auto;
  min-height: 0;
  /* white-space: nowrap inherits from .btn - kept */
}

/* === 6.3 Product carousel viewport gutter on <lg ============
 * The .pin-h-track-wrap lives OUTSIDE the inner .shell, so the
 * --shell-x horizontal gutter never reaches the .prod-card row.
 * Cards rendered edge-to-edge on phones. Apply the gutter to the
 * wrapping container so cards inset by --shell-x on each side.
 * Specificity (0,1,0) - equal to source, later in source = wins.
 * ============================================================ */
@media (max-width: 1023.98px) {
  .pin-h-track-wrap {
    padding-inline: var(--shell-x, 1.25rem);
  }
}

/* === 6.4 Footer language-menu mobile clipping ===============
 * .lang-toggle sits at footer left (x=20). Source .pb-lang-menu
 * has right:0 + min-width:220px which pushes its left edge off
 * the viewport by ~100px on a 375 phone (truncating the language
 * names). Swap anchor + clamp width below 480px so the menu
 * opens to the RIGHT of the trigger and fits.
 * ============================================================ */
@media (max-width: 480px) {
  .pb-lang-menu {
    left: 0;
    right: auto;
    min-width: 0;
    width: min(220px, calc(100vw - 40px));
    max-width: calc(100vw - 40px);
  }
}

/* === 6.5 Footer mobile tap targets ==========================
 * The existing @media (hover:none) and (pointer:coarse) rule
 * gives 44px on touch devices. Hybrid devices (stylus tablet
 * in pen mode, touch laptop) report fine pointer and skip it.
 * Apply unconditionally on narrow viewports.
 * ============================================================ */
@media (max-width: 640px) {
  footer a,
  footer button {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
  footer .social-row a {
    min-width: 44px;
    min-height: 44px;
    width: 44px;
    height: 44px;
  }
  footer p > a,
  footer address a {
    min-height: 36px;
  }
}

/* === 6.6 Pentagon (.scene-penta) → ataglance gap ===========
 * Pentagon visualization sits directly above the stat strip on
 * every viewport. Default top padding on .ataglance-wrap is
 * minimal, so the pentagon caption visually crowds the
 * "Founded · 2018 · Milton..." block on both mobile (single-
 * column stack) and desktop (side-by-side grid).
 *
 * Tier:
 *   mobile (<lg)   - 1.5-3rem fluid breathing space
 *   tablet/desktop - 1.25-2.5rem (less needed; grid already
 *                    visually separates the two regions)
 *   xl+            - slightly more on wide screens
 *   short laptop   - tighten so 100vh still fits
 * ============================================================ */
@media (max-width: 1023.98px) {
  .hero-shell .ataglance-wrap {
    padding-top: clamp(1.5rem, 6vw, 3rem);
    margin-top: clamp(0.5rem, 2vw, 1.25rem);
  }
  /* On mobile the scene caption + ataglance both live in a
   * single stacked column. Add a hairline separator visual cue
   * via extra top padding on the .ataglance itself so the cells
   * don't read as one continuous block with the scene caption. */
  .hero-shell .ataglance-wrap .ataglance {
    padding-top: 0.5rem;
  }
}
@media (min-width: 1024px) {
  .hero-shell .ataglance-wrap {
    padding-top: clamp(1.25rem, 1.4vh + .25rem, 2.5rem);
  }
}
@media (min-width: 1280px) {
  .hero-shell .ataglance-wrap {
    padding-top: clamp(1.5rem, 1.6vh + .5rem, 3rem);
  }
}
@media (min-width: 1600px) {
  .hero-shell .ataglance-wrap {
    padding-top: clamp(2rem, 1.8vh + .75rem, 3.5rem);
  }
}
@media (min-width: 1024px) and (max-height: 820px) {
  .hero-shell .ataglance-wrap { padding-top: .75rem; }
}
@media (min-width: 1024px) and (max-height: 720px) {
  .hero-shell .ataglance-wrap { padding-top: .5rem; }
}

/* === 6.7 Hero → first section gap on subpages ==============
 * Several subpages (/healthcare, /our-clients, /trust, /careers,
 * /our-work) use py-16 lg:py-20 on the section after the hero,
 * leaving only 64-80px of breathing room. Bump to a fluid
 * minimum so the hero never touches the content.
 * ============================================================ */
[id^="page-"] > .hero-shell + .hairline + section,
main > .hero-shell + .hairline + section {
  /* Was clamp(2.5rem, 5vw, 4.5rem) ≈ 72px - far too much now that heroes
     are compact. Match the normal section rhythm so top = bottom. */
  padding-top: clamp(1.5rem, 2.2vw, 2.5rem);
}
@media (max-width: 480px) {
  [id^="page-"] > .hero-shell + .hairline + section,
  main > .hero-shell + .hairline + section {
    padding-top: 1.75rem;
  }
}

/* === 6.8 Custom cursor visibility polish - REMOVED ==========
 * The custom dot + ring cursor was dropped from Pentabay.html in
 * favour of the native OS pointer, so #pb-cursor / #pb-cursor-ring
 * no longer exist and the light-theme halo that used to live here
 * had nothing left to style.
 * ============================================================ */

/* === 6.9 Mobile hamburger panel - narrower, roomier gutter =
 * The 320px panel on a 375 phone covers ~85% of viewport.
 * Slimmer 288px panel with 32px right gutter feels less
 * intrusive and matches modern mobile dropdown patterns.
 * Below 360 (iPhone SE), use full-bleed.
 * ============================================================ */
.pb-nav-mobile-panel {
  width: min(288px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  right: 16px;
}
@media (max-width: 360px) {
  .pb-nav-mobile-panel {
    left: 12px;
    right: 12px;
    width: auto;
    max-width: none;
  }
}


/* ============================================================
 * WHY CHOOSE US  (#why-us .wcu-card / .wcu-ico)
 * New homepage differentiator section. Reuses the shared .card
 * (radius/padding/hover-lift) and only adds the icon tile + a
 * subtle staggered rise so it matches the existing motion system.
 * ============================================================ */
.wcu-card { min-width: 0; }
.wcu-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  flex: 0 0 auto;
  border: 1px solid var(--pb-hairline);
  background: color-mix(in oklab, var(--pb-cobalt-2) 12%, transparent);
  color: var(--pb-cobalt-2);
}
.wcu-ico svg { width: 24px; height: 24px; display: block; }
.wcu-card h3 { color: var(--pb-fg); }
.wcu-card a {
  color: var(--pb-fg);
  text-decoration: underline;
  text-underline-offset: 2px;
  text-decoration-color: var(--pb-hairline);
  transition: text-decoration-color .2s ease, color .2s ease;
}
.wcu-card a:hover { color: var(--pb-cobalt-2); text-decoration-color: currentColor; }

/* Subtle per-card entrance once the section reveals. The section's own
 * [data-reveal] fade still runs; this only layers a gentle stagger.
 * Gated on prefers-reduced-motion via the block below. */
@media (prefers-reduced-motion: no-preference) {
  #why-us.in .wcu-card {
    animation: wcu-rise .6s cubic-bezier(.22, .61, .36, 1) both;
  }
  #why-us.in .wcu-card:nth-child(1) { animation-delay: .04s; }
  #why-us.in .wcu-card:nth-child(2) { animation-delay: .10s; }
  #why-us.in .wcu-card:nth-child(3) { animation-delay: .16s; }
  #why-us.in .wcu-card:nth-child(4) { animation-delay: .22s; }
  #why-us.in .wcu-card:nth-child(5) { animation-delay: .28s; }
  #why-us.in .wcu-card:nth-child(6) { animation-delay: .34s; }
}
@keyframes wcu-rise {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: none; }
}


/* ============================================================
 * FAQ ACCORDION  ([data-faq] > details.faq-item)
 * Native <details>/<summary> keeps full accessibility; JS
 * (FaqAccordion.jsx) enforces single-open. Here we add the smooth
 * expand/collapse animation:
 *   - Baseline (all browsers): the answer fades + slides in on open.
 *   - Progressive enhancement: browsers that support interpolate-size
 *     + ::details-content get a true animated height on open AND close.
 * The chevron rotation is already handled in Pentabay.html
 * (details[open] .chev { transform: rotate(180deg) }).
 * ============================================================ */
[data-faq] > details.faq-item > summary {
  transition: color .2s ease;
}
[data-faq] > details.faq-item > summary:hover .faq-q { color: var(--pb-cobalt-2); }
[data-faq] > details.faq-item > summary:focus-visible {
  outline: 2px solid var(--pb-cobalt-2);
  outline-offset: 4px;
  border-radius: 8px;
}
.faq-a a {
  color: var(--pb-fg);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.faq-a a:hover { color: var(--pb-cobalt-2); }

/* Baseline reveal - plays whenever the panel opens. */
@media (prefers-reduced-motion: no-preference) {
  details.faq-item[open] > .faq-a-wrap {
    animation: faq-open .32s cubic-bezier(.22, .61, .36, 1);
  }
}
@keyframes faq-open {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: none; }
}

/* Progressive enhancement - genuine height animation on open + close. */
@supports (interpolate-size: allow-keywords) {
  details.faq-item { interpolate-size: allow-keywords; }
  details.faq-item::details-content {
    block-size: 0;
    overflow: clip;
    transition: block-size .32s cubic-bezier(.22, .61, .36, 1),
                content-visibility .32s allow-discrete;
  }
  details.faq-item[open]::details-content { block-size: auto; }
  @media (prefers-reduced-motion: reduce) {
    details.faq-item::details-content { transition: none; }
  }
}


/* ============================================================
 * FAQ ASIDE ARTWORK  (#faq .faq-aside-art)
 * The homepage FAQ's left column runs out of copy ~700px before the
 * accordion beside it ends. This fills that gap with the platform
 * render as ambient texture, not a photo: a radial mask dissolves
 * every edge so no rectangle is visible, and opacity + a desaturating
 * filter keep it well below the copy in contrast. The render is
 * near-black with a blue glow, so on the near-white light theme it has
 * to be pulled right back to a faint blue-grey wash; the dark theme
 * can carry much more of it.
 * lg+ only - below that the column sits directly above the accordion,
 * where anything decorative just pushes the questions down.
 * ============================================================ */
/* Superseded: the FAQ aside artwork was removed from Pentabay.html. Kept as
   a no-op guard so any cached or partially-updated markup can't render the
   old floating image mid-column. */
.faq-aside-art { display: none !important; }

/* ============================================================
 * WHY CHOOSE US BACKGROUND  (#why-us > .whyus-art)
 * The platform render as a full-bleed background for the band, with the
 * eyebrow / heading / lead / proof cards sitting on top.
 *
 * #why-us is a .shell (max-width + auto margins), so an inset:0 child
 * would stop at the content box and show two hard vertical edges. The
 * 50% / translateX(-50%) / 100vw trick breaks it out to the viewport,
 * and \`overflow: clip\` on the section keeps that from creating a
 * horizontal scrollbar. \`clip\` rather than \`hidden\` so the section is
 * not turned into a scroll container - \`hidden\` would break
 * scroll-into-view for anything anchored inside it.
 *
 * Contrast: the source render is near-black with a bright blue glow, and
 * the default theme is near-white with navy text. Laying it in raw would
 * bury the copy, so on light it is taken down to 0.14 with saturation and
 * contrast pulled in, then a scrim gradient (opaque at the top-left where
 * the heading sits, clearing toward the bottom-right) is drawn over it.
 * Net effect is texture behind the band, with body copy still on an
 * essentially flat surface. The dark theme can carry far more of it.
 * ============================================================ */
/* No overflow:clip - see the note in the SELECT CLIENTS block below. */
#why-us { position: relative; isolation: isolate; }
/* Everything that isn't the art gets stacked above it. */
#why-us > *:not(.whyus-art) { position: relative; z-index: 1; }

/* Shared edge-ramp depth for the two full-bleed dark bands (#trust, #why-us).
   Must always stay SMALLER than the band's own vertical padding, or the ramp
   reaches past the padding and starts washing out the copy sitting on the band.
   Measured padding on these sections: 44px @375, 56px @768, 66.6px @1024,
   93.6px @1440 - i.e. it clamps at 44px and then tracks ~6.5vw. This is the same
   curve at ~80%: 34px @375, 38px @768, 51px @1024, 72px @1440, 76px capped.
   A flat 80px was tried first and was fine at 1440 but overran the 44px padding
   at 375, fading the WHY CHOOSE US eyebrow and the "See our work" button.
   One declaration rather than four breakpoints so the two can't drift apart. */
.trust-art,
.whyus-art { --band-ramp: clamp(34px, 5vw, 76px); }

/* BLUE DUOTONE, not grey. The source render (faq-secure-data.webp) is a
   near-monochrome grey-on-black studio shot, and at 26% over a white page it
   read as a flat grey smudge - the one thing this band should not be, since
   every other surface on the homepage is already neutral.
   The tint is done as a real duotone rather than a filter() chain: the art box
   carries a brand-blue field, and the <img> above it blends in
   mix-blend-mode: luminosity, so the render contributes only its light and
   shade while the hue comes entirely from the token. That keeps it exactly
   --pb-blue (no drifting toward cyan or purple the way hue-rotate does on a
   desaturated source) and it stays correct if the token is ever retuned.
   isolation: isolate is load-bearing - without it the img would blend past the
   art box and pick up the page behind the section. */
.whyus-art {
  position: absolute;
  top: 0; bottom: 0; left: 50%;
  width: 100vw;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  isolation: isolate;
  background: var(--pb-blue);
  /* The deep end of the band, used by the scrim's right-hand stop. Derived from
     --pb-blue rather than hard-coded so it tracks the brand token; there is no
     --pb-navy in the palette. */
  --whyus-deep: color-mix(in oklab, var(--pb-blue) 45%, #04122E);
  /* Edge ramps, top AND bottom - unlike #trust this band is an island with page
     colour on both sides (services-canada above, industries-serve below), and it
     was the worst offender in the page's vertical rhythm: measured 247 and 240
     luminance steps into white on the light theme, i.e. two knife edges.
     Masking the whole art box (blue field + duotone img + scrim) fades the band
     out and reveals the page behind it, so no colour is hard-coded and it stays
     correct on both themes.
     Depth comes from --band-ramp (declared just above), sized to stay inside
     this section's vertical padding at every width, so neither ramp reaches the
     heading block or the CTA row. */
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 var(--band-ramp), #000 calc(100% - var(--band-ramp)), transparent 100%);
          mask-image: linear-gradient(180deg, transparent 0, #000 var(--band-ramp), #000 calc(100% - var(--band-ramp)), transparent 100%);
}
.whyus-art img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: 62% 50%;   /* keeps the glowing core out of the copy column */
  mix-blend-mode: luminosity;
}
/* Scrim. Sits above the image, below the content.
   Two passes, and they do different jobs now that the field underneath is a
   saturated blue rather than a grey wash:

   1. The horizontal pass keeps the COPY side on the page surface. It has to be
      fully opaque further across than before (0 -> 46%) because the heading,
      lead and the left-hand proof cards all sit over it and navy-on-blue is
      not a contrast pairing that survives.
   2. From ~62% out it clears almost entirely, which is the "non-text area" -
      that is where the blue is allowed to go deep, and a --pb-navy layer is
      added on top of the right end so it deepens rather than just getting more
      saturated.
   3. The vertical pass closes to surface at both ends so the band still blends
      into the hairline dividers above and below, and holds a good deal of cover
      across the bottom two-thirds where the WHAT SETS US APART boxes sit. */
/* Scrim, rebuilt 2026-07: one flat Deep Ink -> Midnight Panel wash instead
   of the old three-pass, surface-coloured scrim.
   Why the old one had to go: every pass was drawn in var(--pb-surface),
   which on the default LIGHT theme is #FFFFFF. It was opaque white from 0
   to 46% and cleared to transparent at 100%, so the band rendered as a
   white left half meeting a blue right half on a soft diagonal, closing to
   white at the top and bottom too. That reads as an inset panel sitting on
   the page - the "grid / card instead of a backdrop" problem - not as a
   backdrop. A flat wash has no internal edge to read as a boundary.
   Both stops are theme-independent by design: this band is now dark on
   BOTH themes, which is what makes it match the hero. The copy that sits
   directly on it is recoloured to white further down - see the
   FULL-BLEED BAND COPY block. */
.whyus-art::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(180deg,
    rgba(6, 10, 20, 0.88) 0%,
    rgba(8, 16, 31, 0.82) 100%);
}
/* grayscale(1) first, so the source render's own faint colour cast cannot
   fight the --pb-blue field it is blending onto.
   The luminance COMPRESSION that used to sit here is deliberately gone. It
   was contrast(0.5) brightness(1.12) on light and contrast(0.72) on dark,
   and its whole job was keeping the render pale enough for navy body copy
   to sit on top of it. The band is dark now and that copy is white, so the
   render wants the opposite treatment: its full range, pushed slightly
   brighter so it still reads through an 0.88 scrim. Same values on both
   themes, because it is the same dark band on both. */
.whyus-art img {
  opacity: 1;
  filter: grayscale(1) contrast(1.05) brightness(1.15);
}

/* Cards are background:transparent by default, which would let the render
   show through behind their text. Give them the page surface back so the
   card copy sits on a flat field while the art stays visible in the gutters
   between them. Raised from 88% to near-opaque: at 88% over the old grey wash
   the tint was invisible, but over a saturated blue the right-hand cards
   picked up a visible cast and stopped matching the left-hand ones. */
/* The four proof cards (100+, 6, 5.0, 85%) are plain .card elements - there is
   no .stat-card / .wus-card class on them. They were 97% var(--pb-surface),
   which on the LIGHT theme is #FFFFFF, so four near-opaque white rounded cards
   sat on a band that is dark on both themes. That is what read as "the section
   is still in a box": the art behind them is genuinely full-bleed (measured
   0 -> viewport width, border-radius 0), the bright cards were the boxes.

   Now a dark translucent plate. Values are theme-independent literals on
   purpose, matching the .whyus-art scrim above - this band is dark on BOTH
   themes, so a token like --pb-elevated-1 (which is #F7F8FA on light) would
   put the white cards straight back.

   The token rescope is what makes the card copy work without !important: the
   markup sets its body colour inline (style="color:var(--pb-muted)"), and an
   inline style beats any class rule. Redefining the two tokens on the card
   itself lets that inline declaration resolve to a light value instead, and
   .stat-num / .t-eyebrow inherit correctly for free. */
/* The card surface itself is set once, further down, in the
   [data-route="home"] #why-us block - that selector is (1,2,0) and wins over
   anything declared here at (1,1,0). Do not add a competing background to this
   rule; edit the later one. */
/* Same reason as the cards above - these boxes run the full width of the band,
   so the right-hand pair sit over the deepest part of the blue. --pb-elevated-2
   is a near-transparent tint, which is fine on a neutral page and not fine
   here, so it is composited onto the surface first. */
/* Same problem, same fix: these five ran the full width of the band at 94%
   var(--pb-surface). Left light while the proof cards above went dark, the band
   would have read as half-finished, so they move together. Slightly more
   transparent than the cards (0.62 vs 0.70) because they are larger plates and
   the render behind them is worth keeping visible. */
/* Surface set in the [data-route="home"] block further down - see above. */

/* The image used to be dropped below lg (\`.whyus-art img { display: none }\`
   plus \`background: none\`) because on a light band a narrow copy column
   left no clear area for it. That reason is gone: the band is a dark
   full-bleed backdrop at every width now and the copy on it is white, so
   the render stays in on phones too. Removing this was necessary for the
   375px full-bleed check - with the image and the blue field both killed,
   a phone got a flat scrim over nothing. */

/* ============================================================
 * CTA BAND ARTWORK  (.cta-band-art - /compliance only)
 * Sits under the CTA row as a real, fully-legible figure rather than a
 * dialled-back background wash. It is placed BELOW the buttons, not
 * behind the copy, precisely so it can stay at full strength: the
 * heading in this band is centred and the render's subject is centred
 * too, so overlaying them would have forced the image back down to a
 * smudge to keep the type readable.
 *
 * Framed rather than feathered, in BOTH themes. The first pass masked the
 * edges so the render dissolved into the page; because its own ground is
 * near-white that made it read as barely-there on light and as a glowing
 * oval on dark. A defined frame - rounded corners, hairline border, its
 * own light surface, soft shadow - is what actually makes it read as a
 * deliberate visual at full strength.
 * ============================================================ */
.cta-band-art {
  margin: clamp(3rem, 5vw, 4.5rem) auto 0;
  max-width: 68rem;
  width: 100%;
  border-radius: 22px;
  border: 1px solid var(--pb-hairline);
  /* The render's own ground, so the frame and the image share one surface
     and no seam shows at the rounded corners. */
  background: #F4F6FA;
  overflow: hidden;
  box-shadow: 0 26px 70px -34px rgba(10, 31, 68, 0.30);
}
.cta-band-art img {
  display: block;
  width: 100%;
  height: auto;
  opacity: 1;
  /* Slight lift so the pale render holds its own against the page rather
     than washing out - contrast first, then a touch of saturation for the
     blue glow and the maple leaf. */
  filter: contrast(1.06) saturate(1.08);
}
:root[data-theme="dark"] .cta-band-art {
  box-shadow: 0 26px 70px -30px rgba(0, 0, 0, 0.65);
}
/* The band carries py-32/py-48 already; with a figure added that reads as
   too much dead space below, so pull the bottom padding in. */
.cta-band-art { margin-bottom: calc(-1 * clamp(1rem, 3vw, 3rem)); }


/* ============================================================
 * SELECT CLIENTS BACKGROUND  (#trust > .trust-art)
 * Same full-bleed technique as .whyus-art above (50% / translateX(-50%) /
 * 100vw to escape the .shell content box, overflow: clip on the section
 * so it makes no horizontal scrollbar), with the scrim retuned for a
 * centred band instead of a left-aligned one.
 *
 * #why-us could clear its scrim toward one side because the copy is all in
 * the left column. Here the eyebrow / heading / lead are centred and the
 * client marquee runs across the bottom, so the scrim is vertical: opaque
 * behind the heading block, thinnest through the empty gap around the
 * "See Clutch reviews" link, tightening again over the logo row (whose
 * names and meta lines are text directly on the background, not on cards)
 * and closing to solid at both ends so the band blends into the hairlines
 * above and below. A second, softer horizontal pass adds a little extra
 * cover behind the centred copy column without touching the outer gutters,
 * which is where most of the render actually shows.
 * ============================================================ */
/* NO overflow:clip here - that was the actual full-bleed bug.
   Both sections carry class="shell", i.e. max-width: min(95rem, max(80rem,
   80vw)) with auto margins - 1280px inside a 1440px viewport. The art child
   escapes that box with left:50% / width:100vw / translateX(-50%), and its
   getBoundingClientRect() correctly reports the full 1440px... but
   overflow:clip on the SECTION then clipped the painted result straight back
   to the section's 1280px padding box, leaving an 80px page-coloured margin
   down each side. That inset band is exactly the "grid / card" look.
   Clipping does not change layout geometry, which is why a rect-based check
   reported full-bleed while the screenshot plainly showed edges.
   It is safe to drop: html, body { overflow-x: clip } is already set in the
   overflow safeguards block above, so a 100vw child cannot raise a
   horizontal scrollbar. This is the same thing .tq-grid relies on for its
   own full-bleed. */
#trust { position: relative; isolation: isolate; }
#trust > *:not(.trust-art) { position: relative; z-index: 1; }

.trust-art {
  position: absolute;
  /* -3rem, not 0. The hairline divider between the hero and this section
     contributes a ~47px page-coloured gap (1px rule + clamp() margins either
     side), and margins cannot be painted. On the light theme that gap was a
     white sliver pinched between the dark hero bottom and this dark band -
     half of the "blue, sudden white, sudden black" read. Pulling the art up
     over it closes the gap from this side; the hero's ::before fade ends on the
     same Deep Ink from the other. 3rem covers the widest the gap gets
     (clamp maxes at 1.5rem a side); where the gap is narrower the overshoot
     lands on the hero's own faded-to-Deep-Ink bottom, so it cannot show. This
     also hides that hairline, which is the point - a rule between two dark
     bands is just another edge. */
  top: -3rem; bottom: 0; left: 50%;
  /* Full-bleed, like the hero band above. This was previously capped at
     min(100vw, 1680px) with a 12%/88% end fade to limit how hard the 1400px
     source got upscaled - but on a wide display that cap plus the fade left
     the visible art NARROWER than the 1520px content column sitting on top
     of it, so the band read as inset while the hero read as edge-to-edge.
     Matching the hero wins over the upscale: the render is a soft out-of-focus
     grid at 34% opacity behind a scrim, so softness from the extra upscale is
     not something the eye can pick out anyway. The internal scale() below came
     down to compensate. */
  width: 100vw;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  /* isolation is load-bearing for the duotone below: without it the img's
     luminosity blend reaches past this box and picks up the page behind
     the section instead of the blue field. */
  isolation: isolate;
  /* BLUE, not grey. trust-secure-grid.webp is a neutral grey-on-black grid,
     and a Deep Ink scrim over a grey source gives a dark GREY band - the
     scrim alone cannot add hue that is not in the source. Same duotone
     mechanism .whyus-art uses: this box carries a brand-blue field and the
     img blends onto it in luminosity, contributing light and shade only
     while the hue comes from the token. That is what makes the grid pattern
     read as blue rather than gray.
     The 4%/96% edge mask that used to be here is removed - it faded the art
     out before it reached the viewport edge, so the band looked narrower
     than the content column on top of it. Full-bleed means reaching the
     edge. */
  background: var(--pb-blue);
  /* Bottom edge ramp. The scrim below is a flat wash with inset:0, so the band
     used to start and stop at full strength on a single row - a measured 242/256
     luminance step into the page on the light theme. Masking the whole art box
     (blue field + duotone img + scrim together) fades the entire band out, which
     reveals whatever the page is behind it, so this is correct on both themes
     with no colour hard-coded.
     Depth comes from --band-ramp (declared above the .whyus-art rule), which is
     sized to stay inside this section's vertical padding at every width, so the
     ramp never washes over the logo marquee.
     Top is left solid on purpose - it meets the hero, per the offset above. */
  -webkit-mask-image: linear-gradient(180deg, #000 0, #000 calc(100% - var(--band-ramp)), transparent 100%);
          mask-image: linear-gradient(180deg, #000 0, #000 calc(100% - var(--band-ramp)), transparent 100%);
}
.trust-art img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: 50% 62%;
  /* The band is far wider than the render's aspect, so cover crops only
     vertically and object-position-x does nothing: the glowing cube would
     land dead centre, behind the copy column. Scaling up from a left-of-
     centre origin pushes it into the right third (clear space) and enlarges
     the glow, which is the only part of the render that reads at all once
     the light-theme opacity is applied.

     Was 1.35 while the art box was capped at 1680px. Now that the box is
     full-bleed the box itself supplies the extra width, so the internal
     scale comes down to keep the effective upscale roughly where it was.

     Origin moved to centre 2026-07. Pushing the glow off to 16%/45% existed
     to keep it out of the centred copy column on a light band; with a flat
     dark scrim over the whole thing and white copy on top there is nothing
     to dodge, and an off-centre origin made the grid denser on one side -
     which is itself an edge the eye can find. */
  transform: scale(1.08);
  transform-origin: 50% 50%;
  mix-blend-mode: luminosity;
}
/* Scrim, rebuilt 2026-07: one flat Deep Ink -> Midnight Panel wash.
   The old scrim was six vertical stops plus a horizontal pass, all drawn in
   var(--pb-surface) - i.e. #FFFFFF on the default light theme - placed to
   dodge each block of copy: solid to 28% behind the heading, opening to 14%
   around the Clutch link, tightening again over the logo names, solid at
   both ends. Every one of those transitions is an edge, and the horizontal
   pass darkened the gutters relative to the middle, so the band read as a
   centred inset panel rather than a backdrop.
   A single flat wash has no internal transition to read as a boundary, and
   because it is a fixed dark value rather than the surface token it is the
   same band on both themes - which is what makes it match the hero.

   2026-08: 0.85/0.80 -> 0.50/0.54, i.e. the band now reads BLUE.
   At 0.85 the wash buried the --pb-blue field under it: composited, the band
   measured rgb(7,20,45) - near-black, and next to a hero that is plainly blue
   it read as a different site rather than the same passage. Halving the wash
   lands it on rgb(12,48,109), which is the blue field showing through with
   enough ink left to hold the grid render back.

   Contrast was the reason the wash was heavy, and it is not at risk: the copy
   on this band is #FFF / #EAF0FA / rgba(255,255,255,0.88) (see FULL-BLEED BAND
   COPY), which is 14.1:1 / 12.3:1 / 11:1 against rgb(12,48,109) - the change
   moves those UP, because ink over a mid blue is darker than the blue alone.

   The first two stops are the exception to the flat-wash rule and they are
   load-bearing: .trust-art starts at top:-3rem, overlapping the bottom of
   .hero-shell::before, which ramps to Deep Ink #060A14. Opening the blue at
   0% would put a blue edge against that ink. Holding 0.90 at the top and
   easing to the band value by 14% (~90px at this band's height, versus the
   hero ramp's own 160px) keeps hero -> #trust the one continuous passage that
   the ::before comment describes. The bottom needs no such stop: the mask on
   .trust-art fades the whole band out into the page below. */
.trust-art::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(180deg,
    rgba(6, 10, 20, 0.90) 0%,
    rgba(6, 10, 20, 0.50) 14%,
    rgba(8, 16, 31, 0.50) 62%,
    rgba(8, 16, 31, 0.54) 100%);
}
/* Full strength on both themes. The old 0.34 / 0.5 split existed to stop a
   grey render burying navy copy on a white page; the scrim above now does
   the darkening, the copy on top is white, and holding the render back as
   well would just net a flat band with no visible grid. */
.trust-art img { opacity: 1; filter: contrast(1.08) brightness(1.06); }

/* The image used to be dropped below lg. Removed for the same reason as on
   #why-us: the band is a dark full-bleed backdrop at every width now, so
   there is no light surface for the render to crowd, and a phone would
   otherwise get a flat scrim over a bare blue field. */


/* ============================================================
 * EYEBROW DASH - removed app-wide (keep the label text)
 * Drop only the decorative leading dash that ran before every
 * eyebrow (the ::before hairline) - it read as a template/AI tic.
 * The eyebrow label text itself is kept. Done in CSS (loaded last,
 * !important) so it also covers the dynamically injected pages
 * (PB_PAGES, extras, subpages) in one place.
 * ============================================================ */
.t-eyebrow::before { display: none !important; }


/* ============================================================
 * CLIENT TRUST STATS  (#trust .client-stats)
 * Centered metrics bar: accent number + muted label inline
 * ("100+ Projects · 20+ Clients · …"), separated by vertical
 * hairline dividers - no outer box, matching the reference band.
 * ============================================================ */
.client-stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
  margin-inline: auto;
}
.client-stat {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px clamp(16px, 2.4vw, 30px);
  border-right: 1px solid var(--pb-hairline);
}
.client-stat:last-child { border-right: 0; }
.cs-num {
  font-family: 'Open Sans', monospace;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-size: clamp(1.375rem, 1.9vw, 1.75rem);
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: var(--pb-cobalt-2);
}
.cs-label {
  font-size: clamp(0.875rem, 1vw, 1rem);
  font-weight: 500;
  color: var(--pb-muted);
}
@media (max-width: 560px) {
  .client-stat { padding: 4px 14px; }
  .cs-label { font-size: 0.8125rem; }
}

/* ------------------------------------------------------------
 * Home #trust: compact stats + Clutch-rating bar. Replaces the old centred
 * heading/lead/plain-stats block to cut vertical whitespace. Icon + metric
 * on the left, the Clutch review chip on the right, in one bordered band;
 * the client-logo carousel is kept below (small eyebrow labels it).
 * ------------------------------------------------------------ */
.pb-statbar {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: 16px 28px;
  padding: clamp(16px, 1.8vw, 22px) clamp(18px, 2.4vw, 30px);
  border: 1px solid var(--pb-hairline);
  border-radius: 16px;
  background: var(--pb-surface, rgba(127, 127, 127, 0.04));
}
.pb-statbar-stats { display: flex; flex-wrap: wrap; align-items: center; gap: 14px 26px; }
.pb-stat { display: flex; align-items: center; gap: 12px; }
.pb-stat-ico {
  width: 38px; height: 38px; border-radius: 10px; flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(11, 92, 255, 0.08); color: var(--pb-blue);
}
.pb-stat-ico svg { width: 19px; height: 19px; }
.pb-stat-txt { display: flex; flex-direction: column; line-height: 1.15; }
.pb-stat-num {
  font-family: 'Open Sans', monospace; font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-size: clamp(1.05rem, 1.5vw, 1.3rem); color: var(--pb-cobalt-2);
}
.pb-stat-lbl { font-size: 0.78rem; color: var(--pb-muted); }
.pb-statbar-review { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; }
.pb-clutch { display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; }
.pb-clutch-word { font-weight: 800; letter-spacing: -0.02em; }
.pb-clutch-stars { color: #E62415; letter-spacing: 2px; font-size: 0.95rem; }
.pb-clutch-out { font-size: 0.8rem; color: var(--pb-muted); }
.pb-trust-eyebrow { display: flex; justify-content: center; margin-top: clamp(1.5rem, 2.5vw, 2.25rem); }
@media (max-width: 700px) {
  .pb-statbar { justify-content: center; }
  .pb-statbar-review { width: 100%; justify-content: center; }
}
:root[data-theme="dark"] .pb-stat-ico { background: rgba(120, 160, 255, 0.15); color: var(--pb-blue-3, #7CA0FF); }


/* ============================================================
 * CLIENT CAROUSEL  (#trust .client-carousel)
 * Manual paginated logo carousel (ClientCarousel.jsx). Borderless
 * logo cells with name + sector beneath, whole-page slide, side
 * arrows and pagination dots. The viewport is inset by the arrow
 * gutter so nothing overflows the page.
 * ============================================================ */
.client-carousel {
  position: relative;
  max-width: 100%;
}
/* Viewport is the drag surface and clips the strip. The mask fades both
   edges so logos ease in/out rather than hard-clipping - reinforces the
   seamless, endless feel. touch-action: pan-y keeps vertical page scroll
   working while horizontal drags are handled by the marquee. */
.cc-viewport {
  overflow: hidden;
  width: 100%;
  cursor: grab;
  touch-action: pan-y;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%);
}
.cc-viewport.is-dragging { cursor: grabbing; }
/* One non-wrapping row; JS clones it and translates it every frame. */
.cc-track {
  display: flex;
  flex-wrap: nowrap;
  width: max-content;
  will-change: transform;
  user-select: none;
  -webkit-user-select: none;
}
.cc-logo {
  flex: 0 0 auto;
  width: clamp(148px, 15vw, 188px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  padding: 4px clamp(8px, 1.4vw, 22px);
}
.cc-logo img { -webkit-user-drag: none; user-drag: none; }
.cc-mark {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 14px;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid var(--pb-hairline-60);
  background: var(--pb-elevated-1);
}
.cc-mark .cc-letter {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Open Sans', system-ui, sans-serif;
  font-weight: 700;
  font-size: 24px;
  letter-spacing: -0.01em;
}
.cc-mark img {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 10px;
  display: block;
  background: var(--pb-elevated-1);
}
.cc-name {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--pb-fg);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cc-meta {
  font-family: 'Open Sans', monospace;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--pb-muted);
}

/* Arrows sit in the inset gutter so they never overflow the page. */
.cc-arrow {
  position: absolute;
  top: calc(50% - 14px);
  transform: translateY(-50%);
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--pb-hairline);
  background: var(--pb-surface);
  color: var(--pb-fg);
  cursor: pointer;
  transition: border-color .2s ease, color .2s ease, background .2s ease;
}
.cc-arrow:hover { border-color: var(--pb-cobalt-2); color: var(--pb-cobalt-2); }
.cc-arrow:focus-visible { outline: 2px solid var(--pb-cobalt-2); outline-offset: 3px; }
.cc-arrow svg { width: 18px; height: 18px; }
.cc-prev { left: 0; }
.cc-next { right: 0; }

.cc-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 22px;
}
.cc-dot {
  width: 9px;
  height: 9px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  cursor: pointer;
  background: var(--pb-hairline);
  transition: background .2s ease, transform .2s ease;
}
.cc-dot.is-active { background: var(--pb-cobalt-2); transform: scale(1.15); }
.cc-dot:focus-visible { outline: 2px solid var(--pb-cobalt-2); outline-offset: 2px; }

@media (max-width: 560px) {
  .client-carousel { padding-inline: 38px; }
}

/* ============================================================
 * SERVICES MARQUEE  (#services .svc-marquee)
 * The 8 service cards run as a single continuously-scrolling row
 * (ServicesCarousel.jsx) instead of a 2-row grid, so the section is
 * far shorter. Auto-scroll + hover-pause + drag; cards keep equal
 * height via flex stretch.
 * ============================================================ */
.svc-marquee { position: relative; }
.svc-marquee-viewport {
  overflow: hidden;
  cursor: grab;
  touch-action: pan-y;
  padding: 6px;
  margin: -6px;
}
.svc-marquee-viewport.is-dragging { cursor: grabbing; }
.svc-marquee-track {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 20px;
  width: max-content;
  will-change: transform;
  user-select: none;
  -webkit-user-select: none;
}
.svc-marquee-track .service-card {
  flex: 0 0 clamp(286px, 24vw, 336px);
  width: clamp(286px, 24vw, 336px);
  margin: 0;
}
.svc-marquee-track .service-card a { -webkit-user-drag: none; }

/* Prev / next arrows (built by ServicesCarousel.jsx, appended to .svc-marquee
   so they sit OUTSIDE .svc-marquee-viewport - inside it they would be on the
   drag surface and every press would begin a drag).
   Same 44px pill as .testi-arrow so the two carousels read as one control
   vocabulary. They overlay the part-cards at the band's edges, which is where a
   carousel arrow is expected; the viewport has 6px of padding and -6px of
   margin, so left/right 2px lands them just inside the visible edge.
   NOTE: no backticks anywhere in this block - these rules live inside a JS
   template literal, so a backtick here ends the string and breaks the build. */
.svc-arrow {
  position: absolute; top: 50%; transform: translateY(-50%); z-index: 3;
  left: auto; right: auto;
  width: 44px; height: 44px; border-radius: 9999px;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0;
  border: 1px solid var(--pb-hairline);
  background: var(--pb-surface);
  color: var(--pb-fg);
  cursor: pointer;
  box-shadow: 0 8px 24px -10px rgba(8, 16, 31, 0.55);
  transition: border-color .2s ease, color .2s ease, background .2s ease;
}
.svc-arrow:hover { border-color: var(--pb-blue); color: var(--pb-blue); }
.svc-arrow:focus-visible { outline: 2px solid var(--pb-blue); outline-offset: 3px; }
.svc-arrow svg { width: 18px; height: 18px; }
.svc-prev { left: 2px; }
.svc-next { right: 2px; }
/* Solid fill, not translucent: this band runs over a blue gradient and a
   see-through pill loses its edge against the card behind it. A slightly
   stronger rule than .testi-arrow for the same reason - that one sits on a flat
   page, this one sits on art. */
.svc-arrow { border-color: var(--pb-haze); }
@media (max-width: 639.98px) {
  /* Thumb-swipe is the primary gesture at this width; keep the arrows but make
     them smaller so they cover less of an already-narrow card. */
  .svc-arrow { width: 36px; height: 36px; }
  .svc-arrow svg { width: 15px; height: 15px; }
}

/* Custom Software page - lift the stat numbers so they read as the focal
   point of each tile: heavier weight, brand-cobalt colour, tighter tracking
   and a touch larger. Scoped to the page so other stat blocks are untouched. */
#page-custom-software .stat-num {
  font-weight: 700;
  color: var(--pb-blue);
  letter-spacing: -0.02em;
  font-size: 2.6rem;
  line-height: 1;
}

/* ============================================================
 * TESTIMONIAL CAROUSEL  (#reviews .testi-carousel)
 * Single large bordered card, one review at a time, with side
 * arrows and pagination dots (TestimonialCarousel.jsx). Centred
 * header + rating badge above. Responsive: two-column card on
 * desktop, stacked + centred below 768px.
 * ============================================================ */
/* The 46rem cap made the lead break awkwardly - at some widths mid-word,
   on "ghost-write", because a break is allowed at an existing hyphen.
   Wider cap plus balanced wrapping evens the lines out; the nowrap span
   keeps that one hyphenated word whole at every width. */
.testi-head { max-width: 54rem; margin: 0 auto; text-align: center; }
.testi-head h2 { text-wrap: balance; }
.testi-head .t-lead { text-wrap: balance; max-width: 46rem; margin-inline: auto; }
.testi-head .t-lead { hyphens: none; }
.testi-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: clamp(1.25rem, 2vw, 1.75rem);
  padding: 10px 18px;
  border: 1px solid var(--pb-hairline);
  border-radius: 14px;
  background: var(--pb-elevated-1);
}
.testi-badge-stars { display: inline-flex; gap: 2px; }
.testi-badge-num { font-family: 'Poppins', system-ui, sans-serif; font-weight: 700; font-size: 1.05rem; letter-spacing: -0.01em; }
.testi-badge-meta {
  /* Was 'JetBrains Mono', which is not among the loaded families (only Poppins
     and Open Sans are self-hosted in main.jsx), so it silently fell through to
     ui-monospace - a third, system-dependent typeface on the page. Open Sans
     per the two-typeface rule; the uppercase + tracking already carry the
     label treatment. */
  font-family: 'Open Sans', system-ui, sans-serif;
  font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--pb-muted);
}

.testi-carousel { position: relative; margin-top: clamp(2.5rem, 5vw, 4rem); }
.testi-viewport { overflow: hidden; min-width: 0; }
.testi-track { display: flex; transition: transform .5s cubic-bezier(.22, .61, .36, 1); will-change: transform; }
@media (prefers-reduced-motion: reduce) { .testi-track { transition: none; } }
.testi-slide { flex: 0 0 100%; min-width: 100%; }
.testi-card {
  position: relative;
  border: 1.5px solid var(--pb-blue);
  border-radius: 28px;
  background: var(--pb-elevated-1);
  padding: clamp(1.75rem, 3.5vw, 3.25rem);
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.7fr);
  gap: clamp(1.5rem, 3.5vw, 3.5rem);
  align-items: center;
  overflow: hidden;
}
@media (max-width: 767.98px) { .testi-card { grid-template-columns: 1fr; text-align: center; } }
.testi-illus { display: flex; justify-content: center; align-items: center; }
.testi-illus svg,
.testi-illus img { width: 100%; max-width: 320px; height: auto; display: block; }
/* The review illustration ships on a solid white background. In light mode
   the card is near-white, so multiply blends that white seamlessly into the
   card (illustration colours are preserved). Dark mode keeps it as-is, where
   a white illustration reads as an intentional framed tile. */
:root[data-theme="light"] .testi-illus img { mix-blend-mode: multiply; }
.testi-body { position: relative; z-index: 1; min-width: 0; }
.testi-company { font-family: 'Poppins', system-ui, sans-serif; font-weight: 700; font-size: clamp(1.3rem, 2vw, 1.6rem); letter-spacing: -0.01em; color: var(--pb-blue); }
.testi-name { font-family: 'Poppins', system-ui, sans-serif; font-weight: 600; font-size: 1.05rem; margin-top: 6px; color: var(--pb-fg); }
.testi-role { font-size: 0.9rem; margin-top: 2px; color: var(--pb-muted); }
.testi-stars { display: inline-flex; gap: 3px; margin-top: 14px; }
.testi-quote { margin-top: 16px; font-size: clamp(1rem, 1.3vw, 1.15rem); line-height: 1.7; color: var(--pb-fg); }
.testi-more { display: inline-flex; align-items: center; gap: 8px; margin-top: 20px; color: var(--pb-blue); font-weight: 600; text-decoration: none; transition: gap .2s ease; }
.testi-more:hover { gap: 12px; }
/* big decorative quote marks */
.testi-qmark { position: absolute; font-family: Georgia, 'Times New Roman', serif; color: var(--pb-blue); opacity: 0.10; line-height: 0.8; pointer-events: none; user-select: none; }
.testi-qmark-open { top: 18px; left: 26px; font-size: clamp(90px, 12vw, 150px); }
.testi-qmark-close { bottom: -34px; right: 30px; font-size: clamp(90px, 12vw, 150px); }
@media (max-width: 767.98px) { .testi-qmark { display: none; } }
/* arrows */
.testi-arrow {
  position: absolute; top: 50%; transform: translateY(-50%); z-index: 3;
  width: 44px; height: 44px; border-radius: 9999px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid var(--pb-hairline); background: var(--pb-surface); color: var(--pb-fg);
  cursor: pointer; transition: border-color .2s ease, color .2s ease;
}
.testi-arrow:hover { border-color: var(--pb-blue); color: var(--pb-blue); }
.testi-arrow:focus-visible { outline: 2px solid var(--pb-blue); outline-offset: 3px; }
.testi-arrow svg { width: 18px; height: 18px; }
.testi-prev { left: -6px; }
.testi-next { right: -6px; }
@media (min-width: 1280px) { .testi-prev { left: -26px; } .testi-next { right: -26px; } }
/* dots */
.testi-dots { display: flex; justify-content: center; gap: 10px; margin-top: 28px; }
.testi-dot { width: 10px; height: 10px; padding: 0; border: 0; border-radius: 9999px; background: var(--pb-hairline); cursor: pointer; transition: background .2s ease, transform .2s ease; }
.testi-dot.is-active { background: var(--pb-blue); transform: scale(1.2); }
.testi-dot:focus-visible { outline: 2px solid var(--pb-blue); outline-offset: 2px; }

/* ============================================================
 * PRODUCTS CAROUSEL  (#products) - click-driven + compact
 * Converts the tall pinned scroll-carousel into a normal-height
 * horizontal carousel navigated by prev/next buttons
 * (ProductsCarousel.jsx). Un-pins the section (was 720vh) so the
 * page height drops, and lets the track scroll horizontally with
 * snap; the scroll-pin JS is disabled at source.
 * ============================================================ */
@media (min-width: 1024px) {
  #products.pin-h-wrap { height: auto; }
  #products .pin-h-pin {
    position: static;
    height: auto;
    overflow: visible;
    padding: 0;
    justify-content: flex-start;
  }
  #products .pin-h-pin .prod-track {
    transform: none !important;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    flex-wrap: nowrap;
    gap: 1.5rem;
    padding: 4px 2px;
    margin: 0;
    cursor: default;
    scrollbar-width: none;
  }
  #products .pin-h-pin .prod-track > * { scroll-snap-align: start; }
  #products .pin-h-pin .prod-track::-webkit-scrollbar { height: 0; }

  /* Compress the product cards - trim padding, inter-element gaps, the
     oversized heading and the mt-auto foot gap so the cards aren't so
     tall / white-space heavy. */
  #products .pin-h-pin .prod-card {
    min-height: 0;
    padding: 1.5rem 1.75rem 1.375rem;
    gap: 0.7rem;
  }
  #products .pin-h-pin .prod-card h3 {
    font-size: clamp(1.6rem, 2vw, 2.1rem) !important;
    margin-top: 0.35rem;
  }
  #products .pin-h-pin .prod-card > .t-lead {
    font-size: 0.98rem;
    line-height: 1.45;
  }
  #products .pin-h-pin .prod-card ul { margin-top: 0.5rem; }
  #products .pin-h-pin .prod-card .card-foot { padding-top: 0.85rem; }
}
@media (min-width: 1024px) and (prefers-reduced-motion: reduce) {
  #products .pin-h-pin .prod-track { scroll-behavior: auto; }
}

/* Prev / next buttons in the products progress row */
.prod-nav-btn {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--pb-hairline);
  background: var(--pb-surface);
  color: var(--pb-fg);
  cursor: pointer;
  transition: border-color .2s ease, color .2s ease, opacity .2s ease;
}
.prod-nav-btn:hover { border-color: var(--pb-cobalt-2); color: var(--pb-cobalt-2); }
.prod-nav-btn:focus-visible { outline: 2px solid var(--pb-cobalt-2); outline-offset: 3px; }
.prod-nav-btn[disabled] { opacity: 0.35; cursor: default; }
.prod-nav-btn svg { width: 18px; height: 18px; }

/* ============================================================
 * Whitespace + card-sizing tightening (2026-07)
 * Loaded last, so these win on source order. Three levers:
 *   1) Tighter, consistent vertical rhythm between all sections
 *      (was clamp 2.5–4.5rem each side ≈ up to 144px between sections).
 *   2) Sub-page heroes size to their content - the source forced the
 *      home hero's ~full-viewport min-height onto every hero, leaving a
 *      large empty band under short topic/company/legal hero copy.
 *   3) Drop artificial card min-heights so cards fit their content.
 * ============================================================ */
main > section[class*="py-"] {
  padding-top: clamp(1.5rem, 2.2vw, 2.5rem);
  padding-bottom: clamp(1.5rem, 2.2vw, 2.5rem);
}
main > section[id="contact"] { /* keep the real contact band a touch airier */
  padding-top: clamp(2.5rem, 4vw, 4rem);
  padding-bottom: clamp(2.5rem, 4vw, 4rem);
}
/* Sub-page (non-home) heroes: free them from the full-viewport min-height
 * so they end just below their CTAs instead of leaving an empty band. */
main.page:not(#page-home) > section.hero-shell { min-height: auto; }
@media (min-width: 1024px) {
  main.page:not(#page-home) > section.hero-shell { min-height: auto; }
}
/* Cards fit their content rather than a fixed tall min-height. Rows still
 * align via the grid's default stretch, but to the tallest *content*. */
.service-card,
.service-card.service-card { min-height: 0; }
@media (min-width: 640px)  { .service-card { min-height: 0; } }
@media (min-width: 1024px) { .service-card { min-height: 0; } }
/* Framework / small-content cards also size to content. */
.framework-card { min-height: 0; }
/* Cards stretch to EQUAL height within their grid row (default grid
 * behaviour) so every row is aligned. Combined with min-height:0 above,
 * the row height equals the tallest actual content - aligned, not
 * artificially tall. Card footers (.card-foot.mt-auto) pin to the bottom
 * so "Read →" links line up across a row. */
.card,
.service-card,
.wcu-card,
.framework-card { align-self: stretch; }
/* Trim the hairline divider breathing room a touch to match. */
main > div.hairline,
.hairline.shell {
  margin-top: clamp(0.5rem, 0.9vw, 1rem);
  margin-bottom: clamp(0.5rem, 0.9vw, 1rem);
}

/* Stat strips: the value type (Open Sans) was set as large as 2.25–2.5rem
 * (~36px), which is oversized for the text-style values these cells often hold
 * ("Jira + Slack", "1-2 wk sprints", "every PR"). Bring the strip sizes down.
 * Attribute selectors avoid escaping the arbitrary Tailwind class brackets;
 * the big hero numbers (text-[3rem]+) are intentionally left large. */
.stat-num[class*="text-[2rem]"]    { font-size: clamp(1.2rem, 1.9vw, 1.5rem); }
.stat-num[class*="text-[2.25rem]"] { font-size: clamp(1.25rem, 2vw, 1.6rem); }
.stat-num[class*="text-[2.5rem]"]  { font-size: clamp(1.35rem, 2.2vw, 1.75rem); line-height: 1.1; }
/* Tighter stat-cell padding so the strip isn't a tall band of empty space. */
main section div:has(> .stat-num) {
  padding-top: clamp(1rem, 1.5vw, 1.35rem);
  padding-bottom: clamp(1rem, 1.5vw, 1.35rem);
}

/* ------------------------------------------------------------
 * Grid orphans: a 3-column card grid holding exactly 4 cards renders as
 * "3 + 1 lone card" with two empty columns beside it (e.g. the 4th
 * capability block on a topic page). Rebalance those to a clean 2×2.
 * :has() scopes this to grids whose 4th card is also the last child, so
 * 3-card and 6-card grids (which tile cleanly) are untouched.
 * ------------------------------------------------------------ */
@media (min-width: 1024px) {
  [class*="grid-cols-3"]:has(> .card:nth-child(4):last-child),
  [class*="grid-cols-3"]:has(> .service-card:nth-child(4):last-child) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Slightly tighter internal card padding so cards hug their content
 * (doubled class beats the earlier .card / article.card padding rules). */
.card.card { padding: clamp(1rem, 1.3vw, 1.4rem); }
.service-card.service-card { padding: clamp(1rem, 1.4vw, 1.5rem); }
.wcu-card.wcu-card { padding: clamp(1rem, 1.4vw, 1.5rem); }

/* Blog article body: a single CENTRED reading column. The prose sits in a
 * 12-col grid's col-span-8; span the full grid width and centre it (balanced
 * margins) at a comfortable measure. This pattern (col-span-8 + max-w-prose
 * together) is blog-only; font family + colours inherit from the design
 * system. Width is shared with the PHIPA hero column below so hero + body
 * read as one centred column. */
[class*="grid-cols-12"] > [class*="col-span-8"][class*="max-w-prose"] {
  grid-column: 1 / -1;
  max-width: 54rem;
  margin-inline: auto;
}

/* Surface an article's visually-hidden <h1> as a prominent visible title.
 * One blog article ships its H1 sr-only (inline clip styles), so it leads
 * with only an eyebrow + hero image and no visible headline. Reveal it and
 * give it the design system's .t-h1 scale so every article opens with a
 * visible title. Scoped to the CLASSLESS hero <h1>: every other <h1> on the
 * site carries a .t-h1 / .t-display class, so :not([class]) uniquely targets
 * this sr-only heading and leaves every other page untouched. (Matching the
 * inline clip string fails because RevealFixer appends --d, which makes the
 * browser re-serialise the style attribute.) !important is needed only on the
 * properties fighting the inline sr-only styles; font family + colour inherit
 * from the design system (unchanged per request). */
main.page section.shell > h1:not([class]) {
  position: static !important;
  width: auto !important;
  height: auto !important;
  margin: clamp(0.75rem, 1.4vw, 1.25rem) 0 0 !important;
  overflow: visible !important;
  clip: auto !important;
  white-space: normal !important;
  /* Sized to fill the column in ~2 lines (the title is long); smaller than
   * the shared .t-h1 on purpose so it does not wrap to 3+ narrow lines. */
  font-size: clamp(1.9rem, 3.2vw, 2.6rem);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-weight: 700;
  max-width: none;
  text-wrap: balance;
}

/* PHIPA-style article (single-column image hero - uniquely a bare
 * section.shell holding the classless <h1>): make the whole hero a centred
 * column whose content width matches the body's 54rem, so eyebrow, date, H1,
 * image, lead and body all line up as one centred column. :has() scopes this
 * to that one hero; +2*shell-x preserves the mobile side padding. */
main.page section.shell:has(> h1:not([class])) {
  max-width: calc(54rem + 2 * var(--shell-x));
}
/* Reduce the hero banner (intrinsic 1672x941) to a modest size, LEFT-aligned
 * so its left edge lines up with the eyebrow / date / H1 / lead / body (all of
 * which sit at the column's left edge) rather than floating inset-centred. */
main.page section.shell:has(> h1:not([class])) > img {
  max-width: 40rem;
  height: auto;
  margin-inline: 0;
}

/* Blog articles: published date line, sat just under the eyebrow. Uses the
 * eyebrow's mono face + muted colour (design-system tokens, no new font). */
[data-pb-blog-article] .pb-post-date {
  display: block;
  margin-top: clamp(0.5rem, 0.9vw, 0.75rem);
  font-family: 'Open Sans', monospace;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  color: var(--pb-muted);
}
/* Blog articles: drop the decorative leading dash on eyebrows (and the gap it
 * reserved). Scoped to blog articles so the rest of the site is untouched. */
[data-pb-blog-article] .t-eyebrow::before { display: none; }
[data-pb-blog-article] .t-eyebrow { gap: 0; }

/* ============================================================
 * BLOG ARTICLE 2-COLUMN LAYOUT (injected by pentabay-blog-enhance.js)
 * On wide screens: TOC (left, sticky) · content (right). Related articles run
 * FULL WIDTH at the very bottom, after the article. Below 1280px it stays
 * single column: TOC hidden, related still last.
 *
 * ── Why the related rail moved out of the sidebar (2026-08) ────────────────
 * It used to be a third sticky column at 19rem, which cost the article 19rem +
 * a gap of horizontal room for the whole scroll. Two things were wrong with
 * that. It pinned two cards beside the reader for the entire article when their
 * only job is "what to read AFTER this" - a bottom-of-page job by definition -
 * and it capped the content column at 46rem while the page was wide enough to
 * give it more. Now the content column takes the freed width and the cards sit
 * where the decision to read another article actually happens.
 *
 * The centre column goes 46rem -> 56rem. That does NOT lengthen the prose
 * lines: the article body carries .max-w-prose (65ch) inside, so paragraphs
 * stay at the same measure. What gets the extra width is everything that was
 * cramped by the 46rem cap - the FAQ frame, the checklist rows and the closing
 * CTA display type, which is what the "lower sections" block further down this
 * file exists to compensate for.
 * ============================================================ */
@media (min-width: 1280px) {
  /* The GRID IS .pb-blog-cols, not <main>. That wrapper is what bounds the
     sticky TOC - see the long note in addLayout() in
     public/pentabay-blog-enhance.js for the overlap this fixes and the
     measurement behind it. <main> itself stays normal flow, so the breadcrumb
     and the related block simply follow one another down the page. */
  main.page.active[data-pb-blog-article] > .pb-blog-cols {
    display: grid;
    grid-template-columns: minmax(0, 12rem) minmax(0, 56rem);
    justify-content: center;
    column-gap: clamp(1.5rem, 2vw, 2.5rem);
    align-items: start;
  }
  /* Explicit columns because DOM order is content-then-TOC: addLayout() appends
     the TOC second so buildTOC() can read the assembled content for its
     headings. Without these the TOC would auto-place into column 2. */
  main.page.active[data-pb-blog-article] > .pb-blog-cols > .pb-blog-toc {
    grid-column: 1;
    grid-row: 1;
    position: sticky;
    top: 96px;
    align-self: start;
    /* Long articles have more TOC entries than fit a short viewport. Cap to the
       space below the sticky header and let it scroll internally rather than
       clipping the last few sections out of reach. */
    max-height: calc(100vh - 128px);
    overflow-y: auto;
  }
  main.page.active[data-pb-blog-article] > .pb-blog-cols > .pb-blog-content {
    grid-column: 2;
    grid-row: 1;
    min-width: 0;
  }
  /* Full width, and centred on the same axis as the two columns above it:
     12rem + 56rem + the gap.
     The top margin is not decoration. Without it the block butts straight up
     against the article's closing CTA, and because the TOC only stops sticking
     at the article's last pixel, the "Related articles" label lands within ~20px
     of the final TOC entry - two unrelated pieces of furniture reading as one
     column. 4rem clears the TOC tail and marks this as a separate, after-the-
     article block. */
  main.page.active[data-pb-blog-article] > .pb-blog-related {
    max-width: calc(68rem + clamp(1.5rem, 2vw, 2.5rem));
    margin-inline: auto;
    margin-top: 4rem;
    margin-bottom: 2rem;
  }
}
/* Anchor jumps from the TOC clear the sticky header. */
main.page[data-pb-blog-article] h2[id] { scroll-margin-top: 96px; }

/* On this page (left rail) */
.pb-blog-toc { min-width: 0; }
.pb-toc-title {
  font-family: 'Open Sans', monospace;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--pb-muted);
}
.pb-blog-toc ul {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.pb-blog-toc a {
  display: block;
  padding: 6px 0 6px 14px;
  border-left: 2px solid var(--pb-hairline);
  color: var(--pb-muted);
  text-decoration: none;
  font-size: 13px;
  line-height: 1.35;
  transition: color .2s ease, border-color .2s ease;
}
.pb-blog-toc a:hover { color: var(--pb-fg); border-color: var(--pb-blue); }

/* CURRENT SECTION. Marked [aria-current="location"] by the scroll-spy in
   public/pentabay-blog-enhance.js - aria-current rather than a bare class so the
   state is announced, not just drawn. \`location\` is the correct token here:
   \`page\` would claim this link navigates to the page you are already on, which
   is what the nav pill and the breadcrumb use it for.
   The left border is the indicator because it is already there in the resting
   state - only its colour and the text weight change, so nothing reflows as the
   highlight moves down the list while you scroll. */
.pb-blog-toc a[aria-current="location"] {
  color: var(--pb-fg);
  border-color: var(--pb-blue);
  font-weight: 600;
}

/* Related articles - full-width block at the END of the article (see the layout
   note above for why it is no longer a sidebar). */
.pb-blog-related { min-width: 0; }
.pb-related-title {
  font-family: 'Open Sans', monospace;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--pb-muted);
  margin-bottom: 14px;
}
/* Side by side now that the block is full width. auto-fit + minmax rather than
   a fixed 2 columns: buildRelated() always picks exactly 2, but the same rule
   then survives a change to 3 without needing a second breakpoint, and it
   collapses to 1 column on its own when the space is not there. */
.pb-blog-related {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 20rem), 1fr));
  gap: 16px;
}
.pb-blog-related .pb-related-title {
  grid-column: 1 / -1;
  margin-bottom: 0;
}
/* The stacked-rail margins are wrong for a grid - \`gap\` owns the spacing now. */
.pb-blog-related .blog-card { margin-bottom: 0; }
/* The compact 30px pill was a response to the 19rem rail. At full width the
   card is the same object as a /blog index card, so it takes the same button. */

/* ============================================================
 * Blog article - lower sections (FAQ · checklist · "talk to us" CTA)
 * breathing room. These helpers were authored full-width: a 4/8
 * lg:grid-cols-12 split and 5rem display type. Inside the 46rem centre
 * column of the 3-column layout that split turns cramped and the CTA type
 * wraps to four lines, which reads as congested. At the 3-column
 * breakpoint we stack each split into a single column, open the vertical
 * rhythm, and right-size the CTA. Scoped to [data-pb-blog-article] so the
 * same helpers stay full-width on non-blog (location / service) pages.
 * ============================================================ */
@media (min-width: 1280px) {
  /* FAQ + checklist: collapse the authored 4/8 split into one stacked
     column (heading block on top, content below) - far more legible in a
     narrow column, with a comfortable gap between the two rows. */
  main.page.active[data-pb-blog-article] .pb-blog-content section > .grid.lg\\:grid-cols-12 {
    grid-template-columns: minmax(0, 1fr);
    row-gap: clamp(1.5rem, 2vw, 2.25rem);
  }
  main.page.active[data-pb-blog-article] .pb-blog-content section > .grid.lg\\:grid-cols-12 > .lg\\:col-span-4,
  main.page.active[data-pb-blog-article] .pb-blog-content section > .grid.lg\\:grid-cols-12 > .lg\\:col-span-8 {
    grid-column: auto;
  }
  /* Trim the tall py-24/28 the lower sections carry - stacked in the narrow
     column those add up to huge empty bands between FAQ · checklist · CTA.
     :has(> .grid > .col-span-4) is the FAQ/checklist heading block; the
     article prose (col-span-8 only) is left alone. */
  main.page.active[data-pb-blog-article] .pb-blog-content section:has(> .grid.lg\\:grid-cols-12 > .lg\\:col-span-4),
  main.page.active[data-pb-blog-article] .pb-blog-content section:has(.t-display) {
    padding-block: clamp(2.25rem, 3.5vw, 3.25rem);
  }
  /* Any in-content related-card grid: two-up, not 3/4-up, in the narrow column. */
  main.page.active[data-pb-blog-article] .pb-blog-content .grid.lg\\:grid-cols-4,
  main.page.active[data-pb-blog-article] .pb-blog-content .grid.lg\\:grid-cols-3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  /* CTA band: right-size the display heading (the tall py is already trimmed
     by the rule above). :has(.t-display) uniquely targets the CTA. */
  main.page.active[data-pb-blog-article] .pb-blog-content section:has(.t-display) .t-display {
    font-size: clamp(1.9rem, 2.2vw + 1rem, 2.9rem);
  }
}

/* Roomier FAQ rows and checklist items throughout the article column, at
   every width - the dense default rows are the other half of the "congested"
   read. */
[data-pb-blog-article] .pb-blog-content .faq-item {
  padding: clamp(1.25rem, 1.8vw, 1.9rem);
}
[data-pb-blog-article] .pb-blog-content ol.frame > li {
  padding-top: 0.95rem;
  padding-bottom: 0.95rem;
}

/* ------------------------------------------------------------------
 * Checklist - real, working checkboxes (upgraded at runtime by
 * pentabay-blog-enhance.js from decorative spans). A two-column grid
 * (box | text) keeps the box aligned to the first line of the item,
 * even when the text wraps. The native input is visually hidden; the
 * .pb-check-box is the drawn control.
 * ------------------------------------------------------------------ */
/* Higher specificity than the generic ol.frame > li padding rule above so
   the label (not the li) owns the row padding and stays fully clickable. */
[data-pb-blog-article] .pb-blog-content ol.frame > li.pb-check-row { padding: 0; }
.pb-check-row { border-bottom: 1px solid var(--pb-hairline-40); }
.pb-check-row:last-child { border-bottom: 0; }
.pb-check-label {
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: start;
  column-gap: 12px;
  padding: 0.85rem 0;
  cursor: pointer;
}
.pb-check {
  position: absolute;
  width: 1px; height: 1px;
  opacity: 0;
  margin: 0;
  pointer-events: none;
}
.pb-check-box {
  grid-column: 1;
  width: 18px; height: 18px;
  margin-top: 0.22em;            /* centre the box on the first text line */
  border: 1.5px solid var(--pb-blue);
  border-radius: 5px;
  display: grid;
  place-items: center;
  transition: background .15s ease, border-color .15s ease;
}
.pb-check:checked + .pb-check-box { background: var(--pb-blue); border-color: var(--pb-blue); }
.pb-check:checked + .pb-check-box::after {
  content: "";
  width: 5px; height: 9px;
  margin-top: -2px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.pb-check:focus-visible + .pb-check-box { outline: 2px solid var(--pb-blue); outline-offset: 2px; }
.pb-check-text { grid-column: 2; color: var(--pb-fg); line-height: 1.5; }
.pb-check:checked ~ .pb-check-text { color: var(--pb-muted); }

/* Print button disabled until something is ticked. */
[data-pb-blog-article] .btn:disabled,
[data-pb-blog-article] .btn[disabled] {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

/* Print: the checklist button adds html.pb-printing-checklist for the
 * print, isolating #checklist to the page and dropping unchecked rows,
 * the intro copy and the button - so the PDF is just the ticked items. */
@media print {
  html.pb-printing-checklist body * { visibility: hidden !important; }
  html.pb-printing-checklist #checklist,
  html.pb-printing-checklist #checklist * { visibility: visible !important; }
  html.pb-printing-checklist #checklist {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    margin: 0;
    padding: 24px !important;
  }
  html.pb-printing-checklist #checklist > .grid { display: block !important; }
  html.pb-printing-checklist #checklist p.t-body,
  html.pb-printing-checklist #checklist button { display: none !important; }
  html.pb-printing-checklist #checklist .pb-check-row:not(:has(.pb-check:checked)) { display: none !important; }
  html.pb-printing-checklist #checklist .pb-check-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
/* Article bullet lists: the maple-red "●" markers carry an mt-2 that drops
   them below the first line. In the article body use a black dot sized to a
   neat marker, and match the t-body 1.65 line-height (0.6em glyph × ~2.75
   ≈ 1.65em box) so the dot sits centred on the first line of its item. */
[data-pb-blog-article] .pb-blog-content li > .maple {
  color: var(--pb-fg);
  margin-top: 0;
  align-self: flex-start;
  font-size: 0.6em;
  line-height: 2.75;
}

/* Below the 3-column breakpoint: hide the TOC, let related sit full-width
   under the article (with the usual side gutter). */
@media (max-width: 1279.98px) {
  .pb-blog-toc { display: none; }
  .pb-blog-related {
    padding-inline: var(--shell-x);
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
}

/* ============================================================
 * BLOG INDEX (/blog) - filterable card grid (reference layout).
 * Category filter is CSS-only: visually-hidden radio inputs drive
 * :checked ~ sibling rules for the active tab + card visibility,
 * so it works inside the innerHTML-mounted page with no JS.
 * ============================================================ */
.blog-index .pbf-radio {
  position: absolute;
  width: 1px; height: 1px;
  opacity: 0;
  pointer-events: none;
}
.blog-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 2rem;
}
.blog-tab {
  display: inline-flex;
  align-items: center;
  padding: 9px 18px;
  border-radius: 9999px;
  border: 1px solid var(--pb-hairline);
  background: var(--pb-surface);
  color: var(--pb-muted);
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: color .2s ease, border-color .2s ease, background .2s ease;
}
.blog-tab:hover { color: var(--pb-fg); border-color: var(--pb-blue); }
/* Active tab - one rule per category radio. */
#pbf-all:checked ~ .blog-tabs label[for="pbf-all"],
#pbf-healthcare:checked ~ .blog-tabs label[for="pbf-healthcare"],
#pbf-compliance:checked ~ .blog-tabs label[for="pbf-compliance"],
#pbf-cost:checked ~ .blog-tabs label[for="pbf-cost"],
#pbf-comparisons:checked ~ .blog-tabs label[for="pbf-comparisons"],
#pbf-checklists:checked ~ .blog-tabs label[for="pbf-checklists"] {
  background: var(--pb-blue);
  color: #fff;
  border-color: transparent;
}
/* Keyboard focus ring on the label whose radio is focused. */
.pbf-radio:focus-visible ~ .blog-tabs label[for="pbf-all"] { outline: 2px solid var(--pb-blue); outline-offset: 2px; }
.blog-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  margin-top: 2.5rem;
}
@media (min-width: 640px) {
  .blog-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 1024px) {
  .blog-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
/* Category filtering - hide cards not in the selected category.
   "All" (#pbf-all) has no rule, so every card stays visible. */
#pbf-healthcare:checked  ~ .blog-grid .blog-card:not([data-cat="healthcare"]),
#pbf-compliance:checked   ~ .blog-grid .blog-card:not([data-cat="compliance"]),
#pbf-cost:checked         ~ .blog-grid .blog-card:not([data-cat="cost"]),
#pbf-comparisons:checked  ~ .blog-grid .blog-card:not([data-cat="comparisons"]),
#pbf-checklists:checked   ~ .blog-grid .blog-card:not([data-cat="checklists"]) {
  display: none;
}
.blog-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid var(--pb-hairline);
  border-radius: 16px;
  overflow: hidden;
  background: var(--pb-surface);
}
.blog-card-media {
  display: block;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-bottom: 1px solid var(--pb-hairline-60);
}
.blog-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Anchor the crop to the top: these covers carry the pentabay logo along
     their top edge and are taller than the card box, so a centred cover crop
     trims the logo. Cropping from the bottom keeps the logo + title intact. */
  object-position: top center;
  display: block;
}
/* Branded fallback panel for articles without a hero image. Always a
   brand-blue gradient with white label text, so it reads in both themes. */
.blog-card-media--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, var(--pb-blue), color-mix(in oklab, var(--pb-blue) 55%, #0a1f44));
}
.blog-card-media--fallback span {
  font-family: 'Open Sans', monospace;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #fff;
  opacity: 0.95;
  text-align: center;
}
.blog-card-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  padding: 16px 18px 18px;
}
/* Compact, capped title so every card body stays short and even. */
.blog-card-title {
  margin-top: 8px;
  font-size: clamp(1.05rem, 1.1vw + 0.7rem, 1.25rem);
  line-height: 1.25;
}
.blog-card-excerpt {
  margin-top: 8px;
  color: var(--pb-muted);
  font-size: 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
/* margin-top:auto pins the meta + Read-more to the bottom of every card,
   so - with the grid stretching cards to equal height - the buttons line
   up across the whole row regardless of title/excerpt length. */
.blog-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 12px;
  margin-top: auto;
  padding-top: 16px;
  font-family: 'Open Sans', monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--pb-muted);
}
.blog-card-cta {
  margin-top: 14px;
  align-self: flex-start;
}
/* Slightly shorter cover so cards aren't overly tall. */
.blog-card-media { aspect-ratio: 16 / 9; }
@media (min-width: 1024px) {
  .blog-card-media { aspect-ratio: 40 / 21; }
}

/* ------------------------------------------------------------
 * Blog hero (#page-blog): drop the 100svh min-height that left a
 * tall near-empty band. The hero is a 2-column grid - copy on the
 * left, a featured "latest post" card on the right - so the right
 * side is filled rather than empty.
 * ------------------------------------------------------------ */
#page-blog .hero-shell {
  min-height: 0;
  display: block;
}
.blog-hero-feature {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--pb-hairline);
  border-radius: 18px;
  overflow: hidden;
  background: var(--pb-surface);
  text-decoration: none;
  transition: border-color .2s ease, transform .2s ease;
}
.blog-hero-feature:hover { border-color: var(--pb-blue); transform: translateY(-2px); }
/* Show the full featured cover (it carries baked-in title art) - the
   image is ~1.9:1, not 16:9, so cropping to a fixed box cut the logo and
   the cost-breakdown panel. Let it size to its natural aspect ratio. */
.blog-hero-feature-media {
  display: block;
  overflow: hidden;
  border-bottom: 1px solid var(--pb-hairline-60);
}
.blog-hero-feature-media img { width: 100%; height: auto; display: block; }
.blog-hero-feature-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 20px 20px;
}
.blog-hero-feature-tag {
  font-family: 'Open Sans', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--pb-muted);
}
.blog-hero-feature-title {
  font-size: clamp(1.1rem, 1vw + 0.8rem, 1.35rem);
  font-weight: 700;
  line-height: 1.25;
  color: var(--pb-fg);
}
.blog-hero-feature-cta {
  font-family: 'Open Sans', monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: var(--pb-blue);
  margin-top: 4px;
}

/* ------------------------------------------------------------
 * Product cards ("Six products" carousel): make the WHOLE card
 * clickable, not just the small "Read … spec" link. Uses the
 * stretched-link pattern - the existing link inside each card gets
 * an ::after that covers the card - so there are no nested anchors
 * and it stays keyboard-accessible. The wordmark image sits below
 * it and isn't interactive, so nothing else is blocked.
 * ------------------------------------------------------------ */
.prod-card { position: relative; }
.prod-card .card-foot .btn-link::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
}
.prod-card .card-foot .btn-link { position: static; }

/* ============================================================
 * VISIBLE BREADCRUMBS - injected by src/Breadcrumbs.jsx as the
 * first child of the active .page (never on the home route).
 * Sits directly below the sticky header, above the hero.
 * ============================================================ */
.pb-breadcrumbs {
  padding-top: clamp(12px, 2vw, 20px);
  padding-bottom: 2px;
}
.pb-breadcrumbs .pb-bc {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  list-style: none;
  margin: 0;
  padding: 0;
  font-family: 'Open Sans', monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.pb-breadcrumbs .pb-bc li { display: inline-flex; align-items: center; gap: 6px 8px; min-width: 0; }
.pb-breadcrumbs a { color: var(--pb-muted); text-decoration: none; transition: color .2s ease; }
.pb-breadcrumbs a:hover { color: var(--pb-fg); }
.pb-breadcrumbs .pb-bc-cur { color: var(--pb-fg); }
.pb-breadcrumbs .pb-bc-sep { color: var(--pb-muted); opacity: 0.5; }

/* ============================================================
 * ANIMATED GRADIENT CARDS  (#services .service-card)
 *
 * Builds on the border-trace the source already ships rather than adding a
 * second mechanism: Pentabay.html:700 puts a conic-gradient ring on
 * .card::after, masked to a 1px band, spun by "animation: trace" off the
 * registered @property --a. That ring is hover-only (opacity 0 -> 0.7) and
 * hard-coded blue -> red. Here it runs continuously, faster, and takes each
 * card's own accent pair, so the "line running around the border" reads at
 * rest and intensifies on hover.
 *
 * Colour comes from the product-accent tokens already in the palette
 * (--pb-blue / red / violet / mint / amber / cobalt / aqua), cycled per card.
 * The surface wash stays at 8-12% so body copy keeps its contrast - the
 * saturation lives in the moving ring, the eyebrow and the stat.
 *
 * Firefox before 128 has no @property, so --a never animates there and the
 * ring renders as a static gradient edge. Degrades quietly.
 * ============================================================ */
/* Corner radius is deliberately not set here: pentabay-tweaks.js owns it
   site-wide via --pb-radius (and exposes a runtime setter), so these cards
   follow that token like every other card. The ring below inherits it. */
.service-card {
  --acc: var(--pb-blue);
  --acc2: var(--pb-blue-3, #3B82FF);
  border-color: color-mix(in oklab, var(--acc) 22%, var(--pb-hairline));
  background:
    linear-gradient(155deg,
      color-mix(in oklab, var(--acc) 10%, var(--pb-surface)) 0%,
      var(--pb-surface) 46%,
      color-mix(in oklab, var(--acc2) 8%, var(--pb-surface)) 100%);
}
/* .card:hover is (0,2,0) and would swap the gradient for a flat fill, so the
   hover state is restated here at matching specificity. */
.service-card:hover {
  border-color: color-mix(in oklab, var(--acc) 55%, transparent);
  background:
    linear-gradient(155deg,
      color-mix(in oklab, var(--acc) 16%, var(--pb-surface)) 0%,
      var(--pb-surface) 44%,
      color-mix(in oklab, var(--acc2) 13%, var(--pb-surface)) 100%);
  box-shadow: 0 14px 44px -18px color-mix(in oklab, var(--acc) 45%, transparent);
}

/* The running ring. Same mask trick as the source, but inheriting the card's
   radius (the source hard-codes 24px, and these cards are 20px now). */
.service-card::after {
  border-radius: inherit;
  background: conic-gradient(from var(--a, 0deg),
    transparent 0deg,
    var(--acc) 45deg,
    var(--acc2) 90deg,
    transparent 165deg,
    transparent 360deg);
  animation: trace 4.5s linear infinite;
  opacity: 0.55;
}
.service-card:hover::after { opacity: 1; }
@media (prefers-reduced-motion: reduce) {
  .service-card::after { animation: none; opacity: 0.4; }
}

/* ============================================================
 * FRAMEWORKS BAND BACKGROUND  (#frameworks > .frameworks-art)
 * Same full-bleed technique as the other three bands (#why-us, #trust,
 * #services): #frameworks is a .shell, so left:50% / width:100vw /
 * translateX(-50%) breaks the art out of the content box to the viewport.
 *
 * The art (/assets/frameworks-wave.webp, 1920x1080) is generated, not stock:
 * a sine-wave surface sampled on a 30x200 grid with each sample drawn as a
 * dot, perspective-compressed and faded with depth, then rasterised. Kept as
 * WebP (86 KB) rather than inline SVG because the SVG form is 6,000 <circle>
 * elements / ~330 KB of markup.
 *
 * BAND HEIGHT IS DERIVED FROM THE ART'S RATIO, and that is the whole reason this
 * min-height exists. The art is full-bleed at 100vw while the band's height came
 * only from its content - six chips and two lines - which made it a 2.2:1 strip
 * at 768px and a 6.2:1 strip at 2560px against a 16:9 image. object-fit: cover
 * resolves that by discarding height, and it was discarding most of it: measured,
 * 55% of the frame survived at 1024px, 47% at 1430px, 38% at 1920px. The wave's
 * crest lost its top and its trough lost its bottom, which is what "only half the
 * image shows" was reporting.
 *
 * Two things fix it together, and neither works alone. The asset is cropped to the
 * wave's measured bounds (frameworks-wave-tight.webp, 1920x784 = 2.449:1 - see
 * _frameworks-wave-crop.mjs; what came off was blank navy, not artwork), and the
 * band is then given the height that ratio needs. Without the crop this would
 * need 804px at 1430px and 1080px at 1920px to show the full frame, i.e. a
 * full-viewport band for six chips; without the height the crop only moves the
 * clipping from 47% to 68%.
 *
 * 100% of the wave is visible up to 1725px (49rem x 2.449). The cap is what
 * stops an ultra-wide viewport demanding a 1045px band: past that width the art
 * starts cropping vertically again, 86% at 1920px and 75% at 2560px, which is
 * the same trade every other band here makes and is far off the 38% it was.
 * Below ~1024px the band is content-taller than this floor and cover crops WIDTH
 * instead - that direction is safe, because it trims the wave's sparse tails and
 * keeps the crest, which sits at 47% of the frame.
 *
 * Sized against 100vw, not 100%, because that is what .frameworks-art itself is
 * width: 100vw - #frameworks is a .shell and is narrower than the art it hosts.
 * Flex-centred so the extra height lands evenly above and below the chips rather
 * than all of it below them; the art is position: absolute and so is unaffected.
 * Re-check both numbers together if the asset is ever recropped.
 *
 * 2026-08: 2.449 -> 3.0 and the 49rem cap -> 40rem. Showing 100% of the wave
 * cost more than it bought. The band's content (eyebrow + six chips + the
 * disclaimer) is ~294px tall at 1420, so a 580px floor left ~93px of dead
 * band above the eyebrow and ~143px below the disclaimer, and that lower
 * slack sat directly on top of #products' own padding - it was the single
 * largest void on the homepage, measured 316px content-to-content.
 *
 * At 3.0 the band is 473px at 1420 and the wave shows ~82% of its height,
 * cropping top and bottom. That is INSIDE the range this design already
 * accepts by its own note above (86% at 1920, 75% at 2560) and the crest
 * sits at 47% of the frame, so what the crop takes is tail, not subject.
 * Both numbers still move together: divisor and cap are the same ratio pair
 * as before, just one step tighter.
 * ============================================================ */
#frameworks {
  position: relative;
  isolation: isolate;
  min-height: min(calc(100vw / 3.0), 40rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
}
#frameworks > *:not(.frameworks-art) { position: relative; z-index: 1; }

.frameworks-art {
  position: absolute;
  top: 0; bottom: 0; left: 50%;
  width: 100vw;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  background: #03102B;
  /* EDGE RAMP - the same mask #why-us uses, and for the same reason: this band
   * is an island with page colour above and below, so without it the art box
   * ends on a rectangle. The ::after scrim below cannot do this job, because it
   * is drawn in a fixed Deep Ink and only ever reaches 0.74 alpha - on the LIGHT
   * theme that left a dark-blue block sitting on white, measured as a 241/255
   * luminance step in a single pixel at BOTH edges. Masking the whole box (field
   * + img + scrim) fades it out and reveals whatever the page is behind it, so
   * nothing is hard-coded and it is correct on both themes.
   * --band-ramp is declared on .whyus-art; redeclared here so this rule does not
   * depend on cascade order or on that section existing. */
  --band-ramp: clamp(34px, 5vw, 76px);
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 var(--band-ramp), #000 calc(100% - var(--band-ramp)), transparent 100%);
          mask-image: linear-gradient(180deg, transparent 0, #000 var(--band-ramp), #000 calc(100% - var(--band-ramp)), transparent 100%);
}
.frameworks-art img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  /* Brightness lift, dark theme. The wave is a generated dot field on near-black
     and it was shipping unfiltered, so the only thing setting its level was the
     scrim over it - which is tuned for the COPY, not for the art, and could not
     brighten one without brightening the other. A filter here separates the two:
     the dots come up, the field the copy sits on does not.
     Deliberately not in a [data-theme="dark"] wrapper: the light theme overrides
     this whole declaration (grayscale + invert, see LIGHT THEME: THE FOUR
     FULL-BLEED BANDS GO PALE), so scoping would be redundant. */
  filter: brightness(1.42) contrast(1.06) saturate(1.08);
}

/* ── NO CSS ANIMATION ON THIS IMAGE. Deliberate, and it is a correction. ──
 * A \`animation: fw-wave-drift ... alternate\` plus @keyframes lived here for
 * part of 2026-08 and it was a bug: src/FrameworksWave.jsx ALREADY pans this
 * same <img> with GSAP (xPercent ±1.2 at scale 1.035, IntersectionObserver-
 * gated). Two animations on one element, and the CSS one silently won -
 * per the cascade, animations outrank inline declarations, and GSAP works by
 * writing inline style. So the component's carefully-scoped pan was being
 * overridden, and the measured live transform was the CSS scale(1.16) rather
 * than GSAP's 1.035.
 *
 * The image no longer animates at all: FrameworksWave.jsx replaces it with a
 * generated <canvas> dot-field, and the <img> is now the fallback that shows
 * when the canvas cannot run (no JS, reduced motion, no 2D context). A still
 * fallback is the correct behaviour in all three of those cases.
 *
 * If you are reintroducing motion here, put it in that component, not in this
 * stylesheet, or you will re-create the same silent conflict. */
.frameworks-art canvas {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  display: block;
  pointer-events: none;
}
/* The canvas draws its own dots at its own level, so it must NOT inherit the
   img's brightness filter or the light theme's invert - it computes both from
   the theme itself. Sibling, not child, so nothing cascades; stated anyway
   because the two rules sit next to each other and the next reader will ask. */
.frameworks-art:has(> canvas[data-pb-wave]) > img { visibility: hidden; }
/* Holds cover across the middle where the chips and the disclaimer sit.
   The end stops used to be 0.70 / 0.74 because this scrim was also trying to
   blend the band into the page; the mask above does that now, and leaving them
   that heavy just fogged the ramp - a dark wash fading out over white reads as
   grey haze rather than a clean dissolve. */
/* 2026-08: TWO LAYERS NOW, and the split is what lets this band be brighter
 * without putting its own copy on a bright field.
 *
 * The brief was "increase the brightness of this background". The naive version
 * of that - filter on the img, thin the scrim uniformly - was built and measured
 * first, and it does not work, because the disclaimer line sits directly on the
 * wave's crest, which is the brightest content in the frame. Measured in-browser
 * at 1440, white-on-background under each run of copy:
 *
 *                       eyebrow                    disclaimer
 *   before this pass    p99 7.22  worst 5.96       p99 7.93  worst 6.09
 *   uniform lift        p99 4.03  worst 2.90       (not re-measured)
 *   two layers (below)  p99 7.35  worst 6.09       p99 7.93  worst 6.09
 *
 * >> MEASUREMENT WARNING, and it cost a wrong conclusion once. An earlier pass
 * >> reported the disclaimer at 2.18:1 and recorded it here as a pre-existing AA
 * >> failure. THAT WAS AN INSTRUMENT ERROR, not a defect. The probe suppressed
 * >> the copy with color: transparent before sampling the background, and on
 * >> this band that does NOT stop the glyphs painting - so it was measuring the
 * >> disclaimer's own text against itself and reporting ~1.4-2.2:1 no matter what
 * >> the background did (which is why the number never moved when the scrim did).
 * >>
 * >> Two things are needed to sample this band honestly:
 * >>   1. visibility: hidden with !important, not a transparent colour.
 * >>   2. transitions disabled first. A visibility transition is in effect
 * >>      here, so the computed value stays visible and the text keeps
 * >>      painting for ~0.45s after you set it. Assert
 * >>      getComputedStyle(el).visibility === 'hidden' before trusting a pixel.
 * >> Re-measured correctly, the disclaimer was never failing: 7.93:1 at p99
 * >> before this pass and after it.
 *
 * LAYER 1, the centre hold, is the same device HeroIntro uses on the home
 * globe - raise the art overall, then mask the middle back down, because the
 * copy is centred and the art is not. Heavier than the old flat scrim where the
 * eyebrow, the chips and the disclaimer sit (~0.55 composited vs 0.28 before),
 * and gone by the outer thirds. The ellipse is sized off the widest copy in the
 * band: the chip row runs x 23%..77% at 1440, so 62% of half-width covers it
 * with margin at every viewport, and 58% vertically spans eyebrow to
 * disclaimer.
 *
 * LAYER 2 is the original vertical ramp with its middle opened right up
 * (0.28 -> 0.10). That is where the brightness comes from - the band's wings
 * and the crest either side of the copy. The two end stops stay heavy for the
 * reason the original note gives: they are what the edge-ramp mask dissolves.
 *
 * Net effect, measured over the band's own pixels with the copy hidden and the
 * drift frozen at the same frame in both variants - mean luminance:
 *
 *                    old      new
 *   whole band     0.0131   0.0159   +21%
 *   left wing      0.0107   0.0159   +49%
 *   right wing     0.0108   0.0157   +45%
 *   copy centre    0.0158   0.0166    +5%
 *
 * which is the shape this is after: the parts of the band with no copy on them
 * carry the brightness, the part with copy on it barely moves. Re-measure both
 * that table and the contrast one above if either layer moves. */
.frameworks-art::after {
  content: "";
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 62% 58% at 50% 52%,
      rgba(6, 10, 20, 0.52) 0%,
      rgba(6, 10, 20, 0.44) 46%,
      rgba(6, 10, 20, 0.18) 78%,
      rgba(6, 10, 20, 0) 100%),
    linear-gradient(180deg,
      rgba(6, 10, 20, 0.30) 0%,
      rgba(6, 10, 20, 0.10) 34%,
      rgba(6, 10, 20, 0.10) 66%,
      rgba(6, 10, 20, 0.34) 100%);
}

/* Copy recoloured onto the band. The chips build from --pb-elevated-2 and
   --pb-fg, so one rescope carries all of them; --pb-mist is separate because
   .t-eyebrow colours from it. Theme-independent literals on purpose - this
   band is dark on BOTH themes, like the three it sits alongside. */
[data-route="home"] #frameworks {
  --pb-fg: #FFFFFF;
  --pb-muted: #FFFFFF;
  --pb-mist: #FFFFFF;
  --pb-elevated-2: rgba(8, 16, 31, 0.62);
  --pb-hairline: rgba(255, 255, 255, 0.16);
  color: var(--pb-fg);
}
[data-route="home"] #frameworks .fw-chip { backdrop-filter: blur(8px); }

/* ============================================================
 * SERVICES BAND BACKGROUND  (#services > .services-art)
 * Same full-bleed technique as .whyus-art: #services is a .shell, so an
 * inset:0 child would stop at the content box and show two hard vertical
 * edges. left:50% / width:100vw / translateX(-50%) breaks it out to the
 * viewport. Verified 0 -> viewport width with border-radius 0.
 *
 * The wash is CSS gradients, not an image file - the reference art is a soft
 * blue glow with no detail to preserve, so this matches it, weighs nothing and
 * never upscales. Stops are theme-independent on purpose: this band is dark on
 * BOTH themes, like the #why-us and #trust bands it sits between.
 * ============================================================ */
#services { position: relative; isolation: isolate; }
#services > *:not(.services-art) { position: relative; z-index: 1; }

.services-art {
  position: absolute;
  top: 0; bottom: 0; left: 50%;
  width: 100vw;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  background:
    /* soft light streak, upper right */
    radial-gradient(70% 120% at 88% 6%, rgba(126, 174, 255, 0.20), transparent 58%),
    /* main glow, upper centre-right */
    radial-gradient(95% 85% at 62% 20%, rgba(43, 110, 220, 0.42), transparent 66%),
    /* deeper pool, lower left */
    radial-gradient(85% 95% at 10% 80%, rgba(4, 14, 38, 0.62), transparent 70%),
    linear-gradient(155deg, #07203F 0%, #0A2B5E 48%, #061C3E 100%);
  /* Same edge ramp as .frameworks-art / .whyus-art, same measured reason: the
   * ::after scrim tops out at 0.62 alpha, so on the LIGHT theme this band also
   * ended as a hard rectangle on white - 236/255 at the top edge, 239/255 at
   * the bottom. Not reported, but it is the identical defect two sections up,
   * and fixing only #frameworks would just move the odd-one-out. */
  --band-ramp: clamp(34px, 5vw, 76px);
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 var(--band-ramp), #000 calc(100% - var(--band-ramp)), transparent 100%);
          mask-image: linear-gradient(180deg, transparent 0, #000 var(--band-ramp), #000 calc(100% - var(--band-ramp)), transparent 100%);
}
/* Styled ahead of time so dropping a real <img> in needs no CSS change. */
.services-art img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
}
/* Holds cover where the copy sits. The mask on the art box now does the
   blending into the page, so the end stops came down from 0.55 / 0.62 - left
   that heavy they fogged the ramp instead of dissolving it. */
.services-art::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(180deg,
    rgba(6, 10, 20, 0.34) 0%,
    rgba(6, 10, 20, 0.10) 26%,
    rgba(6, 10, 20, 0.10) 74%,
    rgba(6, 10, 20, 0.38) 100%);
}

/* Copy + cards recoloured onto the band. One rescope does nearly all of it:
   .service-card builds its own background from --pb-surface
   (linear-gradient(155deg, color-mix(--acc 10%, --pb-surface), --pb-surface,
   color-mix(--acc2 8%, --pb-surface))), so pointing --pb-surface at a dark
   value turns all eight cards into dark plates AND keeps each card's accent
   tint intact - no per-card work, no !important. --pb-mist is separate because
   .t-eyebrow colours from it, not from --pb-fg/--pb-muted. */
[data-route="home"] #services {
  --pb-surface: #08101F;
  --pb-fg: #FFFFFF;
  --pb-muted: #FFFFFF;
  --pb-mist: #FFFFFF;
  --pb-hairline: rgba(255, 255, 255, 0.14);
  --pb-hairline-60: rgba(255, 255, 255, 0.10);
  --pb-hairline-40: rgba(255, 255, 255, 0.07);
  color: var(--pb-fg);
}
/* The badge mixes its accent toward black for contrast on a white card. On a
   dark plate that reads as a dark smudge, so it mixes toward the band's light
   foreground instead. */
[data-route="home"] #services .service-card .svc-num {
  color: color-mix(in oklab, var(--acc) 34%, #F7F8FB);
  background: color-mix(in oklab, var(--acc) 26%, transparent);
  border-color: color-mix(in oklab, var(--acc) 45%, transparent);
}
/* Same for the card's footer link - the deeper accents (#0F4FBA) are too dark
   to read against the plate. */
[data-route="home"] #services .service-card .card-foot a.btn-link {
  color: color-mix(in oklab, var(--acc) 42%, #F7F8FB);
}
/* Card titles, lifted for legibility on this band. The band is dark on BOTH
   themes, so the lift is unconditional here (it used to be dark-theme-only, from
   when the band was light) - a raw deep token measures ~3.2:1 on the plate and
   only scrapes AA-large because these are 24px/600. --pb-fg is the rescoped
   light value inside this section, so the mix resolves correctly.
   Hue is BLUE now, not --pb-red: this rule is (1,4,1) and was overriding the
   base .svc-head h3 rule, so switching that one to blue alone left every title
   on the homepage still rendering salmon. Both had to change. */
[data-route="home"] #services .service-card .svc-head h3 {
  color: color-mix(in oklab, var(--pb-blue-3) 62%, var(--pb-fg));
}

/* Per-card accent pairs, cycling every six cards.
   2026-07: the mint (6n+4) and amber (6n+6) steps were dropped on request -
   green and yellow read as off-brand next to the rest of the grid, then to four
   blues plus red and violet, and now to blue only - see the note below. */
/* BLUE ONLY. The rotation is gone entirely - every card takes the base
   --acc / --acc2 declared on .service-card above (--pb-blue / --pb-blue-3).
   It used to be six steps: blue, RED, VIOLET, blue-2, cobalt, blue-3, which put
   a red card and a violet card in every run of six - the S/02 badge, ring, title
   accent and footer link on those two cards all went off-brand.
   Deliberately deleted rather than re-pointed at six near-identical blues: with
   the tokens that close together the rotation only produced adjacent cards that
   looked subtly mismatched instead of deliberately different. One blue for all
   of them is predictable and cannot drift off-brand as cards are added. */

/* Accent the labels. --acc is tuned for the ring, so text steps to a darker
   mix where the raw token would fail contrast on white (mint and amber in
   particular). */
.service-card .svc-num {
  color: color-mix(in oklab, var(--acc) 78%, #000000);
}
.service-card .svc-num {
  background: color-mix(in oklab, var(--acc) 12%, transparent);
  border-color: color-mix(in oklab, var(--acc) 30%, transparent);
}
.service-card .card-foot .btn-link { color: color-mix(in oklab, var(--acc) 80%, #000000); }

/* Dark theme: the wash needs more presence against a dark surface, and the
   label mixes flip from navy-darkened to white-lightened. */
:root[data-theme="dark"] .service-card {
  background:
    linear-gradient(155deg,
      color-mix(in oklab, var(--acc) 22%, var(--pb-elevated-1)) 0%,
      var(--pb-elevated-1) 48%,
      color-mix(in oklab, var(--acc2) 16%, var(--pb-elevated-1)) 100%);
}
:root[data-theme="dark"] .service-card:hover {
  background:
    linear-gradient(155deg,
      color-mix(in oklab, var(--acc) 32%, var(--pb-elevated-1)) 0%,
      var(--pb-elevated-1) 46%,
      color-mix(in oklab, var(--acc2) 24%, var(--pb-elevated-1)) 100%);
}
:root[data-theme="dark"] .service-card .svc-num,
:root[data-theme="dark"] .service-card .card-foot .btn-link {
  color: color-mix(in oklab, var(--acc2) 82%, #FFFFFF);
}

/* ============================================================
 * WHY NORTH AMERICA RESIDENCY PLATES  (#canada .wna-plate)
 *
 * Replaces the four .wna-card tiles (pastel gradient wash + continuously
 * traced conic ring + gradient-filled stat text, four different accent hues).
 * See the markup comment in Pentabay.html for why that went.
 *
 * The plate is built as a spec sheet, not a marketing tile:
 *
 *   ▔▔▔▔▔▔▔▔▔▔▔▔▔▔▔  <- accent rule, top edge, 3px
 *   CANADA RESIDENCY          01
 *   ca-central-1                     <- mono, --pb-fg, the loudest thing here
 *   PHIPA / PIPEDA tenants land …
 *   ─────────────────────────────
 *   PHIPA   PIPEDA                   <- governing frameworks, on the record
 *
 * Accent comes from [data-accent] rather than :nth-child, so the row can be
 * re-ordered or extended without silently recolouring its neighbours. Only two
 * values are used: "red" for the Canadian card and "blue" for the rest. That
 * is the real hierarchy of this section - Canada is the root, the other three
 * are consequences of it - and it keeps the row inside the brand palette.
 *
 * Deliberately NOT reusing .card: .card is a flex column with a 24px radius,
 * transparent fill and a 4px hover lift, and it is shared with the proof-stat
 * tiles in #why-us. These need a flatter, tighter, more technical chrome, and
 * two different kinds of information should not carry identical chrome.
 * ============================================================ */
#canada .wna-plate {
  --wna-acc: var(--pb-blue);
  --wna-acc-2: var(--pb-blue-3, #3B82FF);
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 1.6rem 1.5rem 1.35rem;
  border: 1px solid var(--pb-hairline);
  border-radius: var(--pb-radius, 12px);
  background: var(--pb-surface);
  overflow: hidden;
  transition: border-color .25s ease, box-shadow .25s ease, transform .25s ease;
}
#canada .wna-plate[data-accent="red"] {
  --wna-acc: var(--pb-red);
  --wna-acc-2: var(--pb-red-2, #DA3146);
}
/* Accent rule along the top edge. A pseudo-element rather than border-top so it
   can carry a gradient and sit inside the plate's radius - a coloured border-top
   would square off against the rounded corners on the light theme. */
#canada .wna-plate::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  /* Solid across the full width. An earlier version faded the right-hand third
     to transparent, which made the rule read as a half-filled progress bar
     rather than a deliberate accent edge. */
  background: linear-gradient(90deg, var(--wna-acc) 0%, var(--wna-acc-2) 100%);
}
/* A very faint wash of the accent bleeding down from that rule, so the plate is
   not a plain white box but nowhere near the full-surface gradient it replaced.
   Kept low (5%) and faded over most of the plate's height with a midpoint stop -
   at 34% height it terminated on a visible horizontal edge just under the label
   row, which read as a coloured band rather than a wash. */
#canada .wna-plate::after {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 70%;
  background: linear-gradient(to bottom,
    color-mix(in oklab, var(--wna-acc) 5%, transparent) 0%,
    color-mix(in oklab, var(--wna-acc) 2%, transparent) 40%,
    transparent 100%);
  pointer-events: none;
}
#canada .wna-plate > * { position: relative; z-index: 1; }
#canada .wna-plate:hover {
  border-color: color-mix(in oklab, var(--wna-acc) 45%, var(--pb-hairline));
  box-shadow: 0 12px 32px -20px color-mix(in oklab, var(--wna-acc) 55%, transparent);
  transform: translateY(-2px);
}

/* Head row: label left, index right, on one baseline. */
#canada .wna-plate-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
}
#canada .wna-plate-label {
  min-width: 0;
  color: color-mix(in oklab, var(--wna-acc) 72%, #000000);
}
#canada .wna-plate-idx {
  flex: 0 0 auto;
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.16em;
  font-variant-numeric: tabular-nums;
  /* Was color-mix(--pb-muted 70%, transparent), which put the plate index at
     70% alpha - grey on the light theme, half-white on the dark one. The token
     unmixed is the full-strength endpoint on both. */
  color: var(--pb-muted);
}

/* The region string. Full-contrast --pb-fg, not accent-tinted and not gradient
   filled: "ca-central-1" is the payload of the card and it was previously the
   least legible thing in it. The accent lives in the rule and the label. */
#canada .wna-plate .wna-stat {
  margin-top: 1.35rem;
  /* Open Sans, per the site's two-typeface rule (Poppins headings / Open Sans
     content). Tabular numerals keep the region strings aligned card-to-card,
     which is the only thing the monospace was actually buying here. */
  font-family: 'Open Sans', system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
  font-size: clamp(1.0625rem, 1.15vw, 1.375rem);
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--pb-fg);
  /* Long region strings must not wrap mid-token, but they also must not push the
     grid track wider than its cell - so shrink instead of overflowing. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
#canada .wna-plate-body {
  margin-top: 0.85rem;
  font-size: 14px;
  line-height: 1.62;
  color: var(--pb-muted);
}
/* Footer sits on the plate's bottom edge whatever the body length, so the four
   framework rows line up across the row.
   The separator is border-top on the row itself. An absolutely-positioned
   ::before was tried first and rendered wrong: with no explicit top it resolves
   to its static position, which inside a flex row is the first flex-item slot,
   so the rule drew as a short segment beside the tags instead of above them. */
#canada .wna-plate-foot {
  margin-top: auto;
  padding-top: 1.05rem;
  border-top: 1px solid var(--pb-hairline-60, var(--pb-hairline));
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}
#canada .wna-plate-tag {
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  color: color-mix(in oklab, var(--wna-acc) 78%, #000000);
  background: color-mix(in oklab, var(--wna-acc) 9%, transparent);
  border: 1px solid color-mix(in oklab, var(--wna-acc) 22%, transparent);
  white-space: nowrap;
}

/* Dark theme: surface steps up to the elevated fill and the accent mixes flip
   from navy-darkened to white-lightened, same convention as .service-card. */
:root[data-theme="dark"] #canada .wna-plate {
  background: var(--pb-elevated-1);
}
:root[data-theme="dark"] #canada .wna-plate-label,
:root[data-theme="dark"] #canada .wna-plate-tag {
  color: color-mix(in oklab, var(--wna-acc-2) 82%, #FFFFFF);
}
:root[data-theme="dark"] #canada .wna-plate-tag {
  background: color-mix(in oklab, var(--wna-acc-2) 14%, transparent);
  border-color: color-mix(in oklab, var(--wna-acc-2) 30%, transparent);
}

@media (prefers-reduced-motion: reduce) {
  #canada .wna-plate { transition: none; }
  #canada .wna-plate:hover { transform: none; }
}
@media (max-width: 380px) {
  #canada .wna-plate { padding: 1.3rem 1.2rem 1.1rem; }
}

/* ============================================================
 * FRAMEWORK PANEL  (#compliance .fw-*)
 *
 * One bordered panel holding the section heading, a single framework
 * slide, side arrows, and a tab strip of the five seals along the
 * bottom. Only the active slide is in flow (the others carry [hidden]),
 * so the deck height follows the active card; min-height keeps the
 * arrows from jumping between frameworks whose copy differs in length.
 * ============================================================ */
/* Brand wash instead of the near-white gradient: --pb-blue bleeding in from
   the top-left, --pb-red from the bottom-right - the two logo colours - over
   the neutral panel tint. Both sit at single-digit alpha so the white slide
   card on top keeps full text contrast; the tint is meant to be felt at the
   panel's edges rather than read as a colour block. */
/* margin-top is 0 when the panel is a section's first child - which on the
   homepage it always is (#products contains nothing else). That margin was
   written for a panel following a heading; stacked on the section's own
   padding it just doubled the pad, ~48px + 41px of empty band before the
   panel's border even started. The :first-child guard keeps the original
   value available for any later use that does put copy above it. */
.fw-panel {
  margin-top: clamp(2rem, 3.5vw, 3rem);
  border: 1px solid color-mix(in oklab, var(--pb-blue) 16%, var(--pb-hairline));
  border-radius: 24px;
  background:
    radial-gradient(115% 85% at 0% 0%,
      color-mix(in oklab, var(--pb-blue) 18%, transparent) 0%,
      transparent 64%),
    radial-gradient(105% 80% at 100% 100%,
      color-mix(in oklab, var(--pb-red) 14%, transparent) 0%,
      transparent 62%),
    linear-gradient(180deg, var(--pb-elevated-1), var(--pb-elevated-2));
  padding: clamp(1.5rem, 3vw, 3rem);
}
.fw-panel:first-child { margin-top: 0; }
/* Headline left, supporting copy right. Grid, not flex-wrap: with wrap the
   two max-widths plus the gap needed 1136px and the panel only offers ~1118px
   at a 1600px viewport, so the lead dropped under the headline everywhere
   below 1920. Explicit tracks keep them side by side from 1024 up and stack
   them deliberately below that. */
.fw-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
}
@media (min-width: 1024px) {
  .fw-head {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 0.85fr);
    align-items: end;
    gap: clamp(1.5rem, 3vw, 3rem);
  }
}
.fw-head-left { min-width: 0; }
.fw-head-left h2 { text-wrap: balance; }
.fw-head-lead { min-width: 0; margin: 0; }

/* Stage: arrows flank the deck. Grid rather than absolute positioning so
   the arrows can never overlap the card on a narrow viewport. */
.fw-stage {
  margin-top: clamp(1.5rem, 2.4vw, 2.25rem);
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: clamp(0.75rem, 2vw, 1.75rem);
}
.fw-arrow {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: 9999px;
  border: 1px solid var(--pb-hairline);
  background: var(--pb-surface);
  color: var(--pb-blue);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color .2s ease, background .2s ease, color .2s ease;
}
.fw-arrow svg { width: 18px; height: 18px; }
.fw-arrow:hover { border-color: var(--pb-blue); background: var(--pb-blue); color: #FFFFFF; }
.fw-arrow:focus-visible { outline: 2px solid var(--pb-blue); outline-offset: 3px; }

/* The deck's two pseudo-elements are the stacked card edges peeking above
   the active slide - they sit behind it, inset and stepped up.
   display:grid with every slide in cell 1/1 stacks all five on top of each
   other, so (a) they can cross-fade instead of popping, and (b) the deck's
   height is the TALLEST slide and never changes as you page through. That's
   why inactive slides use visibility rather than [hidden]: display:none
   would drop them out of the stack and the height would jump per slide.
   visibility still removes them from the a11y tree and the tab order. */
.fw-deck {
  position: relative;
  isolation: isolate;
  margin-top: 22px;
  display: grid;
}
.fw-deck::before,
.fw-deck::after {
  content: "";
  position: absolute;
  top: -11px;
  height: 24px;
  border: 1px solid var(--pb-hairline-60);
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  background: var(--pb-surface);
  z-index: -1;
  pointer-events: none;
}
.fw-deck::before { left: 3%; right: 3%; }
.fw-deck::after  { left: 7%; right: 7%; top: -22px; opacity: 0.6; }

/* Every slide occupies the same grid cell; only .is-active is visible. The
   incoming slide fades and rises, so both the 3s auto-advance and a click
   land the same way. */
.fw-slide {
  grid-area: 1 / 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: clamp(0.875rem, 1.4vw, 1.125rem);
  padding: clamp(1.5rem, 2.4vw, 2rem) clamp(1.25rem, 3vw, 2.5rem);
  border: 1px solid var(--pb-hairline);
  border-radius: 16px;
  background: var(--pb-surface);
  box-shadow: 0 1px 2px rgba(10, 31, 68, 0.04), 0 18px 40px -24px rgba(10, 31, 68, 0.18);
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px) scale(0.995);
  transition: opacity .45s ease, transform .45s ease, visibility .45s;
}
.fw-slide.is-active {
  opacity: 1;
  visibility: visible;
  transform: none;
}
@media (prefers-reduced-motion: reduce) {
  .fw-slide { transition: none; transform: none; }
}

/* Mark and name sit on one line to keep the card short. */
.fw-slide-head {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-width: 0;
}
/* Supplied logos (assets/fw-*.png), trimmed from the originals so each one's
   artwork fills its box to the same degree - SOC 2 in particular shipped with
   ~40% empty margin, which made it read much smaller than the others at the
   same box height.
   Sized by HEIGHT with the width left to follow: aspect ratios run 1:1
   (PIPEDA, GDPR) to 2.1:1 (PHIPA, whose wordmark sits beside its shield), so
   a fixed square would squash the wide ones.
   White plate on all five, in both themes. Four of the five ship with opaque
   backgrounds - GDPR is a JPEG, and hipaa/pipeda/soc2 have no alpha channel -
   so on the panel's tinted wash they showed as accidental white boxes of
   differing sizes. A consistent plate turns that into a deliberate logo chip,
   and it also gives PHIPA's black wordmark something to sit on in dark mode. */
.fw-slide-mark {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.fw-slide-mark img {
  box-sizing: content-box;
  height: 46px;
  width: auto;
  max-width: 150px;
  object-fit: contain;
  display: block;
  background: #FFFFFF;
  border: 1px solid var(--pb-hairline-60);
  border-radius: 8px;
  padding: 7px 9px;
}
.fw-slide-title {
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: clamp(1.375rem, 1vw + 1rem, 1.875rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.fw-slide-desc {
  max-width: 62ch;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--pb-muted);
}
.fw-facts {
  margin-top: 2px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px clamp(1.5rem, 4vw, 3.5rem);
  padding-top: clamp(0.875rem, 1.4vw, 1.125rem);
  border-top: 1px solid var(--pb-hairline-60);
  width: min(100%, 40rem);
}
.fw-fact dt {
  font-family: 'Open Sans', monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--pb-muted);
}
.fw-fact dd {
  margin-top: 3px;
  font-size: 0.9375rem;
  line-height: 1.35;
  color: var(--pb-fg);
}
/* The "Where" value is the product's address, so it navigates. Underlined by
   default rather than on hover only - it reads as plain text otherwise, and
   nobody hovers what doesn't look clickable. */
.fw-fact-link {
  color: inherit;
  text-decoration: underline;
  text-decoration-color: color-mix(in oklab, var(--fw-accent, var(--pb-blue)) 45%, transparent);
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  transition: color .2s ease, text-decoration-color .2s ease;
}
.fw-fact-link:hover,
.fw-fact-link:focus-visible {
  color: var(--fw-accent, var(--pb-blue));
  text-decoration-color: currentColor;
}
.fw-fact-link:focus-visible {
  outline: 2px solid var(--fw-accent, var(--pb-blue));
  outline-offset: 3px;
  border-radius: 3px;
}
.fw-slide-link {
  font-family: 'Open Sans', monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--fw-accent, var(--pb-blue));
  text-decoration: none;
}
.fw-slide-link:hover { text-decoration: underline; }

/* --- per-framework colours -------------------------------------------
 * The source ships one monochrome seal per framework via
 * .seal { color: var(--pb-red) }. Each framework now carries its own
 * accent instead, so the marks read as distinct logos.
 *
 * These are palette-accurate to each regime (EU emblem gold-on-blue,
 * Ontario green, Canadian flag red, US navy) - they are NOT the official
 * certification badges. The AICPA SOC seal in particular may only be
 * displayed by licensed CPA firms, and this section's own footnote states
 * Pentabay is not an AICPA-attested auditor, so shipping real attestation
 * artwork here would contradict it. Drop in licensed SVGs later by
 * replacing the mark's <svg> and the --fw-accent value.
 * -------------------------------------------------------------------- */
/* Sampled from each supplied logo, then darkened where needed: the accent
   also colours the "Read the framework" link, so it has to clear AA for small
   text on white - the SOC 2 shield's cyan and the PHIPA shield's mid-blue
   both fail that at face value. */
.fw-slide[data-fw="hipaa"],  .fw-tab[data-fw="hipaa"]  { --fw-accent: #0F6FA8; }
.fw-slide[data-fw="phipa"],  .fw-tab[data-fw="phipa"]  { --fw-accent: #2C6FC4; }
.fw-slide[data-fw="pipeda"], .fw-tab[data-fw="pipeda"] { --fw-accent: #2B2C5A; }
.fw-slide[data-fw="gdpr"],   .fw-tab[data-fw="gdpr"]   { --fw-accent: #2B3990; }
.fw-slide[data-fw="soc2"],   .fw-tab[data-fw="soc2"]   { --fw-accent: #0E7C8C; }

/* The home page now runs the six products through this same widget (the
   frameworks moved to /compliance). Accents follow each product's card
   colour from the old carousel - cobalt, mint, amber, violet, aqua, red -
   darkened to clear AA for the small link text on white, same constraint
   the framework accents above are solving for. */
.fw-slide[data-fw="ava"],     .fw-tab[data-fw="ava"]     { --fw-accent: #1D4ED8; }
.fw-slide[data-fw="zentra"],  .fw-tab[data-fw="zentra"]  { --fw-accent: #0B7A5A; }
.fw-slide[data-fw="mydart"],  .fw-tab[data-fw="mydart"]  { --fw-accent: #8A6100; }
.fw-slide[data-fw="weguard"], .fw-tab[data-fw="weguard"] { --fw-accent: #6D28D9; }
.fw-slide[data-fw="neo"],     .fw-tab[data-fw="neo"]     { --fw-accent: #0E7490; }
.fw-slide[data-fw="trackme"], .fw-tab[data-fw="trackme"] { --fw-accent: #B00D26; }

/* ---------------------------------------------------------------------
 * PRODUCT SLIDE: two-column (copy left, image right)
 * The framework slides were a single centred column. Products carry more
 * to say and a visual, so inside #products the slide becomes a split.
 * Scoped to #products so the layout can't leak if the framework panel is
 * ever reinstated elsewhere.
 * ------------------------------------------------------------------- */
#products .fw-slide {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  text-align: left;
  gap: clamp(1.25rem, 2.5vw, 2.25rem);
  padding: clamp(1.5rem, 2.4vw, 2.25rem);
}
@media (min-width: 860px) {
  #products .fw-slide { grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr); }
}
#products .fw-slide-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: clamp(0.7rem, 1.1vw, 0.95rem);
}
#products .fw-slide-head { justify-content: flex-start; }
#products .fw-slide-desc {
  max-width: 44ch;
  color: var(--pb-fg);
  font-size: 1.0625rem;
  font-weight: 500;
}
/* The second paragraph is the detail; keep it clearly subordinate to the
   one-line pitch above it. */
#products .fw-slide-more {
  max-width: 52ch;
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--pb-muted);
}
#products .fw-slide-feats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 1.25rem;
  width: 100%;
  max-width: 32rem;
  margin: 0.15rem 0 0;
  padding: 0;
  list-style: none;
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 12px;
  color: var(--pb-muted);
}
#products .fw-slide-feats li {
  position: relative;
  padding-left: 0.95rem;
  line-height: 1.5;
}
#products .fw-slide-feats li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.5em;
  width: 5px;
  height: 5px;
  border-radius: 9999px;
  background: var(--fw-accent, var(--pb-blue));
}
#products .fw-facts {
  justify-content: flex-start;
  width: 100%;
  max-width: 32rem;
}

/* Image slot. Sized to the reference proportions so a real screenshot can
   drop straight in; until then it holds a branded plate. */
#products .fw-slide-figure {
  align-self: stretch;
  min-height: 15rem;
  border-radius: 14px;
  overflow: hidden;
  display: grid;
  place-items: center;
  background:
    linear-gradient(150deg,
      color-mix(in oklab, var(--fw-accent, var(--pb-blue)) 22%, transparent) 0%,
      color-mix(in oklab, var(--fw-accent, var(--pb-blue)) 7%, transparent) 55%,
      transparent 100%),
    var(--pb-elevated-1);
  border: 1px solid var(--pb-hairline-60);
}
/* The supplied captures run from 0.51 (NEO, a full-page dashboard) to 2.01
   (WeGuard, a wide console), so anchor the crop to the top-left: that keeps
   each app's header and first row of real content in frame instead of
   centring on whitespace. */
#products .fw-slide-shot {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top left;
  display: block;
}
#products .fw-slide-plate {
  display: grid;
  place-items: center;
  padding: 1.1rem 1.5rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--pb-hairline-60);
  box-shadow: 0 10px 30px -18px rgba(10, 31, 68, 0.45);
}
#products .fw-slide-plate img {
  height: 34px;
  width: auto;
  max-width: 190px;
  object-fit: contain;
  display: block;
}
:root[data-theme="dark"] #products .fw-slide-plate { background: rgba(255, 255, 255, 0.95); }
@media (max-width: 859px) {
  #products .fw-slide-figure { min-height: 10rem; order: -1; }
  #products .fw-slide-feats { max-width: none; }
}
/* The stage is [arrow | deck | arrow]. On a phone the two arrows plus their
   gaps took ~135px of a 350px panel, leaving the card about 216px wide -
   survivable for the old single-column framework slide, far too narrow now
   that the slide carries an image, two paragraphs and a feature grid. Give
   the deck the full width; the tab strip below is the primary control on
   touch anyway, and it stays fully keyboard- and screen-reader navigable. */
@media (max-width: 639px) {
  #products .fw-stage { grid-template-columns: minmax(0, 1fr); }
  #products .fw-stage .fw-arrow { display: none; }
}

/* Tab strip: hairline across the top, active tab marked by a short
   segment sitting on that hairline (the reference's tab indicator). */
.fw-tabs {
  margin-top: clamp(2rem, 3.5vw, 3rem);
  padding-top: 1px;
  border-top: 1px solid var(--pb-hairline);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
}
@media (min-width: 640px)  { .fw-tabs { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
/* One row, however many items the panel holds. This was a hard repeat(5)
   for the five frameworks; the six products left Trackme orphaned on a
   second row. Counting the children keeps the strip correct either way. */
@media (min-width: 1024px) {
  .fw-tabs { grid-template-columns: repeat(5, minmax(0, 1fr)); }
  .fw-tabs:has(> :nth-child(6)) { grid-template-columns: repeat(6, minmax(0, 1fr)); }
  .fw-tabs:has(> :nth-child(7)) { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
.fw-tab {
  position: relative;
  /* Makes the tab its own stacking context so the z-index:-1 glow layer sits
     behind the logo but still above the panel's background. */
  isolation: isolate;
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 20px 10px 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
  color: var(--pb-muted);
  opacity: 0.7;
  transition: color .2s ease, opacity .2s ease;
}
.fw-tab:hover { opacity: 1; color: var(--pb-fg); }
.fw-tab.is-active { opacity: 1; color: var(--pb-fg); }
/* Active tab: a full-width rule in the framework's accent sitting on the
   strip's hairline, with the colour bleeding downward behind the logo - the
   glow is a gradient rather than a box-shadow so it can't spill onto the
   neighbouring tabs. Both layers are always present so they fade and grow
   in rather than popping. */
.fw-tab::before {
  content: "";
  position: absolute;
  top: -1px;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--fw-accent, var(--pb-blue));
  transform: scaleX(0.4);
  opacity: 0;
  transition: opacity .3s ease, transform .3s ease;
}
.fw-tab::after {
  content: "";
  position: absolute;
  inset: 0 0 0 0;
  background: linear-gradient(180deg,
    color-mix(in oklab, var(--fw-accent, var(--pb-blue)) 16%, transparent) 0%,
    color-mix(in oklab, var(--fw-accent, var(--pb-blue)) 5%, transparent) 45%,
    transparent 85%);
  opacity: 0;
  transition: opacity .3s ease;
  pointer-events: none;
  z-index: -1;
}
.fw-tab.is-active::before { opacity: 1; transform: none; }
.fw-tab.is-active::after { opacity: 1; }
.fw-tab:focus-visible { outline: 2px solid var(--pb-blue); outline-offset: 2px; border-radius: 8px; }
.fw-tab-mark { flex: 0 0 auto; display: inline-flex; align-items: center; }
/* 28px of artwork, not 24: four of the five logos carry their own wordmark,
   and below that the type turns to mush. */
.fw-tab-mark img {
  box-sizing: content-box;
  height: 28px;
  width: auto;
  max-width: 96px;
  object-fit: contain;
  display: block;
  background: #FFFFFF;
  border: 1px solid var(--pb-hairline-60);
  border-radius: 6px;
  padding: 5px 7px;
}
/* Logo-strip behaviour: every logo keeps its own colour, dimmed until its tab
   is selected - the same read as the reference's customer-logo row.
   The dim is on the LOGO ONLY, not the whole tab. It used to be
   .fw-tab { opacity: 0.55 }, which also took the name text down with it and
   left the five unselected labels reading grey in light mode and half-white in
   dark - the one thing the site-wide text rule forbids. The selected tab is
   still obvious: its logo comes up to full colour and it alone gets the
   ::before fill and ::after marker set above. */
.fw-tab { opacity: 1; }
.fw-tab .fw-tab-mark { opacity: 0.55; transition: opacity .25s ease; }
.fw-tab:hover .fw-tab-mark,
.fw-tab.is-active .fw-tab-mark { opacity: 1; }
.fw-tab-name {
  font-family: 'Open Sans', monospace;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.fw-tab.is-active .fw-tab-mark { color: var(--pb-blue); }

/* Dark theme: the panel and card fills read as elevated panels instead of
   near-white, and the seal tile needs a stronger blue wash to register. */
:root[data-theme="dark"] .fw-panel {
  border-color: color-mix(in oklab, var(--pb-blue) 34%, var(--pb-hairline));
  background:
    radial-gradient(115% 85% at 0% 0%,
      color-mix(in oklab, var(--pb-blue) 26%, transparent) 0%,
      transparent 62%),
    radial-gradient(105% 80% at 100% 100%,
      color-mix(in oklab, var(--pb-red) 16%, transparent) 0%,
      transparent 60%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.02));
}
:root[data-theme="dark"] .fw-slide,
:root[data-theme="dark"] .fw-deck::before,
:root[data-theme="dark"] .fw-deck::after,
:root[data-theme="dark"] .fw-arrow {
  background: var(--pb-elevated-1);
}
:root[data-theme="dark"] .fw-arrow { color: var(--pb-blue-3, #3B82FF); }
/* The logo-sampled accents are picked for a white card and go muddy on a dark
   one, so each steps to a lighter tint in dark mode. */
:root[data-theme="dark"] .fw-slide[data-fw="hipaa"],
:root[data-theme="dark"] .fw-tab[data-fw="hipaa"]  { --fw-accent: #4FB3E8; }
:root[data-theme="dark"] .fw-slide[data-fw="phipa"],
:root[data-theme="dark"] .fw-tab[data-fw="phipa"]  { --fw-accent: #7FB3F0; }
:root[data-theme="dark"] .fw-slide[data-fw="pipeda"],
:root[data-theme="dark"] .fw-tab[data-fw="pipeda"] { --fw-accent: #9A9CD8; }
:root[data-theme="dark"] .fw-slide[data-fw="gdpr"],
:root[data-theme="dark"] .fw-tab[data-fw="gdpr"]   { --fw-accent: #7B87D8; }
:root[data-theme="dark"] .fw-slide[data-fw="soc2"],
:root[data-theme="dark"] .fw-tab[data-fw="soc2"]   { --fw-accent: #4FC8D8; }
:root[data-theme="dark"] .fw-slide[data-fw="ava"],
:root[data-theme="dark"] .fw-tab[data-fw="ava"]     { --fw-accent: #7CA0FF; }
:root[data-theme="dark"] .fw-slide[data-fw="zentra"],
:root[data-theme="dark"] .fw-tab[data-fw="zentra"]  { --fw-accent: #4ADE9E; }
:root[data-theme="dark"] .fw-slide[data-fw="mydart"],
:root[data-theme="dark"] .fw-tab[data-fw="mydart"]  { --fw-accent: #F5C451; }
:root[data-theme="dark"] .fw-slide[data-fw="weguard"],
:root[data-theme="dark"] .fw-tab[data-fw="weguard"] { --fw-accent: #A78BFA; }
:root[data-theme="dark"] .fw-slide[data-fw="neo"],
:root[data-theme="dark"] .fw-tab[data-fw="neo"]     { --fw-accent: #4FC8D8; }
:root[data-theme="dark"] .fw-slide[data-fw="trackme"],
:root[data-theme="dark"] .fw-tab[data-fw="trackme"] { --fw-accent: #FF8896; }

/* The plate is defined for both themes above; dark mode only needs the border
   softened, since a light hairline on white would ring each chip. */
:root[data-theme="dark"] .fw-slide-mark img,
:root[data-theme="dark"] .fw-tab-mark img {
  border-color: transparent;
}

/* Mobile: stack the mark above the name and let the copy breathe. */
@media (max-width: 639.98px) {
  .fw-slide-head { flex-direction: column; gap: 10px; }
  .fw-deck { margin-top: 16px; }
  .fw-arrow { width: 36px; height: 36px; }
  .fw-facts { gap: 12px; }
}

/* ============================================================
 * CLIENT QUOTE CARDS  (#reviews .tq-*)
 *
 * Replaces the single-card testimonial slider with all five verbatim
 * reviews on screen at once: alternating brand-blue and light blocks,
 * each closing on a name plus a quote-mark badge in the bottom-right.
 *
 * Multi-column, not grid: five cards in a 4-col grid would leave one
 * orphan on a second row. columns:4 packs them by height, so the block
 * stays bottom-ragged (short quotes make short cards) the way the
 * reference layout does, and it degrades to 3 / 2 / 1 columns without
 * any per-breakpoint card maths.
 *
 * Alternation is nth-child on DOM order, so it reads 1-blue, 2-light,
 * 3-blue, 4-light, 5-blue regardless of how the columns pack.
 * ============================================================ */
/* Grid, deliberately not multi-column. Multi-column fills column-major,
   so nth-child alternation stopped reading as a checkerboard - at two
   columns cards 1 and 3 (both blue) ended up side by side. A grid fills
   row-major, so blue/light/blue/light always alternates across each row.
   align-items: start keeps the bottoms ragged, card height following
   quote length, rather than stretching every card to the row's tallest.
   Five tracks at 1536+ puts all five reviews on one line; below that the
   last row simply runs short. */
.tq-grid {
  margin-top: clamp(2rem, 4vw, 3rem);
  /* Full-bleed. #reviews is a .shell, i.e. the 1520px content column, so the
     grid breaks out to the viewport: 50% resolves against the parent's
     content box, 50vw against the viewport, so the negative margin lands the
     edge at x=0. html/body already carry overflow-x: clip
     (see the overflow safeguards block), so 100vw cannot raise a horizontal
     scrollbar here. The padding is the small outer gutter. */
  width: 100vw;
  margin-inline: calc(50% - 50vw);
  padding-inline: clamp(1rem, 1.6vw, 2rem);
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: 16px;
}
@media (min-width: 640px)  { .tq-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1280px) { .tq-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }

/* Stepped row: cards 1 and 3 tall, 2 and 4 short. min-height rather than
   height, so a longer quote (or a translated string, which runs ~20% longer
   in French) grows the card instead of overflowing it - at 452px wide the
   longest of these four quotes needs ~305px, well inside the 460px floor,
   so in practice the pairs match exactly. */
@media (min-width: 1280px) {
  .tq-card:nth-child(odd)  { min-height: 460px; }
  .tq-card:nth-child(even) { min-height: 370px; }
}

.tq-card {
  display: flex;
  flex-direction: column;
  padding: clamp(1.5rem, 2vw, 1.875rem);
  border-radius: 4px;
  /* Floor so a two-line quote still reads as a block rather than a chip;
     margin-top:auto on the attribution then opens the same generous gap
     under short quotes that the reference layout has. */
  min-height: 320px;
}
.tq-quote {
  font-size: clamp(1rem, 0.35vw + 0.92rem, 1.125rem);
  line-height: 1.6;
  letter-spacing: -0.005em;
  /* Quote marks are baked into the text as &ldquo;/&rdquo;, so kill the
     UA quotes on <blockquote> to avoid doubling them. */
  quotes: none;
}
.tq-attrib {
  margin-top: auto;
  padding-top: clamp(1.5rem, 2.5vw, 2.25rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-width: 0;
}
.tq-who { min-width: 0; }
.tq-name {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 600;
  font-size: 0.9375rem;
  line-height: 1.3;
}
.tq-meta {
  font-family: 'Open Sans', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.4;
  margin-top: 5px;
}
.tq-src {
  display: inline-block;
  margin-top: 8px;
  font-family: 'Open Sans', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  border-bottom: 1px solid currentColor;
  padding-bottom: 1px;
  /* Was 0.75, which dimmed the "Clutch" attribution to grey on the light
     theme and half-white on the blue cards. Full strength on both; the
     underline already marks it as the link in the block. */
  opacity: 1;
  transition: opacity .2s ease;
}
.tq-src:hover { opacity: 1; }
/* Badge: the glyph sits on the cap line, so nudge it down to land
   optically centred in the circle instead of high in it. */
.tq-mark {
  flex: 0 0 auto;
  width: 54px;
  height: 54px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 13px;
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1;
  user-select: none;
}

/* --- alternating fills (light theme) --- */
.tq-card:nth-child(odd) {
  background: var(--pb-blue);
  color: #FFFFFF;
}
.tq-card:nth-child(odd) .tq-meta,
.tq-card:nth-child(odd) .tq-src { color: #FFFFFF; }
.tq-card:nth-child(odd) .tq-mark { background: #FFFFFF; color: var(--pb-blue); }

.tq-card:nth-child(even) {
  background: var(--pb-elevated-2);
  color: var(--pb-fg);
}
.tq-card:nth-child(even) .tq-meta { color: var(--pb-muted); }
.tq-card:nth-child(even) .tq-src { color: var(--pb-blue); }
.tq-card:nth-child(even) .tq-mark { background: var(--pb-blue); color: #FFFFFF; }

/* --- dark theme: the light card can't stay near-white, and
       --pb-elevated-2 is the page gradient's own bottom stop, so it would
       vanish. Lift it off the background instead and add a hairline. --- */
:root[data-theme="dark"] .tq-card:nth-child(even) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--pb-hairline-60);
}
/* --pb-blue is a deep #0F4FBA - fine as a link on a white card, too dark
   on the dark-theme panel. Step up to the lighter brand blue there. */
:root[data-theme="dark"] .tq-card:nth-child(even) .tq-src {
  color: var(--pb-blue-3, #3B82FF);
}

/* Mobile: drop the min-height floor so a short quote doesn't leave a
   tall empty card when it's the full width of the screen. */
@media (max-width: 639.98px) {
  .tq-card { min-height: 0; padding: 1.375rem 1.25rem 1.5rem; }
  .tq-mark { width: 46px; height: 46px; font-size: 1.875rem; padding-top: 11px; }
}

/* ============================================================
 * DARK-MODE PAGE GRADIENT
 *
 * Replaces the flat #060A14 page fill in dark mode with a
 * top-to-bottom ramp through three existing brand surfaces:
 *   #060A14  Deep Ink       (--pb-surface,    darkest, top)
 *   #08101F  Midnight Panel (--pb-elevated-1, mid)
 *   #0E1426  Slate Fog      (--pb-elevated-2, lightest, bottom)
 * plus a soft #0F4FBA (--pb-blue) glow at the very top of the page.
 *
 * Two layers, deliberately anchored differently:
 *   body::before - VIEWPORT-fixed base ramp, so the ink→fog reveal
 *     reads the same on a 2,000px page and a 20,000px one. (A ramp
 *     stretched over the whole document would be imperceptible, and
 *     background-attachment: fixed is unreliable on iOS Safari - a
 *     position: fixed pseudo-element is the dependable equivalent.)
 *   body::after - DOCUMENT-anchored glow, so the blue wash sits over
 *     the hero and scrolls away rather than trailing the viewport.
 *
 * Both are z-index negative, which paints them behind body's own
 * background - hence the transparent body below, leaving <html>'s
 * flat --pb-surface as the fallback beneath (its colour matches the
 * ramp's top stop, so there is no seam if a pseudo fails to paint).
 *
 * Geometry is declared unscoped and toggled by opacity so the
 * gradient cross-fades with the source's transition: background
 * .3s ease on html/body instead of snapping in on theme switch.
 * Sections and cards are transparent / alpha-blended throughout, so
 * the ramp shows through the full page.
 *
 * To drop the glow and keep the plain three-stop ramp: delete the
 * two body::after rules below. Light mode is untouched.
 *
 * To anchor the ramp to the DOCUMENT instead of the viewport (one
 * continuous ink→fog across the whole page): add position: relative
 * to the body rule at the end of this block, and switch body::before
 * to position: absolute. Note the ink→fog delta is only ~8/10/18 in
 * RGB, so spread over a ~9,000px page it is close to invisible -
 * viewport-anchoring is what makes the three stops actually read.
 * ============================================================ */
body::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: -2;
  pointer-events: none;
  opacity: 0;
  transition: opacity .3s ease;
  background: linear-gradient(180deg, #060A14 0%, #08101F 52%, #0E1426 100%);
}
:root[data-theme="dark"] body::before { opacity: 1; }

/* Brand glow - wide, shallow ellipse bled off the top edge so only
   its soft lower falloff lands on the page. No positioned ancestor,
   so top: 0 resolves against the initial containing block = the
   top of the DOCUMENT (hidden routes are display:none, so that is
   the top of whichever page is active). */
body::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: min(78vh, 760px);
  z-index: -1;
  pointer-events: none;
  opacity: 0;
  transition: opacity .3s ease;
  background: radial-gradient(120% 65% at 50% -6%,
    color-mix(in oklab, var(--pb-blue) 22%, transparent) 0%,
    color-mix(in oklab, var(--pb-blue) 8%, transparent) 34%,
    transparent 68%);
}
:root[data-theme="dark"] body::after { opacity: 1; }

/* Let the two layers above show. Specificity (0,2,1) beats the
   source's "html, body { background: var(--pb-surface) }" (0,0,1)
   at Pentabay.html line 179 - html keeps the flat fill. */
:root[data-theme="dark"] body { background: transparent; }

/* =========================================================
 * SERVICE LINE LINKS  (#services-canada .svc-line)
 * The "Our expertise includes" grid on the home page. Deliberately
 * lighter than .service-card: these are an index that routes to the
 * eight service pages, not a second set of pitch cards competing with
 * the #services block directly above.
 * ========================================================= */
.svc-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  align-content: center;
  gap: 0.6rem;
  /* Equal height whether or not a tile carries a framework chip, so the
     row holding Compliance-Ready Software doesn't tower over the others. */
  min-height: 4.1rem;
  padding: 0.95rem 1.15rem;
  border: 1px solid var(--pb-hairline);
  border-radius: 12px;
  background: transparent;
  text-decoration: none;
  color: var(--pb-fg);
  transition: border-color .25s ease, background .25s ease, transform .25s ease;
}
.svc-line:hover,
.svc-line:focus-visible {
  border-color: color-mix(in oklab, var(--pb-blue) 55%, transparent);
  background: color-mix(in oklab, var(--pb-blue) 5%, transparent);
  transform: translateY(-2px);
}
.svc-line-name {
  flex: 1 1 auto;
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  line-height: 1.35;
}
/* Frameworks sit on their own line under the name. Inline, the chip stole
   enough width to wrap "Compliance-Ready Software" onto two lines while
   every other tile stayed on one, so that row read as broken. */
.svc-line-sub {
  flex: 0 0 100%;
  margin-top: 0.15rem;
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--pb-muted);
}
/* Arrow stays on the name's line; the chip wraps beneath both. Ordered in
   CSS rather than markup so the chip keeps following the name in the DOM. */
.svc-line-arr {
  order: 1;
  margin-left: auto;
  color: var(--pb-muted);
  transition: transform .25s ease, color .25s ease;
}
.svc-line-sub { order: 2; }
.svc-line:hover .svc-line-arr,
.svc-line:focus-visible .svc-line-arr {
  color: var(--pb-blue);
  transform: translateX(3px);
}
@media (prefers-reduced-motion: reduce) {
  .svc-line, .svc-line-arr { transition: none; }
  .svc-line:hover, .svc-line:focus-visible { transform: none; }
  .svc-line:hover .svc-line-arr { transform: none; }
}

/* ============================================================
 * ServiceCapabilities + WhyPentabay
 * Styles for the two shared section factories in
 * public/pentabay-service-sections.js (Custom Software, AI & Data,
 * Cloud & DevOps).
 *
 * Colours come from the --pb-* tokens rather than the literal hexes in
 * the brief, because those literals are the DARK palette only and this
 * site's default theme is light. --pb-elevated-2 already resolves to
 * #0E1426 ("fog") on dark and #EEF1F5 on light, so one rule covers both
 * themes; the same holds for --pb-elevated-1 (#08101F "panel") and
 * --pb-surface (#060A14 "ink"). Hairlines use --pb-hairline for the same
 * reason - a literal rgba(255,255,255,.075) border is invisible on white.
 *
 * Column count is driven by --svc-cols, set inline per page (4 / 3 / 2)
 * so the last row never leaves a single orphan card. Collapses to 2 up
 * to 999px and 1 up to 559px, per the brief.
 * ============================================================ */
.svc-cap-rule {
  display: flex; align-items: center; gap: 16px;
  padding-bottom: 14px; border-bottom: 1px solid var(--pb-hairline);
}
.svc-cap-rule-label,
.svc-cap-rule-count {
  font-family: 'Open Sans', monospace; font-size: 11px; line-height: 1;
  letter-spacing: 0.16em; text-transform: uppercase; color: var(--pb-muted);
}
.svc-cap-rule-count { margin-left: auto; white-space: nowrap; }

.svc-cap-grid {
  display: grid; gap: 12px;
  grid-template-columns: repeat(var(--svc-cols, 3), minmax(0, 1fr));
}
@media (max-width: 999px) { .svc-cap-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 559px) { .svc-cap-grid { grid-template-columns: minmax(0, 1fr); } }

.svc-cap-card {
  display: grid; grid-template-columns: auto minmax(0, 1fr);
  gap: 14px; align-items: start;
  padding: 18px; border-radius: 16px;
  background: var(--pb-elevated-2);
  border: 1px solid var(--pb-hairline);
  transition: border-color 0.18s ease, transform 0.18s ease;
}
/* Hover and keyboard focus behave identically. Only the border, the 2px
   lift and the code chip change - nothing else moves, per the brief. */
.svc-cap-card:hover,
.svc-cap-card:focus-within {
  border-color: var(--pb-blue-2);
  transform: translateY(-2px);
}
.svc-cap-card:focus-within { outline: 2px solid var(--pb-blue-2); outline-offset: 2px; }

.svc-cap-code {
  font-family: 'Open Sans', monospace; font-size: 10.5px; font-weight: 600;
  line-height: 1; letter-spacing: 0.12em; text-transform: uppercase;
  padding: 6px 8px; border-radius: 7px; white-space: nowrap;
  color: var(--pb-muted);
  background: var(--pb-elevated-1);
  border: 1px solid var(--pb-hairline);
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}
.svc-cap-card:hover .svc-cap-code,
.svc-cap-card:focus-within .svc-cap-code {
  background: var(--pb-blue); border-color: var(--pb-blue); color: #FFFFFF;
}
.svc-cap-name {
  font-family: 'Poppins', system-ui, sans-serif; font-weight: 600;
  font-size: 18px; line-height: 1.3; letter-spacing: -0.01em; color: var(--pb-fg);
}
.svc-cap-desc { margin-top: 7px; font-size: 13.5px; line-height: 1.6; color: var(--pb-muted); }

/* Image card beside a serviceCapabilities intro (opt-in via cfg.figure).
   Same frame language as .hero-figure - 18px radius, hairline, clipped - so a
   section figure and a hero figure read as the same object. No aspect-ratio box
   here: unlike .hero-figure this is not cover-cropped, the art is shown whole,
   so the frame takes the image's own ratio and nothing is thrown away.
   NOTE: no backticks in this block - these rules live inside a JS template
   literal, so a backtick would end the string and break the build. */
.svc-intro-figure {
  margin: 0;
  /* align-self, NOT the items-center utility on the grid in the markup: that
     class does not exist in this project's CSS (it is a hand-written utility
     subset, not full Tailwind), so align-items computed to stretch, the figure
     grew to the copy column's height, and 34px of dead space appeared under the
     art inside the frame. Measured, not guessed: frame 474x298 around a 472x263
     image. Setting it here also means the figure cannot be broken again by a
     caller that forgets the class. */
  align-self: center;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--pb-hairline);
  background: var(--pb-elevated-1);
  box-shadow: 0 18px 60px -22px rgba(8, 16, 31, 0.32);
  line-height: 0;
}
.svc-intro-figure img { width: 100%; height: auto; display: block; }
/* The art is a dark-blue render, so on the light theme it lands as a heavy slab.
   Same correction .hero-figure takes in the opposite direction. */
:root[data-theme="light"] .svc-intro-figure { box-shadow: 0 18px 50px -26px rgba(8, 16, 31, 0.22); }
@media (max-width: 1023px) {
  /* Copy first, art under it - the grid collapses to one column here. */
  .svc-intro-figure { margin-top: 4px; }
}

/* Standards / practice labels under a capability grid. TEXT, deliberately not
   .pill chips: nothing in this row is interactive, and a bordered chip reads as
   a button and invites a click that does nothing. Mono, tracked and uppercase
   with a middle-dot separator, which is the same idiom as the section eyebrows
   on these pages. */
.svc-tags {
  font-family: 'Open Sans', monospace;
  font-size: 11.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--pb-muted);
  line-height: 2;
}
.svc-tag { white-space: nowrap; }
.svc-tag:not(:first-child)::before {
  /* The literal character, NOT a "\\00B7" CSS escape: this stylesheet is a JS
     template literal, where a backslash starts a JS escape sequence and "\\0"
     is an invalid one - it fails the build, it does not reach CSS. */
  content: "·";
  margin: 0 11px;
  opacity: 0.55;
  white-space: normal;
}

/* The one <em> the heading supports is an underline, not an italic. */
.svc-cap-heading em {
  font-style: normal;
  text-decoration: underline;
  text-decoration-color: var(--pb-blue-2);
  text-decoration-thickness: 2px;
  text-underline-offset: 5px;
}

/* Inline code spans inside capability / reason copy (region names such as
   ca-central-1). Mono role, no background fill so it stays quiet in body text. */
.svc-cap-desc code, .why-row-body code {
  font-family: 'Open Sans', monospace; font-size: 0.94em;
  letter-spacing: 0.01em; color: var(--pb-fg);
}

/* ---- WhyPentabay: two-column list, hairline between rows, no cards ---- */
.why-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); column-gap: 44px; }
@media (max-width: 899px) { .why-grid { grid-template-columns: minmax(0, 1fr); } }
.why-row { padding: 22px 2px; border-top: 1px solid var(--pb-hairline); }
/* Top rule on the first row of each column only, so the two columns start level. */
@media (min-width: 900px) { .why-row:nth-child(-n+2) { border-top: 0; padding-top: 0; } }
@media (max-width: 899px) { .why-row:first-child { border-top: 0; padding-top: 0; } }
.why-row-lead {
  font-family: 'Open Sans', monospace; font-weight: 600; font-size: 13.5px;
  line-height: 1.4; letter-spacing: 0.01em; color: var(--pb-fg);
}
.why-row-body { margin-top: 8px; font-size: 13.5px; line-height: 1.65; color: var(--pb-muted); max-width: 46ch; }

/* ---- LabelGrid: label-only tiles (benefits, industry names) ----
 * Deliberately flatter than .svc-cap-card: no code chip, no icon, no
 * description, no lift on hover. Only the border shifts to the blue,
 * because half of these tiles are links and half are not - a transform
 * would imply the plain ones are interactive too.
 *
 * Column count comes from --lg-cols (set inline per use so the last row
 * is never left with a single orphan tile), stepping to 2 on tablet and
 * 1 below 560px per the brief. */
.lg-grid {
  display: grid; gap: 12px;
  grid-template-columns: repeat(var(--lg-cols, 4), minmax(0, 1fr));
}
@media (max-width: 999px) { .lg-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 559px) { .lg-grid { grid-template-columns: minmax(0, 1fr); } }

.lg-tile {
  display: flex; align-items: center; min-height: 74px;
  padding: 18px 20px; border-radius: 16px;
  background: var(--pb-elevated-2);
  border: 1px solid var(--pb-hairline);
  font-family: 'Poppins', system-ui, sans-serif; font-weight: 600;
  font-size: 16px; line-height: 1.35; letter-spacing: -0.01em;
  color: var(--pb-fg);
  transition: border-color 0.18s ease;
}
.lg-tile:hover, .lg-tile:focus-visible { border-color: var(--pb-blue-2); }
/* Linked tiles carry no underline (the tile itself is the affordance) but
   must keep a visible focus ring for keyboard users. */
.lg-tile--link { text-decoration: none; }
.lg-tile--link:focus-visible { outline: 2px solid var(--pb-blue-2); outline-offset: 2px; }

/* ============================================================
 * HOMEPAGE: #why-us "WHAT SETS US APART" boxes
 *
 * Five title+paragraph pairs that previously ran as plain stacked prose. Boxed
 * so they scan as five discrete claims.
 *
 * Deliberately lighter than the .card stat tiles directly above them: a subtle
 * --pb-elevated-2 fill with a hairline, versus .card's transparent fill, 24px
 * radius and 4px hover lift. Two different kinds of information should not carry
 * identical chrome, and five lifting cards in a row is a lot of motion.
 * No transform on hover either - these are not links, so they should not
 * advertise themselves as clickable; only the border warms up.
 * ============================================================ */
.pb-diff {
  display: flex; flex-direction: column;
  padding: 1.45rem 1.55rem;
  border: 1px solid var(--pb-hairline);
  border-radius: 18px;
  background: var(--pb-elevated-2);
  transition: border-color .25s ease;
}
.pb-diff:hover { border-color: var(--pb-blue-2); }
.pb-diff-num {
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.18em;
  color: var(--pb-blue);
}
/* Descendant-scoped on purpose. These titles are <h3> (correct outline: the
   section's <h2> then five sub-claims), and the bare-heading rule near the top
   of this file - h3:not([class*="t-"]) - is specificity (0,1,1), so a single
   .pb-diff-title class (0,1,0) loses the font-size fight and the titles render
   at that rule's clamp() instead (24.56px at 1440px, the browser-ish default
   look). .pb-diff .pb-diff-title is (0,2,0) and wins. Do not flatten this back
   to one class. */
.pb-diff .pb-diff-title {
  margin-top: 0.7rem;
  font-size: 17px; font-weight: 600;
  line-height: 1.35; letter-spacing: -0.01em;
  color: var(--pb-fg);
}
.pb-diff-body {
  margin-top: 0.55rem;
  font-size: 14px; line-height: 1.6;
  color: var(--pb-muted);
}
@media (max-width: 380px) { .pb-diff { padding: 1.15rem 1.2rem; } }
@media (prefers-reduced-motion: reduce) { .pb-diff { transition: none; } }

/* ============================================================
 * HOMEPAGE: #frameworks chips
 * Promoted out of the cramped #why-us side rail.
 *
 * The #industries-serve marquee rules that used to head this block are
 * gone. That section is a photo grid now (.ind-grid / .ind-card, styled
 * in the homepage redesign block at the end of this file), so every
 * "#industries-serve .cc-track .svc-line" selector here matched nothing
 * - .svc-line survives only in the #services-canada index grid, which
 * these rules were explicitly scoped away from.
 * ============================================================ */

/* Framework credentials. The old .pill treatment was 11px in --pb-muted, which
 * on the light theme is ~2.6:1 against the page - present but not readable at a
 * glance. This is 13px semibold in --pb-fg on --pb-elevated-2, with an accent
 * dot, so the chips actually register as credentials. Tokens only, so it holds
 * on both themes. */
.fw-chip {
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.7rem 1.15rem;
  border-radius: 9999px;
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 13px; font-weight: 600;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--pb-fg);
  background: var(--pb-elevated-2);
  border: 1px solid var(--pb-hairline);
  transition: border-color .2s ease, background .2s ease;
}
.fw-chip::before {
  content: ""; flex: 0 0 auto;
  width: 7px; height: 7px; border-radius: 9999px;
  background: var(--pb-blue);
}
.fw-chip:hover { border-color: var(--pb-blue-2); }
@media (max-width: 559px) {
  .fw-chip { font-size: 12px; padding: 0.6rem 0.95rem; letter-spacing: 0.08em; }
}
@media (prefers-reduced-motion: reduce) { .fw-chip { transition: none; } }

/* proseBlock: a heading + one prose paragraph, no card chrome. Measure is
 * capped so a long paragraph stays readable on a wide viewport. */
.pb-prose-block { max-width: 78ch; }

/* The display-face thesis line that can precede a label grid (/design). */
.lg-thesis {
  font-family: 'Poppins', system-ui, sans-serif; font-weight: 500;
  font-size: 24px; line-height: 1.4; letter-spacing: -0.015em;
  color: var(--pb-fg); max-width: 34ch;
}
@media (max-width: 767px) { .lg-thesis { font-size: 20px; max-width: none; } }

@media (prefers-reduced-motion: reduce) {
  .svc-cap-card, .svc-cap-code, .lg-tile { transition: none; }
  .svc-cap-card:hover, .svc-cap-card:focus-within { transform: none; }
}

/* 360px floor: tighten padding so nothing overflows the viewport. */
@media (max-width: 380px) {
  .svc-cap-card { padding: 14px; gap: 11px; }
  .svc-cap-name { font-size: 16.5px; }
  .svc-cap-rule { gap: 10px; }
  .lg-tile { padding: 15px 16px; min-height: 64px; font-size: 15px; }
}

/* ============================================================
 * HOMEPAGE REDESIGN (2026-07) - spacious, card-forward pass.
 *
 * Everything below is scoped to [data-route="home"] (the router stamps
 * that on <html>, and the value is "home", not "/"). Nothing here may
 * leak to a subpage. This block sits LAST in the stylesheet on purpose:
 * several rules have to beat earlier same-specificity declarations, and
 * source order is the cheapest way to do that without an !important war.
 *
 * Tokens only - no literal hexes - so the light theme (the default) and
 * the dark theme both hold. The one exception is the hero, which
 * re-scopes --pb-fg/--pb-muted to white for itself in Pentabay.html and
 * is therefore already theme-independent.
 * ============================================================ */

/* ── Hero video background ───────────────────────────────────
 * The <video> is the first child of .hero-shell and is absolutely
 * positioned, so it never becomes a row in that section's
 * grid-template-rows: 1fr auto. It layers ABOVE the section's
 * gradient + earth-photo background and BELOW every child that comes
 * after it in the DOM (the .aura, the micro-grid <svg>, and the
 * .shell content wrapper) - those paint later at z-index auto.
 *
 * Do not raise the opacity much past 0.3: the headline, lead and the
 * pentagon's SVG labels all sit on this field, and the hero's white
 * copy is only holding contrast because the gradient underneath stays
 * dark. .hero-shell already carries Tailwind "relative" and
 * "overflow-hidden", which is what makes inset:0 and the clip work. */
[data-route="home"] .hero-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: 0.25;
  pointer-events: none;
  border: 0;
}
/* The copy column + pentagon wrapper. This is .hero-shell's only
 * in-flow child; lifting it to z-index 1 keeps every interactive thing
 * in the hero (buttons, the #penta nodes, #nodeTip) clickable above the
 * video plate. */
[data-route="home"] .hero-shell > .shell {
  position: relative;
  z-index: 1;
}
/* A moving background behind body copy is the textbook reduced-motion
 * complaint, so drop it entirely rather than slowing it down. The
 * gradient + photo fallback underneath is unaffected. */
@media (prefers-reduced-motion: reduce) {
  [data-route="home"] .hero-video { display: none; }
}
/* Phones: pull the video further back (smaller type sits closer to the
 * bright parts of the frame) and let the poster stand in instead of
 * spending a cellular download on decoration. */
@media (max-width: 768px) {
  [data-route="home"] .hero-video { opacity: 0.15; }
}

/* ── Hero → page hand-off ────────────────────────────────────
 * The hero owns a full background of its own (a 4-stop blue scrim over
 * hero-na-earth.jpg, Pentabay.html ~line 305) and the page underneath is
 * --pb-surface. Those two never met, so the hero ended on a hard horizontal
 * colour step. This ramps the last 160px of the hero into the page colour so
 * the band ends instead of stopping.
 *
 * ::before, NOT ::after. The hero element is
 *   <section class="relative grain spotlight overflow-hidden hero-shell">
 * and BOTH .grain::after (film-grain tile, mix-blend-mode: overlay) and
 * .spotlight::after (the cursor-tracking radial) already target its single
 * ::after. A third rule there would win on specificity and its background
 * shorthand would replace the spotlight gradient outright - i.e. adding the
 * fade would silently delete the cursor spotlight from the hero. ::before is
 * unused on this element (verified: its content resolves to none).
 *
 * z-index: 1, not 0. ::before is the element's FIRST child in paint order,
 * and .hero-video is z-index: 0 - at 0 the fade would paint under the video and
 * do nothing. 1 puts it above the video, the .aura and the micro-grid <svg>,
 * while .hero-shell > .shell (also z-index: 1, but later in order) still paints
 * above it, so no hero copy is dimmed.
 *
 * Colour is Deep Ink on BOTH themes, deliberately not --pb-surface.
 * An earlier version faded to var(--pb-surface) on the reasoning that the hero
 * should hand off to the page colour. That was right in isolation and wrong in
 * sequence: what follows the hero on this page is #trust, which is a dark
 * full-bleed band on both themes. Fading to the surface token therefore put a
 * ~47px WHITE sliver (the hairline divider and its margins) between two dark
 * bands on the light theme - blue hero, sudden white, sudden black. Ending the
 * hero on the same Deep Ink the bands are built from makes hero -> #trust one
 * continuous dark passage, and on the dark theme the value is identical to
 * --pb-surface so nothing changes there.
 * .trust-art carries a matching negative top offset to close that 47px gap from
 * its side; the two are a pair. If #trust ever stops following the hero, change
 * this back to a ramp ending on var(--pb-surface) and drop that offset - note
 * the top stop then has to be the surface colour at zero alpha, NOT the
 * transparent keyword, which is rgba(0,0,0,0) and fades through black. */
[data-route="home"] .hero-shell::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 160px;
  background: linear-gradient(to bottom, rgba(6, 10, 20, 0), #060A14);
  z-index: 1;
  pointer-events: none;
}
/* Phones: the hero stacks (copy, then the route scene, then the caption) and
 * runs well past one viewport, which puts the scene caption inside the bottom
 * 160px. Shorten the ramp so it never washes over that line - there is also far
 * less hero width for the eye to read the seam across at this size. */
@media (max-width: 767px) {
  [data-route="home"] .hero-shell::before { height: 96px; }
}

/* ── Hero product rail ───────────────────────────────────────
 * The tab strip that replaced the interactive product
 * constellation in the hero's right column. It sits on the hero's
 * blue field, where Pentabay.html has already re-scoped --pb-fg /
 * --pb-muted / --pb-hairline to white values, so the hairline and
 * copy tokens below stay legible without hardcoding a light-theme
 * colour that would break on the other theme.
 *
 * --hp-accent is set per product by initHeroProducts() from the
 * same six accents the constellation nodes carried. The fallback
 * value keeps the rail sane if the script never runs. */
[data-route="home"] .hero-prods {
  --hp-accent: #7FA9FF;
  /* The six product accents were picked for constellation nodes sitting on
     the BRIGHT part of the earth video. The rail lives on the hero's deep
     blue left side instead, and there the darker ones - AVA #2563EB and
     WeGuard #7C3AED especially - sink into the field and the underline
     reads as nothing at all. Lifting each toward white keeps the
     per-product identity while guaranteeing the underline and the status
     pip are actually visible. NEO's cyan barely moves; AVA's blue moves a
     lot, which is exactly the intent. */
  --hp-accent-lit: color-mix(in oklab, var(--hp-accent) 58%, #FFFFFF);
  margin-top: clamp(1.5rem, 2.6vw, 2.5rem);
  padding-top: clamp(1.15rem, 1.8vw, 1.6rem);
  border-top: 1px solid var(--pb-hairline);
  max-width: 68ch;
  /* The hero copy column is centred, and a max-width block does not centre
     itself from the parent's text-align - it needs auto side margins, or the
     rail and its top rule sit left of the headline above them. */
  margin-inline: auto;
}
[data-route="home"] .hero-prods-rail {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  /* Flex children ignore text-align, so the tablist needs this to follow the
     centred hero. */
  justify-content: center;
  gap: 0.5rem clamp(1rem, 2.2vw, 2rem);
}
/* Weight is constant at 600 across active and inactive on purpose.
   Bolding the active tab would change its width, and since the rail
   auto-advances every 3.6s the whole row would twitch sideways six
   times a cycle. Active state is carried by colour + the underline. */
[data-route="home"] .hero-prod {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0 0 0.55rem;
  position: relative;
  cursor: pointer;
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: clamp(0.9375rem, 1vw, 1.0625rem);
  font-weight: 600;
  line-height: 1.2;
  /* Pure white even when inactive, per the site-wide "no half-white text"
     rule. The 0.6 / 0.9 / 1.0 ramp this used to carry made the five unselected
     product names read grey against the hero photo. The selected tab is still
     unambiguous: only it gets the ::after accent underline below. */
  color: #FFFFFF;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  transition: color .25s ease;
}
[data-route="home"] .hero-prod::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  border-radius: 2px;
  background: var(--hp-accent-lit);
  transform: scaleX(0);
  transform-origin: left center;
  opacity: 0;
  transition: transform .3s ease, opacity .3s ease;
}
[data-route="home"] .hero-prod:hover { color: #FFFFFF; }
[data-route="home"] .hero-prod.is-active { color: #FFFFFF; }
[data-route="home"] .hero-prod.is-active::after { transform: scaleX(1); opacity: 1; }
[data-route="home"] .hero-prod:focus-visible {
  outline: 2px solid #FFFFFF;
  outline-offset: 3px;
  border-radius: 4px;
}

/* The min-height is load-bearing, not decoration. The six taglines
   are different lengths, and this panel is the LAST thing in the
   hero - so without a reserved box the hero's own height would
   change every 3.6s and shove the entire page below it up and down
   on a timer. The floor is set for the longest tagline (Trackme's,
   at ~100 characters) plus the link row. */
[data-route="home"] .hero-prod-panel {
  margin-top: clamp(0.8rem, 1.3vw, 1.1rem);
  min-height: 5.75rem;
}
[data-route="home"] .hero-prod-tag {
  margin: 0;
  max-width: 56ch;
  margin-inline: auto;   /* centred hero: keep the tagline on the centre axis */
  font-size: clamp(0.9375rem, 1vw, 1.0625rem);
  line-height: 1.55;
  color: #FFFFFF;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.7);
}
[data-route="home"] .hero-prod-link {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 0.7rem;
  font-family: 'Open Sans', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #FFFFFF;
  text-decoration: none;
  /* Was 0.9 - full opacity so the row is pure white like the rest of the hero
     copy. The hover still reads: the gap opens. */
  opacity: 1;
  transition: opacity .2s ease, gap .2s ease;
}
[data-route="home"] .hero-prod-link:hover { opacity: 1; gap: 0.8rem; }
[data-route="home"] .hero-prod-link:focus-visible {
  outline: 2px solid #FFFFFF;
  outline-offset: 3px;
  border-radius: 4px;
}
[data-route="home"] .hero-prod-ver {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
}
[data-route="home"] .hero-prod-ver::before {
  content: "";
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: var(--hp-accent-lit);
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--hp-accent-lit) 30%, transparent);
}
/* The URL is the one lowercase thing in an uppercase row, so it has
   to opt out of the parent's text-transform explicitly. */
[data-route="home"] .hero-prod-url {
  text-transform: none;
  letter-spacing: 0.02em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.45);
  padding-bottom: 1px;
}
/* Phones: the six names wrap to two or three rows and the taglines
   run longer, so the reserved panel grows with them. */
@media (max-width: 640px) {
  [data-route="home"] .hero-prods { max-width: none; }
  [data-route="home"] .hero-prods-rail { gap: 0.4rem 1.1rem; }
  [data-route="home"] .hero-prod-panel { min-height: 7.5rem; }
}
@media (prefers-reduced-motion: reduce) {
  [data-route="home"] .hero-prod,
  [data-route="home"] .hero-prod::after,
  [data-route="home"] .hero-prod-link { transition: none; }
}

/* ── Industry photo grid (#industries-serve) ──────────────────
 * Replaces the single-row marquee. Column counts are chosen so the
 * 8 cards always fill complete rows and never leave an orphan:
 * 1-up below 560px (8x1), 2-up from 560 (4x2), 4-up from 1100 (2x4).
 * 3-up is deliberately skipped - 8 cards in 3 columns is 3/3/2, and
 * that short last row is exactly the ragged look this section had
 * when it was a clipped marquee. */
[data-route="home"] #industries-serve .ind-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(1rem, 1.6vw, 1.5rem);
}
@media (min-width: 560px) {
  [data-route="home"] #industries-serve .ind-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 1100px) {
  [data-route="home"] #industries-serve .ind-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

[data-route="home"] .ind-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;                 /* clips the media zoom to the card */
  text-decoration: none;
  color: inherit;
  border: 1px solid var(--pb-hairline);
  border-radius: var(--pb-radius);  /* 12px on the homepage, see the token block */
  background: var(--pb-elevated-1);
  transition: border-color .3s ease, transform .3s ease, box-shadow .3s ease;
}
/* aspect-ratio, not a fixed height: the sources are all 900x600, so 3/2
   reserves the exact box the image will occupy and the grid cannot shift
   as the lazy images arrive. */
[data-route="home"] .ind-card-media {
  display: block;
  aspect-ratio: 3 / 2;
  overflow: hidden;
  background: var(--pb-elevated-2);
  border-bottom: 1px solid var(--pb-hairline-60);
}
[data-route="home"] .ind-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .5s cubic-bezier(.22, .61, .36, 1);
}
[data-route="home"] .ind-card-body {
  display: block;
  padding: clamp(1rem, 1.4vw, 1.35rem);
}
[data-route="home"] .ind-card-name {
  display: block;
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: clamp(1.0625rem, 1.1vw, 1.1875rem);
  font-weight: 600;
  line-height: 1.25;
  color: var(--pb-fg);
}
[data-route="home"] .ind-card-sub {
  display: block;
  margin-top: 0.4rem;
  font-family: 'Open Sans', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: color-mix(in oklab, var(--pb-blue) 55%, var(--pb-muted));
}
/* The arrow is its own row under the label rather than floating at the
   card's right edge: industry names run from "Fintech" to
   "Telecommunications", and a right-anchored arrow left a ragged gap that
   made the 4-up row look misaligned. */
[data-route="home"] .ind-card-arr {
  display: block;
  margin-top: 0.85rem;
  font-size: 1.05rem;
  line-height: 1;
  color: color-mix(in oklab, var(--pb-blue) 75%, var(--pb-muted));
  transition: transform .3s ease, color .3s ease;
}
[data-route="home"] .ind-card:hover,
[data-route="home"] .ind-card:focus-visible {
  border-color: color-mix(in oklab, var(--pb-blue) 55%, transparent);
  transform: translateY(-4px);
  box-shadow: 0 16px 44px -22px color-mix(in oklab, var(--pb-blue) 50%, transparent);
}
[data-route="home"] .ind-card:hover .ind-card-media img,
[data-route="home"] .ind-card:focus-visible .ind-card-media img { transform: scale(1.06); }
[data-route="home"] .ind-card:hover .ind-card-arr,
[data-route="home"] .ind-card:focus-visible .ind-card-arr {
  transform: translateX(4px);
  color: var(--pb-blue);
}
[data-route="home"] .ind-card:focus-visible {
  outline: 2px solid var(--pb-blue);
  outline-offset: 3px;
}

/* Scroll reveal, replayed on every entry.
 * The hidden state is gated behind [data-ind-anim="1"], which
 * initIndustryReveal() sets at runtime and only when the visitor has not
 * asked for reduced motion. That gate is the whole safety story: with no
 * JS, a failed script, or reduced motion, the attribute never appears and
 * these rules never apply, so the cards cannot end up stranded at
 * opacity 0. Never move the hidden state onto .ind-card directly.
 * --i is set per card in the markup, so the stagger needs no JS. */
[data-route="home"] .ind-grid[data-ind-anim="1"] .ind-card {
  opacity: 0;
  transform: translateY(20px) scale(0.98);
  transition:
    opacity .55s cubic-bezier(.22, .61, .36, 1),
    transform .55s cubic-bezier(.22, .61, .36, 1),
    border-color .3s ease,
    box-shadow .3s ease;
  transition-delay: calc(var(--i, 0) * 70ms);
}
[data-route="home"] .ind-grid[data-ind-anim="1"].is-in .ind-card {
  opacity: 1;
  transform: none;
}
/* Hovering a card mid-reveal must not fight the stagger delay, or the
   lift waits out the remaining delay before it starts. */
[data-route="home"] .ind-grid[data-ind-anim="1"].is-in .ind-card:hover,
[data-route="home"] .ind-grid[data-ind-anim="1"].is-in .ind-card:focus-visible {
  transition-delay: 0s;
  transform: translateY(-4px);
}
@media (prefers-reduced-motion: reduce) {
  [data-route="home"] .ind-card,
  [data-route="home"] .ind-card-media img,
  [data-route="home"] .ind-card-arr { transition: none; }
  [data-route="home"] .ind-card:hover { transform: none; }
  [data-route="home"] .ind-card:hover .ind-card-media img { transform: none; }
}

/* ── Sub-page body rhythm + art bands ────────────────────────
 * Companion to applyBodyBands() in public/pentabay-page-imagery.js, which stamps
 * data-band="anchor|photo|panel" on the content sections of every /services and
 * /industries route. That file owns WHICH section gets which role and the
 * route -> art map; everything visual is here.
 *
 * Why: measured, only the hero on those pages carried any background. Everything
 * after it was the same flat surface for 4,700-7,900px (/enterprise-it: 13
 * sections, 7,866px, zero images in <main>). Three roles give the page a spine
 * and an alternating rhythm without touching twenty page templates.
 *
 * anchor - flat Deep Ink, full-bleed. The page's structural break.
 * photo  - full-bleed per-route photo, forced to brand blue (see the duotone
 *          note below). One per page.
 * panel  - inset elevated surface. Subtle; it only has to stop two neighbouring
 *          sections reading as one slab.
 *
 * The hero and the closing CTA band are deliberately untagged and unstyled here.
 * ============================================================ */

/* Shared: the two full-bleed bands. */
main.page:not(#page-home) > section[data-band="anchor"],
main.page:not(#page-home) > section[data-band="photo"] {
  position: relative;
  isolation: isolate;
  /* Real vertical padding. These inherit the generic section rhythm, which
     resolves to only 24-60px (measured: 40px @390, 60px @768, 24px @1024,
     31.7px @1440, 40px @1920 - it is NOT monotonic, 1024 is the tightest).
     That is too tight for a full-bleed band, and it left no room for the edge
     ramp: at 1440 a 72px ramp against 31.7px of padding faded to the page colour
     straight through the eyebrow, which on the light theme put light copy on a
     near-white background (measured bg luminance 107-130 under .t-eyebrow).
     Padding first, then a ramp that fits inside it at every width. */
  padding-block: clamp(3.5rem, 6vw, 6rem);
  /* Must stay BELOW padding-block above at every width. 28px @<=880,
     32.8px @1024, 46px @1440, 52px capped - against 56 / 61.4 / 86.4 / 96px of
     padding. Re-check both if either changes. */
  --band-ramp: clamp(28px, 3.2vw, 52px);
  /* The full token set, copied from the proven [data-route="home"] #services
     band rather than invented. --pb-surface is the load-bearing one: the stack
     section's cells are markup-level bg-[var(--pb-surface)] and .frame reads
     --frame-bg, so rescoping only the TEXT tokens would have produced white
     cards carrying white text on these bands - invisible on the light theme.
     --card-hover-bg and --frame-bg are hard-coded per theme in Pentabay.html and
     are not derived from --pb-surface, so they need their own values. */
  --pb-surface: #08101F;
  --pb-elevated-1: #0B1424;
  --pb-elevated-2: #0E1A2E;
  --card-hover-bg: rgba(255, 255, 255, 0.06);
  --frame-bg: linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.025));
  --pb-fg: #FFFFFF;
  --pb-fg-soft: #FFFFFF;
  --pb-muted: #FFFFFF;
  --pb-mist: #FFFFFF;
  --pb-paper: #FFFFFF;
  --pb-hairline: rgba(255, 255, 255, 0.16);
  --pb-hairline-60: rgba(255, 255, 255, 0.11);
  --pb-hairline-40: rgba(255, 255, 255, 0.08);
  --pb-haze: rgba(255, 255, 255, 0.16);
  --pb-haze-60: rgba(255, 255, 255, 0.11);
  --pb-haze-40: rgba(255, 255, 255, 0.08);
  /* Explicit, not just the token rescope. html/body declare color: var(--pb-fg)
     which resolves to #000000 AT THE BODY on the light theme, and that computed
     black inherits down here - rescoping the token cannot re-resolve an already
     inherited value. Any heading with no colour rule of its own would stay black
     on the dark band. Same reason .hero-shell and .pb-hero-banner carry one. */
  color: var(--pb-fg);
}
/* Content above the band background. */
main.page:not(#page-home) > section[data-band="anchor"] > *,
main.page:not(#page-home) > section[data-band="photo"] > * {
  position: relative;
  z-index: 1;
}
/* The band itself. These sections ARE .shell (max-width + auto margins), so
   full-bleed needs the 50% / 100vw / translateX(-50%) break-out; html and body
   already carry overflow-x: clip, so 100vw cannot raise a scrollbar.
   Ramped top and bottom edges for the same reason #trust and #why-us have them:
   a flat inset:0 band starts and stops on one pixel row, which is a hard
   luminance step into the page. --band-ramp stays inside the sections' vertical
   padding at every width, so the ramp never washes over copy. */
main.page:not(#page-home) > section[data-band="anchor"]::before,
main.page:not(#page-home) > section[data-band="photo"]::before {
  content: "";
  position: absolute;
  top: 0; bottom: 0; left: 50%;
  width: 100vw;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  background-color: #08101F;
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 var(--band-ramp), #000 calc(100% - var(--band-ramp)), transparent 100%);
          mask-image: linear-gradient(180deg, transparent 0, #000 var(--band-ramp), #000 calc(100% - var(--band-ramp)), transparent 100%);
}
/* Photo band. The art is forced to brand blue with a background-blend-mode
   duotone: the layer stack is [scrim gradient, art], the art blends in
   luminosity against background-color, so the render contributes light and shade
   only and the hue comes from --pb-blue. That is not decoration - the source
   photos carry off-brand casts (industry-enterprise-it.webp has orange RGB
   lighting and a visible hardware brand), and the duotone neutralises all of
   them so twenty routes land on the same blue regardless of the source.
   background-blend-mode does this inside ONE element, so ::after stays free. */
main.page:not(#page-home) > section[data-band="photo"]::before {
  background-color: var(--pb-blue);
  background-image:
    linear-gradient(178deg,
      rgba(6, 12, 26, 0.86) 0%,
      rgba(6, 12, 26, 0.70) 45%,
      rgba(6, 12, 26, 0.82) 100%),
    var(--pb-body-art, none);
  background-repeat: no-repeat, no-repeat;
  background-position: center center, center center;
  background-size: cover, cover;
  background-blend-mode: normal, luminosity;
}
/* Cards on the two bands. Dark translucent plates rather than their normal
   surfaces - the same call #why-us makes. Without this the light theme puts a
   near-white .card on a dark band, and the card's own copy is inheriting the
   band's light tokens, so it would be white on white. */
main.page:not(#page-home) > section[data-band="anchor"] .card,
main.page:not(#page-home) > section[data-band="photo"] .card,
main.page:not(#page-home) > section[data-band="anchor"] article.card,
main.page:not(#page-home) > section[data-band="photo"] article.card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(6px);
}

/* Panel role. Inset elevated surface, no full-bleed and no token rescope, so the
   copy keeps the page's normal colours and nothing inside can be stranded.
   These sections are .shell, so their box is the content column and their own
   --shell-x padding becomes the panel's inner padding - no extra padding needed. */
main.page:not(#page-home) > section[data-band="panel"] {
  background: var(--pb-elevated-1);
  border: 1px solid var(--pb-hairline-60);
  border-radius: var(--pb-radius, 12px);
}
/* The template puts a .hairline divider between most sections. Against a panel
   edge that reads as a double rule, so drop the one immediately adjacent. */
main.page:not(#page-home) > section[data-band="panel"] + div.hairline,
main.page:not(#page-home) > div.hairline:has(+ section[data-band="panel"]) {
  display: none;
}

/* ── Insights header photo (#insights .insights-figure) ───────
 * The band's header used to be copy-left with only the "Discuss your project"
 * link on the right, so the whole right half sat empty from the eyebrow down to
 * the INDUSTRIES SERVED strip. The photo fills it; the link moved under the lead.
 *
 * A framed figure at full strength, NOT a background wash - same reasoning as
 * .faq-figure and the blog covers. This one is a photograph rather than a render,
 * so a wash would be worse still: dialled back it reads as a grey rectangle, and
 * there is nothing here that needs the copy sitting on top of it.
 *
 * GRACEFUL ABSENCE is the load-bearing part. The asset may not be present, and a
 * 16/9 bordered frame around a broken image is far more visible than no figure at
 * all. The img's onerror stamps .insights-nofig on the row; the two rules at the
 * bottom then hide the frame and put the grid back to one column, which is
 * exactly the layout that shipped before. Nothing to clean up if the file never
 * arrives.
 * ============================================================ */
[data-route="home"] #insights .insights-head {
  display: grid;
  gap: clamp(1.5rem, 3vw, 2.75rem);
  align-items: center;
}
@media (min-width: 1024px) {
  [data-route="home"] #insights .insights-head {
    /* Copy takes slightly more than half: the lead is capped at max-w-xl, so an
       even split would leave a gap between the text and the frame rather than
       widening the measure. */
    grid-template-columns: minmax(0, 1.08fr) minmax(0, 0.92fr);
  }
}
/* The link sat on the header row's baseline before. In the copy column it needs
   its own top margin, and inline-flex so the arrow stays on the text baseline. */
[data-route="home"] #insights .insights-head-cta {
  display: inline-flex;
  margin-top: clamp(1.25rem, 2vw, 1.75rem);
}
[data-route="home"] #insights .insights-figure {
  margin: 0;
  border: 1px solid var(--pb-hairline);
  border-radius: var(--pb-radius, 12px);
  overflow: hidden;
  /* Placeholder fill so the frame is never a transparent hole while the photo
     is still decoding. */
  background: var(--pb-elevated-2);
  /* Fixed ratio + cover, so the frame is stable whatever the source dimensions
     are and the crop is predictable. */
  aspect-ratio: 16 / 9;
}
[data-route="home"] #insights .insights-figure img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Faces sit in the upper half of most meeting photography, so bias the crop
     upward rather than centring it. */
  object-position: center 32%;
}
/* Dark theme: a bright daylight photograph is the highest-luminance thing in an
   otherwise near-black band and pulls the eye off the heading. Pulled back just
   enough to sit in the page rather than punch out of it - not enough to look
   dimmed. */
:root[data-theme="dark"][data-route="home"] #insights .insights-figure img {
  filter: brightness(0.88) saturate(0.94);
}
/* Asset absent - revert to the pre-photo layout. */
[data-route="home"] #insights .insights-head.insights-nofig { grid-template-columns: minmax(0, 1fr); }
[data-route="home"] #insights .insights-head.insights-nofig .insights-figure { display: none; }

/* ── FAQ intro panel (#faq left column + .faq-figure) ─────────
 * The render is now the BACKGROUND of the FAQ intro copy (eyebrow, heading,
 * lead) rather than a separate framed square sitting under it.
 *
 * Read the history before changing the approach. A background wash lived here
 * once and was removed because, dialled back far enough to sit under copy on
 * the light theme, it just read as a smudge; it was replaced by a full-strength
 * framed figure, and that framed version is what this replaces.
 *
 * This is NOT a return to the wash. The failure mode of the wash was trying to
 * keep a dark render legible under DARK copy on a white page - the render had to
 * be faded almost to nothing, and that is what looked like dirt. Instead the
 * intro becomes a dark PANEL on both themes with the render at close to full
 * strength and the copy recoloured light, which is the same pattern #trust and
 * #why-us already use for "image behind copy" on this page. Nothing has to be
 * faded, so nothing can turn muddy.
 *
 * The figure element is reused as the art layer rather than adding markup: it is
 * already aria-hidden with an empty alt, which is exactly right for a backdrop.
 * ============================================================ */
[data-route="home"] #faq .grid > div:first-child {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: var(--pb-radius, 12px);
  border: 1px solid var(--pb-hairline);
  padding: clamp(1.5rem, 2.4vw, 2.25rem);
  /* Dark on BOTH themes, like #trust / #why-us. A fixed value, not
     --pb-elevated-1, because that token is #F7F8FA on light and would put the
     dark render behind black text. */
  background: #08101F;
  /* An explicit color declaration, NOT just the token rescope below. html/body set
     color: var(--pb-fg), which resolves to #000000 AT THE BODY on the light
     theme; that computed black then inherits all the way down here. Rescoping
     --pb-fg on this panel does not retroactively re-resolve an inherited
     computed value, so the heading - which has no colour rule of its own and
     simply inherits - stayed black on the dark panel (measured 1.1:1).
     .t-eyebrow and the lead were fine because both have their own
     color: var(--pb-*) declaration that re-resolves here. .hero-shell and
     .pb-hero-banner both carry the same explicit colour for this reason. */
  color: var(--pb-fg);
  /* Copy tokens rescoped light. The lead sets its colour inline
     (style="color:var(--pb-muted)") and an inline style beats any class rule, so
     the token has to change underneath it - same trick #why-us uses. */
  --pb-fg: #FFFFFF;
  --pb-fg-soft: #FFFFFF;
  --pb-muted: #FFFFFF;
  --pb-mist: #FFFFFF;
  --pb-hairline: rgba(255, 255, 255, 0.18);
  --pb-hairline-60: rgba(255, 255, 255, 0.13);
}
/* Below lg the grid is one column, so the panel shrink-wraps to ~150px of copy
   and the square render would be cropped to a sliver. A floor gives the art room
   without reintroducing the ~400px of decorative scrolling that got the old
   full-size figure hidden on phones in the first place. */
@media (max-width: 1023px) {
  [data-route="home"] #faq .grid > div:first-child { min-height: 15.5rem; }
}
/* Copy sits above the art. */
[data-route="home"] #faq .grid > div:first-child > *:not(.faq-figure) {
  position: relative;
  z-index: 1;
}
/* The inline link in the lead has to read on the dark panel. */
[data-route="home"] #faq .grid > div:first-child a {
  color: #9CC0FF;
  text-underline-offset: 3px;
}
[data-route="home"] #faq .grid > div:first-child a:hover { color: #FFFFFF; }

/* The art layer. Shown at every width now - as a background it costs no layout
   height, which is what made the old figure a phone problem. */
[data-route="home"] #faq .faq-figure {
  display: block;
  position: absolute;
  inset: 0;
  margin: 0;
  border: 0;
  border-radius: 0;
  background: none;
  aspect-ratio: auto;
  z-index: 0;
  pointer-events: none;
}
[data-route="home"] #faq .faq-figure img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* The render is dark navy at the top and a bright constellation at the
     bottom. Anchoring to the bottom keeps the glow low in the panel and the
     dark end behind the eyebrow/heading, so the copy sits on the calm part
     without needing a heavy scrim over it. */
  object-position: center bottom;
}
/* Scrim. Strongest at the top-left where the eyebrow, heading and lead sit,
   clearing toward the bottom so the constellation still reads. Fixed values
   rather than --pb-surface: the panel is dark on both themes. */
[data-route="home"] #faq .faq-figure::after {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(178deg,
      rgba(8, 16, 31, 0.92) 0%,
      rgba(8, 16, 31, 0.78) 34%,
      rgba(8, 16, 31, 0.34) 66%,
      rgba(8, 16, 31, 0.12) 100%);
}
/* Short-panel correction. Below lg the panel is wide and only ~250px tall, so
   object-fit: cover crops the square render down to a horizontal band - and with
   object-position anchored to the bottom that band is the BRIGHT constellation,
   with the copy sitting directly on it. The reasoning that puts the dark end
   behind the heading only holds while the box is portrait (371x758 at 1440).
   So at these sizes: pull the crop up off the glow, and carry the scrim across
   the whole panel instead of clearing it at the bottom, because here the copy
   fills the panel rather than occupying its top third. */
@media (max-width: 1023px) {
  [data-route="home"] #faq .faq-figure img { object-position: center 32%; }
  [data-route="home"] #faq .faq-figure::after {
    background:
      linear-gradient(178deg,
        rgba(8, 16, 31, 0.90) 0%,
        rgba(8, 16, 31, 0.86) 55%,
        rgba(8, 16, 31, 0.70) 100%);
  }
}
/* Held slightly back so the glow never competes with the headings - the same
   complaint the framed version had at full strength - but nowhere near the
   fade the old background wash needed, so the blues stay blue instead of
   collapsing to grey. Identical on both themes now that the panel is dark on
   both; there is no longer a light-theme case needing the opposite correction. */
[data-route="home"] #faq .faq-figure img {
  filter: brightness(0.85) saturate(0.9);
}

/* ── Bolder homepage reveals ──────────────────────────────────
 * Doubles the section travel (26px -> 50px) and slows it (0.6s ->
 * 0.9s) so the entrance is actually noticeable.
 *
 * The :not([data-reveal-staggered]) guard is REQUIRED, not cosmetic.
 * RevealFixer stamps data-reveal-staggered on any section whose
 * children it cascades individually, and Pentabay.html zeroes that
 * section's own transform so the block move and the per-item move do
 * not stack. This rule is (0,3,0) and would beat that (0,2,0) reset,
 * re-introducing a 50px block slide UNDER an already-moving cascade -
 * every card would travel twice. Excluding staggered sections keeps
 * the two mechanisms separate: this rule owns the sections that move
 * as one block, and RevealFixer's keyframes own the cascaded ones
 * (their distance and timing were raised to match, in RevealFixer.jsx
 * - change them together or the page animates at two speeds).
 *
 * Reduced motion needs nothing here: the global reset already forces
 * transition-duration to ~0 and [data-reveal] to opacity 1 /
 * transform none. */
[data-route="home"] [data-reveal]:not([data-reveal-staggered]) {
  transform: translateY(50px);
  transition-duration: 0.9s;
}

/* ── Section rhythm: let the homepage breathe ─────────────────
 * This deliberately REVERSES, for the homepage only, the 2026-07
 * "Whitespace + card-sizing tightening" rule further up
 * (main > section[class*="py-"] -> 24-40px a side). That tightening
 * is right for ~40 dense topic pages; the homepage is the one page
 * that should read unhurried. Subpages keep the tight rhythm.
 *
 * Specificity: (0,3,1) here against (0,1,1) there, and this block
 * also loads later - it wins on both counts, no !important.
 *
 * The hero is untouched by design: .hero-shell carries no py-*
 * utility (its padding lives on the inner .shell), so the selector
 * simply does not match it and the 100svh landing fold is safe. */
/* 2026-08: pulled back from clamp(3.5rem, 6.5vw, 6.5rem). "Unhurried"
 * had turned into dead space: at 1440 that clamp resolved to 93.6px a
 * side, and because it applies to BOTH edges the void between two
 * sections was 93.6 + 47 (the divider and its margins) + 93.6 = 234px
 * - measured from the last line of one section to the first line of the
 * next, i.e. roughly a quarter of the viewport of nothing, repeated
 * eleven times down the page.
 *
 * 3.5vw lands at 50.4px, so a boundary is now ~128px content-to-content.
 * For scale, the subpage rhythm this block exists to override is 24-40px
 * a side, i.e. 75-107px per boundary - so the homepage still reads a step
 * airier than a topic page, which is the whole point of the block, while
 * losing the scroll through emptiness. An intermediate 4vw pass was tried
 * first and still left a visible void at every seam. Floor and ceiling
 * come down proportionally so narrow and ultra-wide keep the same
 * relationship to the vw term.
 *
 * 2026-08, second pass: 3.5vw -> 2.9vw (50.4 -> 41px at 1440). The padding
 * was no longer the largest thing in a seam once it came down, so this pass
 * is deliberately the SMALL half of the change - the other two contributors
 * were both bigger and are fixed at their source instead of by shaving more
 * off here: #frameworks' min-height slack (~143px below its last line, see
 * FRAMEWORKS BAND BACKGROUND) and .fw-panel's 48px margin-top stacked on top
 * of #products' own padding (see PRODUCT PANEL). Measured content-to-content
 * at the frameworks -> products seam that screenshots flagged: 316px -> 153px.
 * Still ahead of the 75-107px subpage rhythm, so the homepage keeps its step
 * of extra air. */
[data-route="home"] main#page-home > section[class*="py-"] {
  padding-top: clamp(1.75rem, 2.9vw, 3.25rem);
  padding-bottom: clamp(1.75rem, 2.9vw, 3.25rem);
}
/* The closing contact band is the page's full stop - airier still,
   mirroring the exception the tightening block already made for it.
   Kept proportionally ahead of the section value above (~1.3x). */
[data-route="home"] main#page-home > section#contact {
  padding-top: clamp(2.25rem, 3.75vw, 4.25rem);
  padding-bottom: clamp(2.25rem, 3.75vw, 4.25rem);
}
/* ── First band after the hero ────────────────────────────────
 * #trust needs far less top padding than the rest, because the hero
 * above it does not end on its content: its last element sits 183px
 * clear of the hero's bottom edge (the globe's lower arc plus the
 * 160px hand-off fade). That slack already IS the breathing room, so a
 * full section pad on top of it stacked ~270px of emptiness between the
 * hero's product rail and the "SELECT CLIENTS" eyebrow. This lifts the
 * heading into the space the hero already provides.
 * Only #trust: every other section follows real content, so the shared
 * value above is correct for them. */
[data-route="home"] main#page-home > section#trust {
  padding-top: clamp(0.75rem, 1.75vw, 1.875rem);
}
/* Phones stay tighter. The clamp floor above is 36px a side, which
   across 12 sections is still ~860px of pure padding on a 375px screen. */
@media (max-width: 767px) {
  [data-route="home"] main#page-home > section[class*="py-"] {
    padding-top: 2rem;
    padding-bottom: 2rem;
  }
  [data-route="home"] main#page-home > section#contact {
    padding-top: 2.5rem;
    padding-bottom: 2.5rem;
  }
  /* The hero stacks on phones and ends much closer to its own content,
     so #trust does not get the desktop lift - it would collide with the
     hero's scene caption. Back to the normal section value. */
  [data-route="home"] main#page-home > section#trust {
    padding-top: 2rem;
  }
}
/* The dividers were pulled in to match the tight rhythm; open them
   back up so a hairline reads as a section break, and drop them to
   0.4 so they whisper rather than rule the page.
   2026-08: 1.6vw -> 0.9vw alongside the section padding above, so the
   divider keeps its proportion to the gap it sits in rather than
   becoming the dominant part of it. */
[data-route="home"] main#page-home > div.hairline {
  margin-top: clamp(0.35rem, 0.7vw, 0.85rem);
  margin-bottom: clamp(0.35rem, 0.7vw, 0.85rem);
}
[data-route="home"] main#page-home .hairline { opacity: 0.4; }

/* ── Card corner radius, via the token rather than the property ──
 * pentabay-tweaks.js owns radius site-wide:
 *   :root { --pb-radius: 24px }
 *   .card, .frame, .prod-card, .mock, .pill { border-radius: var(--pb-radius) }
 * and .frame takes calc(var(--pb-radius) * 0.75).
 *
 * So re-scoping the VARIABLE on the homepage subtree is strictly
 * better than restating border-radius on five selectors: every card,
 * frame, pill and plate follows automatically, the traced ring on
 * .card::after keeps working (it is border-radius: inherit), and
 * there is no cascade fight with the tweaks stylesheet at all.
 * 24px reads soft/consumer; 12px is the tighter, more engineered
 * corner the reference layout uses. */
[data-route="home"] main#page-home { --pb-radius: 12px; }

/* ── Card interior space ─────────────────────────────────────
 * Base .card padding is clamp(1.125rem, 1.6vw, 1.75rem) (18-28px).
 * Open it up - the single biggest lever on "spacious" - while
 * keeping a hard phone floor so a 375px card does not spend half
 * its width on padding.
 *
 * NOT restated here: the card hover state. Pentabay.html:240
 * already does translateY(-4px) + a blue border + a lifted shadow
 * on .card:hover, which is the hover treatment this pass wanted
 * (and a touch stronger). Re-declaring it would have collided with
 * the animated-gradient hover that .service-card restates at
 * matching specificity. Left alone on purpose. */
[data-route="home"] main#page-home .card {
  padding: clamp(1.375rem, 2.4vw, 2.25rem);
}
@media (max-width: 640px) {
  [data-route="home"] main#page-home .card { padding: 1.25rem; }
}
/* Service cards ride a marquee at a fixed 300-352px, so they get a
   tighter interior than a full-width grid card would. */
[data-route="home"] #services .service-card {
  padding: clamp(1.25rem, 1.8vw, 1.6rem);
}

/* ── Large stat numbers (#why-us) ────────────────────────────
 * The four proof numbers (100+, 6, 5.0, 85%) are the page's only
 * genuinely numeric stats, and the tightening block above shrank
 * every .stat-num carrying text-[2.25rem] to 20-25.6px - correct
 * for the stat strips whose "values" are strings like
 * "Jira + Slack", wrong for these four.
 *
 * Scoped to #why-us for exactly that reason: do not let this leak
 * to a text-valued strip. (1,2,0) beats the
 * .stat-num[class*="text-[2.25rem]"] rule at (0,2,0), so the
 * inline Tailwind size is overridden without !important.
 * font-family is deliberately NOT changed - .stat-num is Open Sans
 * by design (Pentabay.html:273) and this pass changes no fonts. */
[data-route="home"] #why-us .stat-num {
  font-size: clamp(2.75rem, 5vw, 3.75rem);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.03em;
  color: var(--pb-fg);
}
/* Roomier gutters between the four proof cards. :has() pins this to
   the stats grid so the "WHAT SETS US APART" grid below is untouched. */
[data-route="home"] #why-us div.grid:has(> .card) {
  gap: clamp(1.25rem, 2vw, 1.75rem);
}
/* Top accent on each proof card. Deliberately an INSET box-shadow,
   not border-top: every .card carries a conic-gradient ring on
   ::after that traces the border box on hover, and a real 3px
   border would sit in that exact band and fight it. An inset shadow
   paints inside the padding box instead - layout-neutral, no
   collision. box-shadow does not accumulate across rules, so the
   hover state has to restate both layers (the lifted shadow value
   is copied from .card:hover in Pentabay.html:240). */
[data-route="home"] #why-us .card {
  box-shadow: inset 0 3px 0 0 color-mix(in oklab, var(--pb-blue) 60%, transparent);
}
[data-route="home"] #why-us .card:hover {
  box-shadow:
    inset 0 3px 0 0 var(--pb-blue),
    0 12px 40px -16px rgba(30, 64, 175, 0.18);
}

/* ── Service cards (#services marquee) ───────────────────────
 * Slightly wider cards so the copy inside gets a longer measure.
 * flex-basis AND width move together: ServicesCarousel.jsx measures
 * the real rendered width each frame, so they must agree or the
 * loop wrap goes soft. min-width was avoided for the same reason.
 *
 * No border-top accent here: .service-card already carries a
 * per-card accent (--acc, cycled every six cards) as a gradient
 * wash, a continuously traced conic ring on the border, and a
 * coloured S/0n badge. A fourth accent on the same edge as the ring
 * would be noise. */
[data-route="home"] #services .svc-marquee-track .service-card {
  flex: 0 0 clamp(300px, 25vw, 352px);
  width: clamp(300px, 25vw, 352px);
}
/* Edge fade, the same device .cc-viewport already uses on the client
   strip: without it the service cards hard-clip mid-card against the
   section gutter, which reads as a broken layout rather than a strip
   that continues. Narrower stops (3%/97%) than the logo marquee's
   6%/94% because these are content cards, not logos - fading a card
   title out would be worse than the clip. Horizontal only, so the
   viewport's 6px vertical padding (which exists to let the card hover
   lift and its shadow show) is untouched. */
[data-route="home"] #services .svc-marquee-viewport {
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 3%, #000 97%, transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0, #000 3%, #000 97%, transparent 100%);
}
[data-route="home"] #services .service-card .t-h3 {
  font-size: 1.3rem;
  font-weight: 600;
}
[data-route="home"] #services .service-card .svc-list {
  line-height: 1.7;
}
[data-route="home"] #services .service-card .card-foot .btn-link {
  font-weight: 600;
}

/* ── Client logo marquee (#trust) ────────────────────────────
 * The marquee itself needed no work: .cc-viewport already carries
 * the 6%/94% edge-fade mask, and ClientCarousel.jsx already drives
 * a seamless rAF loop at 42px/s with clone-fill, hover-pause,
 * drag-to-scrub and a reduced-motion opt-out. A CSS
 * "animation: marquee-drift" would have fought the JS transform on
 * .cc-track every frame, so it was not added.
 *
 * This is the one addition: a hover lift on the logo tile, so the
 * strip responds to the cursor the way the rest of the page does. */
[data-route="home"] #trust .cc-logo .cc-mark {
  transition: border-color .25s ease, transform .25s ease;
}
[data-route="home"] #trust .cc-logo:hover .cc-mark {
  border-color: color-mix(in oklab, var(--pb-blue) 45%, var(--pb-hairline));
  transform: translateY(-2px);
}
@media (prefers-reduced-motion: reduce) {
  [data-route="home"] #trust .cc-logo .cc-mark { transition: none; }
  [data-route="home"] #trust .cc-logo:hover .cc-mark { transform: none; }
}

/* ── Testimonial cards (#reviews) ────────────────────────────
 * The .tq-* block further up is a tuned layout - full-bleed grid,
 * stepped tall/short rows, alternating brand-blue and light fills
 * by nth-child, quote-mark badge bottom-right. Only the parts that
 * serve "spacious + unified corners" are touched.
 *
 * Two proposals were dropped as actively wrong here:
 *   - border-left: 4px solid var(--pb-blue) - invisible on the
 *     blue-filled odd cards, and it would break the alternation.
 *   - a 48px quote badge - .tq-mark is already 54px at 2.25rem,
 *     i.e. larger than the "bigger" value asked for.
 * .tq-card sets its own border-radius (4px) rather than reading
 * --pb-radius, so it needs the explicit value. */
[data-route="home"] .tq-card {
  border-radius: 12px;
  padding: clamp(1.625rem, 2.4vw, 2.25rem);
}
[data-route="home"] .tq-grid { gap: 20px; }
[data-route="home"] .tq-name { font-size: 1rem; }
[data-route="home"] .tq-meta { font-size: 11px; letter-spacing: 0.07em; }

/* ── Product carousel (#products) ────────────────────────────
 * Scoped to the homepage because the whole .fw-* vocabulary is
 * SHARED with the framework panel on /compliance - an unscoped
 * .fw-tab rule would restyle that page too.
 *
 * The tab indicator was left alone: .fw-tab::before is already a
 * 3px rule in the active product's own accent sitting on the strip
 * hairline, with a gradient glow bleeding down behind the logo
 * (::after). A border-bottom would have been a second, duller
 * indicator competing with it.
 *
 * .fw-slide-figure already has 14px corners and an accent-tinted
 * plate; it just had no depth. One shadow, and the corner brought
 * in to match the page's new 12px. */
[data-route="home"] #products .fw-slide-figure {
  border-radius: 12px;
  box-shadow: 0 18px 48px -26px rgba(6, 10, 20, 0.5);
}
/* Compound, not descendant: data-theme and data-route both live on
   <html>, so ":root[data-theme=dark] [data-route=home]" would be
   asking for a data-route element INSIDE the root and could never
   match. They have to be one selector. */
:root[data-theme="dark"][data-route="home"] #products .fw-slide-figure {
  box-shadow: 0 18px 48px -22px rgba(0, 0, 0, 0.65);
}

/* ── Data residency cards (#canada) ──────────────────────────
 * Superseded. This block used to re-size .wna-stat for the old
 * .wna-card tiles; the region string is now typed by the
 * "WHY NORTH AMERICA RESIDENCY PLATES" block above, which owns the
 * whole plate. Its selector is (1,2,0) and so was this one, so
 * leaving this in place would have won on source order and silently
 * overridden the new size. Do not reintroduce a .wna-stat rule down
 * here - put it with the rest of the plate. */

/* ── FAQ (#faq) ──────────────────────────────────────────────
 * The accordion is a single .frame with Tailwind divide-y
 * hairlines between rows - a stacked list, not separate cards - so
 * it is NOT broken into individual bordered cards here: the
 * dividers are inline classes in the markup and the result would
 * have been double-ruled.
 *
 * The chevron is also left completely alone. Each summary already
 * ships an SVG .chev, and Pentabay.html already rotates it 180deg
 * on details[open]. The proposed summary::after arrow would have
 * rendered a second chevron beside the real one.
 *
 * What is added: roomier rows, a longer measure on the answer, and
 * a faint accent wash so the open row is obvious at a glance. */
[data-route="home"] #faq details.faq-item {
  padding: clamp(1.25rem, 2vw, 1.6rem);
  transition: background-color .25s ease;
}
[data-route="home"] #faq details.faq-item[open] {
  background: color-mix(in oklab, var(--pb-blue) 5%, transparent);
}
[data-route="home"] #faq .faq-q { font-size: 1rem; }
[data-route="home"] #faq .faq-a { line-height: 1.75; }
@media (prefers-reduced-motion: reduce) {
  [data-route="home"] #faq details.faq-item { transition: none; }
}


/* ============================================================
 * FULL-BLEED BAND COPY  (#trust / #why-us)
 *
 * Both bands became dark, fixed-value backdrops above (Deep Ink ->
 * Midnight Panel), on BOTH themes, so that they read like the hero
 * rather than like an inset panel. That inverts the contrast for the
 * copy sitting directly on them: on the default LIGHT theme
 * --pb-fg is near-black ink and --pb-muted is a mid grey, and neither
 * survives on an 0.85 Deep Ink field.
 *
 * The hero solves this by re-scoping --pb-fg / --pb-muted to white for
 * its whole subtree. That approach is WRONG here and was not used:
 * these two sections contain cards (.card, .pb-diff) and logo tiles
 * (.cc-mark) that keep their own light surfaces, so a subtree-wide
 * white token would turn their inner text white-on-white.
 *
 * Instead each run of copy that sits DIRECTLY on the band is recoloured
 * by explicit child path. The paths are deliberately exact - e.g.
 * "#why-us > div > .t-eyebrow" reaches the WHY CHOOSE US and WHAT SETS
 * US APART labels, which are grandchildren, but not the four stat-card
 * eyebrows, which are great-grandchildren inside .card. If the markup
 * nesting in these two sections changes, these selectors need revisiting.
 * ============================================================ */

/* #trust - eyebrow / heading / lead live in .mb-10 > .max-w-2xl, and the
   Clutch link in the sibling .mt-7 at the same depth. */
[data-route="home"] #trust > div > div > .t-eyebrow,
[data-route="home"] #trust > div > div > .t-h2,
[data-route="home"] #trust > div > div > .t-lead,
[data-route="home"] #trust > div > div > .btn-link,
/* #why-us - heading block and the WHAT SETS US APART label. */
[data-route="home"] #why-us > div > .t-eyebrow,
[data-route="home"] #why-us > div > .t-h2,
[data-route="home"] #why-us > div > .t-lead {
  color: #FFFFFF;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
}
/* Leads and eyebrows used to step back off pure white (#EAF0FA and
   rgba(255,255,255,0.88)) so the headings led. Both are pure white now, per
   the site-wide "no half-white text" rule - the hierarchy is carried by size
   and weight instead, which is how the rest of the page does it. */
[data-route="home"] #trust > div > div > .t-lead,
[data-route="home"] #why-us > div > .t-lead { color: #FFFFFF; }
[data-route="home"] #trust > div > div > .t-eyebrow,
[data-route="home"] #why-us > div > .t-eyebrow { color: #FFFFFF; }

/* The client marquee names and sector lines are text directly on the band,
   not on the tiles - the tile (.cc-mark) is only the logo plate, so it
   keeps its own surface and is left alone. */
[data-route="home"] #trust .cc-name {
  color: #FFFFFF;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
}
[data-route="home"] #trust .cc-meta { color: #FFFFFF; }

/* ── Client tally  (#trust > .trust-tally) ───────────────────
 * The number that counts 1 -> 20 as the band scrolls in, sitting in what
 * used to be blank space between the copy block and the marquee. Markup
 * and the count-up wiring are in Pentabay.html; this is only its look.
 *
 * Colours are literals, not tokens, for the same reason every other rule
 * in this block is: #trust is a dark band on BOTH themes, so any
 * --pb-fg/--pb-muted reference would come back near-black on the light
 * theme. It also is not a descendant of the "#trust > div > div" chain
 * that colours the eyebrow/heading/lead, so it does not inherit that
 * treatment and has to state its own.
 *
 * The label is width-capped and centred so it breaks under the number
 * rather than running the full band measure. */
[data-route="home"] #trust > .trust-tally {
  margin: clamp(1.25rem, 2.6vw, 2.5rem) auto clamp(1.5rem, 3vw, 2.75rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
}
/* .stat-num only sets font-family + weight, so the size is ours. Sized
   between the .t-h2 above it and the .cc-name row below so it reads as
   the band's focal figure without competing with the headline. */
[data-route="home"] #trust > .trust-tally .trust-tally-num {
  font-size: clamp(2.75rem, 6vw, 4.5rem);
  line-height: 1;
  color: #FFFFFF;
  letter-spacing: -0.02em;
  /* Same shadow the band's headings carry - the art behind this band is
     busiest in the middle, which is exactly where this sits. */
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
  /* The digits change every frame while counting. Without a tabular
     figure the whole number re-centres on each tick and visibly jitters,
     and the "+" walks left and right as 1 -> 9 becomes 10 -> 20. */
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}
[data-route="home"] #trust > .trust-tally .trust-tally-label {
  font-family: 'Open Sans', system-ui, sans-serif;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #FFFFFF;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
  /* No max-width, deliberately. The first version capped this at 22ch and
     the label broke into three stacked lines under the number, reading as
     a paragraph rather than a caption.
     ch is the wrong unit here and raising it was chasing the wrong thing:
     ch is the advance of the font's "0", but this label is
     text-transform: uppercase with 0.14em tracking, so its glyphs are far
     wider than a digit. Measured, the 30-character string renders 314px
     wide while 40ch resolves to only 220px - a ~57ch string by that
     unit's reckoning. Any ch cap that actually fit would be a magic
     number that silently re-wraps the moment the copy or the tracking
     changes.
     Uncapped, the natural width governs, and it lands on ONE line at
     every width tested - 1440, 390, 360 and 320 - with no horizontal
     overflow at any of them. If the copy grows it will wrap on its own on
     the narrow end, which is the correct failure mode; it does not need a
     cap to get there.
     "since 2018" was dropped from the copy separately - the eyebrow
     directly above already says "SELECT CLIENTS, 2018 PRESENT". */
}

/* Hairlines inside the bands were tuned against a near-white surface and
   vanish on a dark one. */
[data-route="home"] #trust,
[data-route="home"] #why-us {
  --pb-hairline: rgba(255, 255, 255, 0.24);
  --pb-hairline-60: rgba(255, 255, 255, 0.18);
  --pb-hairline-40: rgba(255, 255, 255, 0.12);
}

/* Cards on the band - the single source of truth for their surface.
   History: var(--pb-surface) at 97% was white on light and #060A14 on dark
   (identical to the band's own scrim, so the cards dissolved). --pb-elevated-2
   fixed the dark case but is #EEF1F5 on light, which put four near-opaque
   WHITE rounded cards on a band that is dark on BOTH themes - and that, not
   the artwork, is what read as "the section is still in a box". The
   .whyus-art layer behind them is genuinely full-bleed: measured 0 -> viewport
   width with border-radius 0 at 1440/1024/768/375.

   Now a dark translucent plate. The values are theme-independent literals on
   purpose, exactly like the .whyus-art scrim: this band is dark on both themes,
   so any surface/elevated token reintroduces the white version on light.

   The token rescope is what makes the card copy legible without !important -
   the markup sets its body colour inline (style="color:var(--pb-muted)"), and
   an inline style beats any class rule, so the token has to change underneath
   it. .stat-num and .t-eyebrow then inherit correctly for free. */
[data-route="home"] #why-us .card,
[data-route="home"] #why-us .pb-diff {
  backdrop-filter: blur(8px);
  border-color: rgba(255, 255, 255, 0.10);
  --pb-hairline: rgba(255, 255, 255, 0.10);
  --pb-fg: #FFFFFF;
  --pb-muted: #FFFFFF;
  /* .t-eyebrow is coloured from --pb-mist, not --pb-fg/--pb-muted, so it needs
     its own rescope - without it the card eyebrows (SINCE 2018, MERGE GATE...)
     stayed dark navy on the light theme and measured 1.97:1 on this plate. */
  --pb-mist: #FFFFFF;
  color: var(--pb-fg);
}
[data-route="home"] #why-us .card  { background: rgba(8, 16, 31, 0.70); }
/* Slightly more transparent: larger plates, and the render behind them is
   worth keeping visible. */
[data-route="home"] #why-us .pb-diff { background: rgba(8, 16, 31, 0.62); }
[data-route="home"] #why-us .card .stat-num { color: var(--pb-fg); }
/* --pb-blue (#0F4FBA) is too dark to read on this plate. */
[data-route="home"] #why-us .pb-diff .pb-diff-num { color: #7FA9FF; }

/* ============================================================
 * BANNER HERO COPY MEASURE  (.hero-shell.pb-hero-banner)
 * The heading and the lead were on different measures, so their right edges
 * did not line up: h1 carries max-w-[18ch] from topicPage (417.6px at 1440)
 * while the lead carries max-w-[60ch] (504px) - an 86px mismatch, and the 18ch
 * cap forced headings like "Mobile app development for clinical, field and
 * consumer teams." onto four short lines with room to spare beside them.
 *
 * Both now share ONE measure, so the two blocks align exactly and stay aligned
 * at every width (a single value rather than two different ch-based caps at two
 * different font sizes, which drift as the clamps resolve). Applies to all 19
 * routes carrying the banner hero, so the treatment stays consistent.
 *
 * 2026-08-07: the h1 SPLITS OFF from that shared value; the lead keeps it.
 * The 34rem cap is a fixed 544px at every size, so as the heading clamp
 * resolves smaller the cap silently gets WIDER in character terms, and the
 * wrap point drifts with viewport width - "Custom Software Development
 * Company in Canada." was 3 lines from 1440 up but 2 lines at 1024-1280,
 * purely because 544px buys more characters at a smaller size. A ch cap is
 * font-relative, so the wrap point is identical at every breakpoint.
 *
 * WHY 32ch AND NOT SOMETHING NARROWER. In Poppins 1ch = 0.5em (measured, not
 * assumed - the zero glyph is exactly half an em here), so today's 544px is ~31.6ch at
 * the current clamp. That is one character short: 32ch is the exact threshold
 * where the 46-char /custom-software title stops needing a third line. A
 * NARROWER cap moves the wrong way - measured at 1440, 20ch puts that title on
 * 4 lines and /real-estate on 7. The third line was never a missing width cap;
 * it was a cap a hair too tight. 32ch resolves to ~552px at 1440 and ~576px at
 * 1920, i.e. 46% and 38% of the copy column, so the heading still stops well
 * short of the artwork.
 *
 * CONSEQUENCE, deliberate: the h1 and the lead no longer share one measure, so
 * the exact right-edge alignment described above no longer holds at every
 * width. They stay within ~8px at 1440; at 1024 the heading is ~90px narrower
 * than the lead. That is the trade for a wrap point that does not move.
 * ============================================================ */
.hero-shell.pb-hero-banner .hero-grid .t-lead {
  max-width: 34rem;
}
/* 2026-08-07, third pass: 32ch -> 36ch, measured, not guessed. 32ch cleared the
   45-50 char titles to 2 lines but left the 57-64 char band on 3 uneven lines
   (/ai-data 11/20/24, /telecommunications 16/20/26). Sweeping 32/34/36/38ch
   against the reduced clamp below, at 1440:
     32ch  ai-data 3 (11/20/24)  telecom 3 (16/20/26)  design 3 (21/20/21)  real-estate 4
     34ch  ai-data 2 (23/33)     telecom 3 (16/20/26)  design 3 (21/20/21)  real-estate 4
     36ch  ai-data 2 (23/33)     telecom 2 (29/34)     design 3 (21/20/21)  real-estate 3
     38ch  ai-data 2 (23/33)     telecom 2 (29/34)     design 2 (37/26)     real-estate 3
   36ch is the pick: everything at or under 57 chars lands on 2 lines, the 63-64
   char band lands on either a balanced 2 or a 21/20/21 three, and the 84-char
   /real-estate finally drops from 4 lines to 3. 38ch is rejected because it
   forces /design onto a lopsided 37/26 two-liner instead of its even three.

   WIDER IN CHARACTERS, NARROWER IN PIXELS. Because the cap is font-relative and
   the clamp below came down at the same time, 36ch resolves to 504px at 1440
   against the 552px 32ch used to occupy - so the column gives back 48px to the
   hero artwork while fitting MORE text per line. That is the whole reason the
   two values move together.

   And the direction that does NOT work: narrowing. A ch cap and the font scale
   together, so shrinking the type cannot change a line count - only this number
   can. Measured at 1440, 20ch puts /custom-software on 4 lines and /real-estate
   on 7. Every step narrower adds lines. */
.hero-shell.pb-hero-banner .hero-grid h1.t-h1 {
  max-width: 36ch;
}
/* .t-h1 ships hyphens:auto + overflow-wrap:anywhere, which on a display-size
   face breaks words across lines with a visible hyphen ("Mobile app develop-" /
   "ment for clinical..."). That reads as broken rather than as typography, and
   the wider measure above makes it more likely, not less. hyphens:manual stops
   automatic breaking while still honouring any &shy; an author adds
   deliberately; overflow-wrap:break-word is kept only as an overflow guard for
   a pathologically long single word. */
.hero-shell.pb-hero-banner .hero-grid h1.t-h1 {
  hyphens: manual;
  overflow-wrap: break-word;
  word-break: normal;
}

/* ============================================================
 * HERO HEADLINE SIZE  (hero <h1> only - 2026-08-07)
 *
 * The base .t-h1 ramp (line 108) is clamp(1.5rem, 2vw + 1.1rem, 3.5rem),
 * i.e. it keeps growing to 56px while the hero copy column is pinned at a
 * fixed 34rem / 544px measure (BANNER HERO COPY MEASURE above). The two
 * pull in opposite directions, so the WIDER the screen the MORE lines the
 * headline takes: measured at 1920 every topic hero rendered its h1 at 56px
 * over 4 lines (251px tall), and /public-sector took 6 lines / 376px. That
 * is what pushes the headline past the banner's bottom edge and into the
 * artwork - the band is sized for the copy, not the other way round.
 *
 * Shrinking the type is the fix the brief asked for, so the measure and the
 * band geometry are left exactly as they are. Same clamp shape, gentler
 * slope, cap dropped 3.5rem -> 2.5rem (56 -> 40px):
 *
 *   1920: 56   -> 40      1280: 43.2 -> 34.9
 *   1600: 49.6 -> 40      1024: 38.1 -> 30.8
 *   1440: 46.4 -> 37.4     768: 33   -> 26.7
 *
 * Floor held at 1.5rem/24px, so phones are unchanged (they already sat on
 * the old floor). Topic heroes drop from 4 lines to 2-3 at every desktop
 * width and the headline stays inside the band.
 *
 * SCOPE - hero <h1> ONLY. Nothing else on the page moves:
 *   - .hero-shell h1.t-h1 covers all 19 topicPage routes, the /about,
 *     /careers, /contact-us, /privacy... subpages, the /blog index and the
 *     location-blog heroes - every one of them wraps its h1 in .hero-shell.
 *   - #page-weguard's hero is the one that predates .hero-shell (it carries
 *     an inline min-height instead), so it needs its own selector.
 *   - Body/section headings use .t-h2/.t-h3 and are untouched; the home hero
 *     uses .t-display and is handled at its own rule near line 104.
 * Specificity (0,2,1) / (1,1,1) - both beat the (0,1,0) base ramp.
 *
 * 2026-08-07, second pass: cap taken down again 2.5rem -> 2.25rem (40 -> 36px)
 * and the floor 1.5rem -> 1.375rem (24 -> 22px), so the ramp now reads
 * 22px at 320 / 24.8 at 768 / 28.4 at 1024 / 34.5 at 1440 / 36 at 1600+.
 *
 * NOTE this size change does NOT drive the line count any more. Now that the
 * banner measure is in ch (see BANNER HERO COPY MEASURE above), the cap scales
 * with the type, so characters-per-line - and therefore how a title wraps - is
 * identical at every size. The measure decides the wrap; this clamp only
 * decides how big the result is drawn.
 *
 * text-wrap:balance is ALREADY inherited from the .t-display/.t-h1/.t-h2 rule
 * in Pentabay.html and was active throughout - it is not what was missing, and it
 * cannot remove a line, only even out the ones a measure forces. It is
 * restated here so this rule is self-contained and survives that rule being
 * changed. overflow-wrap:break-word likewise duplicates the banner rule above
 * as the safety net for the non-banner heroes, which do not match it.
 *
 * 2026-08-07, third pass: cap 2.25rem -> 1.875rem (36 -> 30px, a 16.7% cut) and
 * floor 1.375rem -> 1.25rem (22 -> 20px). The ramp now reads 20px to 1000 /
 * 28px at 1440 / 30px from 1600 up. line-height is stated here for the first
 * time at 1.15; it was inheriting 1.12 from the base .t-h1 ramp before.
 *
 * Restating the point above, because it is the one that keeps getting lost:
 * this size change moves NO line counts on the banner routes. The measure is in
 * ch, the cap and the type scale together, so characters-per-line is fixed and
 * only the 36ch above can change how a title wraps. What this clamp changes is
 * how big the result is drawn - and, with the ch cap, how many pixels of the
 * band the copy column occupies (504px at 1440, down from 552px).
 * ============================================================ */
.hero-shell h1.t-h1,
#page-weguard > section:first-of-type h1.t-h1 {
  font-size: clamp(1.25rem, 1.25vw + 0.625rem, 1.875rem);
  line-height: 1.15;
  text-wrap: balance;
  overflow-wrap: break-word;
  /* Pentabay.html's .t-h1 base rule (~line 1906) ships hyphens:auto for
     long single words in body copy - on a hero h1 it instead breaks
     ordinary words like "software" mid-syllable ("soft-/ware"). The
     .pb-hero-banner routes already override this at their own, more
     specific selector (hyphens:manual, above); stating it here as
     hyphens:none catches every OTHER hero h1 that only matches this
     shared rule (e.g. /compliance) without touching those five pages -
     their (0,4,1) selector still wins over this (0,2,1) one. */
  hyphens: none;
  -webkit-hyphens: none;
  -moz-hyphens: none;
}

/* ============================================================
 * SERVICE + INDUSTRY HERO BANNERS  (.hero-shell.pb-hero-banner)
 *
 * Every /services and /industries route gets a full-bleed brand-blue
 * illustration behind its hero, matching the blue the home hero reads as.
 * pentabay-page-imagery.js adds the .pb-hero-banner class and sets
 * --pb-hero-banner to the file's url() - the route -> file map lives there
 * (HERO_BANNER), everything visual lives here.
 *
 * THREE BACKGROUND LAYERS, top to bottom:
 *   1. a left-weighted scrim - the copy column sits on the left, so that
 *      side is darkened to hold white text while the right side stays open
 *      for the artwork. Raise the left alphas to push the art back.
 *   2. the illustration itself, positioned right center because the source
 *      files are composed copy-left / art-right. One is not, and overrides
 *      layers 1 and 2 - see "CENTRE-COMPOSED BANNER HERO" below.
 *   3. a brand-blue fallback gradient - the same one the home hero falls
 *      back to. This is what shows if the file is missing or fails, which is
 *      why a not-yet-added asset degrades to "intentional blue band" rather
 *      than a broken hero. Do not remove it.
 *
 * Longhand background-position/-size/-repeat rather than the background
 * shorthand: the mobile block below has to drop layer 2 and keep 1 and 3,
 * and the shorthand can't null a single layer.
 *
 * The token rescope is not optional. These heroes are --pb-surface on both
 * themes by default and the light theme's --pb-fg is #000000; without
 * rescoping, every headline on twenty pages would be black on blue. The
 * --pb-mist / --pb-paper / --pb-haze aliases need their own values even
 * though they are declared as var(--pb-muted) / var(--pb-fg) /
 * var(--pb-hairline) at :root - custom-property substitution resolves where
 * the property is DECLARED, so the aliases computed against :root's values
 * and are inherited already-resolved. .t-eyebrow and .t-lead are coloured
 * from --pb-mist, so missing it leaves both of them dark on the blue.
 * ============================================================ */
.hero-shell.pb-hero-banner {
  background-color: #0F4FBA;
  background-image:
    linear-gradient(100deg,
      rgba(6, 20, 54, var(--pb-scrim-1, 0.42)) 0%,
      rgba(8, 28, 74, var(--pb-scrim-2, 0.26)) 34%,
      rgba(10, 40, 98, var(--pb-scrim-3, 0.10)) 58%,
      rgba(12, 50, 118, 0) 100%),
    var(--pb-hero-banner, none),
    linear-gradient(135deg, #1E5FD4 0%, #0F4FBA 55%, #0A2E6E 100%);
  /* Same --pb-hero-focus knob as the four-layer restatement below; kept in
     sync so whichever stack wins, the art layer honours the route's anchor. */
  background-position: center center, right var(--pb-hero-focus, center), center center;
  background-size: cover, cover, cover;
  background-repeat: no-repeat, no-repeat, no-repeat;

  color: #FFFFFF;
  isolation: isolate;
  /* min-height is NOT set here - see the dedicated rule below. */
  --pb-fg: #FFFFFF;
  --pb-fg-soft: #FFFFFF;
  --pb-muted: #FFFFFF;
  --pb-mist: #FFFFFF;
  --pb-paper: #FFFFFF;
  --pb-hairline: rgba(255, 255, 255, 0.26);
  --pb-hairline-60: rgba(255, 255, 255, 0.20);
  --pb-hairline-40: rgba(255, 255, 255, 0.13);
  --pb-haze: rgba(255, 255, 255, 0.26);
  --pb-haze-60: rgba(255, 255, 255, 0.20);
  --pb-haze-40: rgba(255, 255, 255, 0.13);
  /* Pantone 186 C lands at ~1.4:1 on this field, same as the home hero. */
  --pb-red: #FF5A6E;
  --pb-red-2: #FF7C8C;
}
/* ── Banner band height ─────────────────────────────────────
 * Sub-page heroes are min-height: auto (see "Sub-page (non-home) heroes"
 * further up), which shrink-wraps them to their copy. On a short hero like
 * /fintech that is ~450px, and since the background is cover-cropped, the
 * shorter the band the more of the illustration is thrown away vertically.
 * This gives the art a floor to live in without going full-viewport like the
 * home hero.
 *
 * SPECIFICITY, and the reason this is not just a line in the block above.
 * That rule is main.page:not(#page-home) > section.hero-shell, and
 * :not(#page-home) contributes the specificity of its ARGUMENT - an ID - so
 * the selector is (1,2,2). An ID beats any number of classes, so
 * .hero-shell.pb-hero-banner { min-height: ... } at (0,2,0) silently lost
 * and the band stayed content-sized. Carrying the same :not(#page-home) plus
 * one extra class gets (1,3,2), which wins without reaching for !important.
 * Verified in-browser, not assumed: computed min-height was 0px before this.
 *
 * The cap governs how much art survives, because cover crops the source by
 * exactly the ratio difference. 42rem keeps ~83% of a 16:9 frame at 1440px
 * and ~62% at 1920px. Raising it buys more art at the cost of pushing the
 * first section below the fold - this is the balance point, not arbitrary. */
main.page:not(#page-home) > section.hero-shell.pb-hero-banner {
  min-height: min(52vw, 42rem);
}

/* AI-EDTECH BANNER ANCHOR
 * The banner layer is \`right center\` for every route, i.e. a vertical crop taken
 * equally off the top and the bottom. That is correct for twelve of the thirteen
 * files - they are either wide enough not to crop, or their subject is centred.
 * hero-bg-ai-edtech.webp is neither: it is a 1.641 photograph of seven people
 * standing round a table, so it width-fits every band (1.922 at 1280 up to 2.324
 * at 1920) and loses 14.6% to 29.4% of its HEIGHT - and centred, half of that
 * comes off the top, where the heads are. Measured before and after: at 1920 a
 * centred crop cuts into the back row's heads; at 12% it does not.
 * 12% spends 3.5% of the 29.4% on the top edge and 25.9% on the bottom, and the
 * bottom of that frame is table, laptops and coffee cups.
 * Only the banner layer moves - the scrim gradient and the fallback gradient
 * stay centred.
 *
 * WAS A THREE-VALUE background-position LIST, AND THAT WAS A BUG. This rule
 * restated the whole list to move one layer, back when the stack was three
 * layers deep. The top hand-off later prepended a FOURTH layer (see "A FOURTH
 * BACKGROUND LAYER" above), and a three-value list against four layers is
 * repeated cyclically by CSS - so the 12% landed on the scrim gradient and the
 * artwork silently went back to centre, i.e. exactly the crop this rule exists
 * to prevent. It now sets the custom property the stack reads instead, which
 * is immune to the layer count changing again. */
main.page:not(#page-home) > section.hero-shell.pb-hero-banner[data-pb-banner-anchor="high"] {
  --pb-hero-focus: 12%;
}
/* The copy wrapper is the hero's only in-flow child; lift it so the buttons
 * stay clickable above the ::before ramp. */
.hero-shell.pb-hero-banner > .shell { position: relative; z-index: 1; }
/* The aura blob and the micro-grid <svg> are page-tone decoration tuned for
 * a --pb-surface hero. Over an illustration they only add haze, and the grid
 * reads as a moiré against the artwork's own dot patterns. */
.hero-shell.pb-hero-banner > .aura,
.hero-shell.pb-hero-banner > svg[aria-hidden="true"] { display: none; }
/* Legibility where the artwork's brightest passages reach into the copy. */
.hero-shell.pb-hero-banner .t-eyebrow,
.hero-shell.pb-hero-banner .t-h1,
.hero-shell.pb-hero-banner .t-lead { text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45); }
.hero-shell.pb-hero-banner .t-lead { color: #FFFFFF; }
/* Narrow the measure on banner heroes only. The factory sets max-w-[60ch],
 * which at 1280-1440 runs the longer lines of a 6-7 line lead right into the
 * artwork - measured against the actual glyph runs (Range client rects, not the
 * element box, which is wider than the text), the worst line-ends were hitting
 * 1.3:1 on /saas-platforms and 1.9:1 on /healthcare where they crossed a bright
 * cloud outline or hexagon. Repositioning the art fixes the page it was drawn
 * for; this fixes the class of problem, because every one of these
 * illustrations has its subject in the right ~45-55% and no hero lead needs a
 * 60-character measure anyway. 48ch is inside the conventional 45-75ch range,
 * so this reads better on its own terms, not just as a collision fix.
 *
 * 48ch is also the number the second batch of banner files is BUILT against -
 * it is ~43% of a 1440px viewport, and _hero-build-batch2.mjs bakes each file's
 * blur and dim ramps to cover that column fully and dissolve by ~60%. Widening
 * this measure would run copy past where those files stop protecting it. */
.hero-shell.pb-hero-banner .t-lead { max-width: 48ch; }
/* Same CTA inversion the home hero uses: a blue-filled primary sits on its
 * own colour here and stops reading as the primary action. */
.hero-shell.pb-hero-banner .btn-primary {
  background: #FFFFFF;
  color: var(--pb-blue);
  box-shadow: 0 8px 24px rgba(4, 18, 54, 0.30);
}
.hero-shell.pb-hero-banner .btn-primary:hover {
  background: #EEF3FF;
  color: var(--pb-blue);
  box-shadow: 0 12px 32px rgba(4, 18, 54, 0.38);
}
.hero-shell.pb-hero-banner .btn-ghost { background: rgba(255, 255, 255, 0.06); }
.hero-shell.pb-hero-banner .btn-ghost:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.55);
  color: #FFFFFF;
}
/* Hero -> page hand-off, same problem and same fix as the home hero: a blue
 * band against --pb-surface ends on a hard horizontal colour step. ::before,
 * not ::after - .grain::after and .spotlight::after both already target this
 * element's ::after and a third rule there would replace the spotlight
 * gradient outright. The home ramp is scoped to [data-route="home"], so
 * ::before is free on these heroes.
 *
 * The stops are theme-scoped literals rather than var(--pb-surface), because
 * a gradient from the \`transparent\` keyword fades through rgba(0,0,0,0) -
 * i.e. through black - which on the light theme would put a grey smudge
 * under every hero. Each theme needs its surface colour at zero alpha as the
 * top stop, which means writing both out. Do not "simplify" these two rules
 * into one \`transparent -> var(--pb-surface)\` gradient; that is the bug they
 * exist to avoid.
 *
 * NINE STOPS, EASED. A two- or three-stop ramp was the original bug report:
 * an earlier version was 96px tall and held at 0.10 alpha until 58%, so ~40px
 * of it did all the blending and it read as a bright BAND with a hard bottom
 * edge rather than a dissolve. It was shaped that way to keep the CTA row off
 * the opaque end when heroes were ~450px tall; the banner band is 672px now,
 * so that constraint is gone. These stops approximate an ease-in curve, which
 * is what makes the start imperceptible - a linear ramp of the same length
 * still shows a visible "top edge" where the wash begins.
 *
 * Length is paired with the shell's bottom padding below: 200px of ramp only
 * stays off the buttons because that padding lifts the CTA row clear of it.
 * Change one and re-check the other, or white copy lands on a half-white
 * field. Measured after this change: <=8% surface tint behind every CTA row. */
.hero-shell.pb-hero-banner::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom,
    rgba(6, 10, 20, 0) 0%,
    rgba(6, 10, 20, 0.015) 22%,
    rgba(6, 10, 20, 0.05) 38%,
    rgba(6, 10, 20, 0.12) 52%,
    rgba(6, 10, 20, 0.24) 64%,
    rgba(6, 10, 20, 0.42) 75%,
    rgba(6, 10, 20, 0.63) 85%,
    rgba(6, 10, 20, 0.84) 93%,
    #060A14 100%);
  z-index: 0;
  pointer-events: none;
}
:root[data-theme="light"] .hero-shell.pb-hero-banner::before {
  background: linear-gradient(to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.015) 22%,
    rgba(255, 255, 255, 0.05) 38%,
    rgba(255, 255, 255, 0.12) 52%,
    rgba(255, 255, 255, 0.24) 64%,
    rgba(255, 255, 255, 0.42) 75%,
    rgba(255, 255, 255, 0.63) 85%,
    rgba(255, 255, 255, 0.84) 93%,
    #FFFFFF 100%);
}

/* ============================================================
 * HERO → PAGE HAND-OFF, DARK THEME: MASK, NOT SCRIM  (2026-08)
 *
 * The ramps above (and the home one at "[data-route="home"]
 * .hero-shell::before") end on an OPAQUE colour, which only works if
 * that colour is what the page actually shows underneath. On the light
 * theme it is: body is a flat #FFFFFF and the banner ramps land on
 * 255,255,255 exactly - measured 0/0/0 delta across the seam on
 * /ai-edtech, /real-estate and /fintech. Those rules are correct and
 * are left alone.
 *
 * On the DARK theme the page stopped being a flat colour when the page
 * gradient landed (see "DARK-MODE PAGE GRADIENT" above). body::before is
 * position: fixed, so it paints #060A14 → #08101F → #0E1426 down the
 * VIEWPORT, not the document - the colour just below the hero therefore
 * depends on where in the window the seam happens to be, and body::after
 * adds a blue glow over the top ~760px of the document on top of that.
 * The ramps end on a hardcoded #060A14, which is only one of those three
 * stops. Measured delta at the seam, dark theme, all four hero routes:
 *     seam at viewport top     ~0            (ramp meets the 0% stop)
 *     seam mid-viewport        2, 6, 11      (page is #08101F)
 *     seam at viewport bottom  8, 10, 18     (page is #0E1426)
 * So the step is not just present, it BREATHES as you scroll - which is
 * what makes it catch the eye far more than a static 8/255 offset would.
 * No fixed colour can fix that, because the target is not fixed.
 *
 * The fix is to stop guessing the page colour and reveal it instead:
 * mask the hero's bottom edge to transparent. body::before and
 * body::after sit at z-index -2/-1 behind a transparent body, so
 * whatever they are painting at that scroll position shows through by
 * construction - exact at every scroll offset, viewport height and page
 * length, with nothing to re-tune later.
 *
 * The mask REPLACES the scrim in dark mode rather than joining it, so
 * ::before is switched off below; leaving both would fade the hero out
 * and then paint a black ramp over the gap.
 *
 * The curve is the same nine eased stops as the scrim, inverted (mask
 * alpha = 1 − scrim alpha) and re-expressed as distances from the bottom
 * edge, so the hand-off keeps the shape that block was tuned to. The
 * multipliers are (1 − stop%), and --pb-hero-fade is the only thing that
 * changes per hero - which is what keeps one curve instead of three.
 *
 * COST: a mask fades the hero's CHILDREN too, not just its background,
 * so the lowest hero content loses a little opacity. Measured on the
 * lowest element in each hero - worst case /web-marketing and
 * /low-code-platforms at 4.5%, /real-estate 4.3%, /ai-edtech 0.6%, home
 * and /fintech 0.0% (their content clears the ramp entirely). Under 5%
 * on the worst page is not resolvable by eye, and the CTA-clearance
 * padding below is what keeps it there - if that padding shrinks,
 * re-check these numbers, not just the contrast.
 *
 * Masking creates a containing block for positioned descendants, same as
 * filter. Checked: .hero-video, .aura and the micro-grid <svg> are all
 * position: absolute and already resolve against .hero-shell (it carries
 * Tailwind "relative"), and the element is already a stacking context
 * via isolation: isolate - so neither changes here. */
:root[data-theme="dark"] .hero-shell.pb-hero-banner,
:root[data-theme="dark"][data-route="home"] .hero-shell {
  --pb-hero-fade: 200px;
  -webkit-mask-image: linear-gradient(to bottom,
    rgb(0, 0, 0)          calc(100% - var(--pb-hero-fade)),
    rgba(0, 0, 0, 0.985)  calc(100% - var(--pb-hero-fade) * 0.78),
    rgba(0, 0, 0, 0.95)   calc(100% - var(--pb-hero-fade) * 0.62),
    rgba(0, 0, 0, 0.88)   calc(100% - var(--pb-hero-fade) * 0.48),
    rgba(0, 0, 0, 0.76)   calc(100% - var(--pb-hero-fade) * 0.36),
    rgba(0, 0, 0, 0.58)   calc(100% - var(--pb-hero-fade) * 0.25),
    rgba(0, 0, 0, 0.37)   calc(100% - var(--pb-hero-fade) * 0.15),
    rgba(0, 0, 0, 0.16)   calc(100% - var(--pb-hero-fade) * 0.07),
    rgba(0, 0, 0, 0)      100%);
          mask-image: linear-gradient(to bottom,
    rgb(0, 0, 0)          calc(100% - var(--pb-hero-fade)),
    rgba(0, 0, 0, 0.985)  calc(100% - var(--pb-hero-fade) * 0.78),
    rgba(0, 0, 0, 0.95)   calc(100% - var(--pb-hero-fade) * 0.62),
    rgba(0, 0, 0, 0.88)   calc(100% - var(--pb-hero-fade) * 0.48),
    rgba(0, 0, 0, 0.76)   calc(100% - var(--pb-hero-fade) * 0.36),
    rgba(0, 0, 0, 0.58)   calc(100% - var(--pb-hero-fade) * 0.25),
    rgba(0, 0, 0, 0.37)   calc(100% - var(--pb-hero-fade) * 0.15),
    rgba(0, 0, 0, 0.16)   calc(100% - var(--pb-hero-fade) * 0.07),
    rgba(0, 0, 0, 0)      100%);
}
/* The home hero's scrim is 160px, not 200px (and 96px on phones), because
   its stacked mobile layout puts the scene caption inside a longer ramp.
   Same lengths here so the hand-off is unchanged in shape. */
:root[data-theme="dark"][data-route="home"] .hero-shell { --pb-hero-fade: 160px; }
@media (max-width: 767px) {
  :root[data-theme="dark"][data-route="home"] .hero-shell { --pb-hero-fade: 96px; }
}
/* Scrim off wherever the mask is on. Specificity clears the (0,2,0)
   source rules without !important. */
:root[data-theme="dark"] .hero-shell.pb-hero-banner::before,
:root[data-theme="dark"][data-route="home"] .hero-shell::before {
  display: none;
}
/* Keep the CTA row clear of the ramp. Without this the tall-copy pages
 * (/web-marketing, /low-code-platforms) put their CTA row at 50-78% of the
 * ramp, i.e. on a ~44%-white field - the ghost button's white label measures
 * ~2.2:1 there, an accessibility failure rather than a cosmetic one.
 *
 * The copy in these heroes is TOP-aligned (.hero-grid is a plain block div on
 * subpages - its align-items:center only bites when the figure layout turns it
 * into a grid, which banner heroes don't use). So all the slack is already at
 * the bottom, and bottom padding works by making the section taller than its
 * min-height, which pushes the ramp DOWN while the copy stays put. That is why
 * the value is generous: it has to exceed the 200px ramp minus the slack the
 * page happens to have.
 *
 * !important is load-bearing here and is not a shortcut. Pentabay.html ~line 88
 * sets \`.hero-grid { padding-bottom: 0 !important }\`, and this element carries
 * both .shell and .hero-grid. An important declaration cannot be beaten by
 * specificity, so the only options were !important or removing that rule and
 * re-testing every other hero on the site. Verified: without it this rule is
 * dropped entirely and the CTA row stays on the wash. */
.hero-shell.pb-hero-banner > .shell {
  padding-bottom: clamp(3rem, 6vw, 7rem) !important;
}
/* The 1px divider the factory emits immediately after the hero
 * (<div class="hairline shell">) draws a hard line straight across the
 * dissolve - it is the visible seam under the fade, separate from the ramp
 * shape. Heroes that fade into the page do not need a rule to separate them
 * from it. Scoped to banner heroes only, so every other hairline is untouched.
 * Specificity (0,4,2) clears .hairline's (0,1,0). */
main.page > section.hero-shell.pb-hero-banner + .hairline {
  background: transparent;
}

/* ============================================================
 * BANNER HERO → TOP HAND-OFF  (2026-08)
 *
 * The counterpart to the ramp above, which solved the hero's BOTTOM edge
 * and left the top one a hard step. Measured at 1440px on
 * /custom-software, /healthcare and /low-code-platforms, both themes,
 * the stack above every banner hero was three bands with two seams:
 *
 *     nav          y   0 - 80     flat var(--pb-surface)
 *     breadcrumbs  y  80 - 118.5  NO background at all
 *     banner       y 118.5 - ...  brand blue + artwork
 *
 *   seam 1, nav -> crumbs, DARK ONLY, delta 19/255. .pb-breadcrumbs sets
 *     no background, so the strip showed body::after's blue document glow
 *     (measured 6,18,39) against the nav's flat 6,10,20. A blue shift, not
 *     just a lighter one, which is why 38px of breadcrumb read as its own
 *     band rather than as part of the bar.
 *   seam 2, crumbs -> banner, BOTH themes. Dark: 6,13,27 -> 22,60,91,
 *     delta 64 on /custom-software, 69 on /healthcare, 75 on
 *     /low-code-platforms. Light: 255,255,255 -> 1,33,102, delta ~253.
 *     A white-to-brand-blue cliff on the default theme.
 *
 * ── Why the crumbs come out of flow rather than getting a background ──
 * Painting the strip to match the nav fixes seam 1 and leaves seam 2
 * exactly as it was, i.e. the thing you actually see. And a 38px band of
 * dead colour between the header and the hero is the complaint, not just
 * its edges - the home page has no such band, the nav sits directly on
 * the hero. Taking the crumbs out of flow reproduces that: the banner
 * starts at y=80 immediately under the nav, and the crumbs float over its
 * top-left, which is the darkest, most scrim-protected part of every one
 * of these files. Two seams become one, and 38px of nothing goes away.
 *
 * Absolute rather than a negative margin on purpose. The strip's height
 * is padding-top clamp(12px, 2vw, 20px) + 2px + an unset line box - 38.5px
 * at 1440, but not a number that survives a viewport change or a font
 * swap. Out of flow needs no number.
 *
 * ── Why the top scrim normalises instead of matching ──────────────────
 * The nav has to end on the colour the banner starts with or seam 2 just
 * moves up 38px. The note at "TOPBAR - blue band on the home hero"
 * rejected painting these navs for exactly that reason: the banners are
 * 13 different illustrations and their top edges do not agree - measured
 * across three routes the blue channel alone ran 27 -> 102.
 *
 * So this does not sample them. Layer 0 below holds an opaque #07142A
 * across the crumb zone and then dissolves into the artwork over the
 * remaining ~110px, which makes the top edge of EVERY banner #07142A by
 * construction. One known colour for the nav to meet, on all 13 routes,
 * at any viewport width, and it does not drift when a file is redrawn.
 * That is also what makes the floating crumbs legible: white on #07142A
 * is ~15:1, and it holds on both themes because the scrim is dark on
 * both - it sits over brand blue, not over page surface.
 *
 * Held near-opaque to 24% and eased after, NOT a straight ramp. The
 * crumbs occupy the first 38px; a linear fade over the same 150px would
 * put their last line on ~0.75 alpha and their descenders on artwork. The
 * 110px eased tail is what keeps this from reading as the "bright band
 * with a hard bottom edge" the bottom ramp was originally guilty of -
 * that version had only ~40px doing the blending, this has 110.
 *
 * A FOURTH BACKGROUND LAYER, not a pseudo-element. Both are taken on this
 * element: ::before is the bottom ramp above, and ::after belongs to
 * .grain / .spotlight (verified in-browser - the section's class list is
 * "relative grain spotlight overflow-hidden hero-shell pb-hero-banner").
 * The longhand background-position/-size/-repeat the layer stack already
 * uses is what makes prepending a layer cheap; keep them in sync, and
 * remember the mobile block below re-declares the whole stack.
 * ============================================================ */
main.page:has(> section.hero-shell.pb-hero-banner) { position: relative; }

/* Out of flow, over the banner's top-left. left/right 0 rather than a width,
   so .shell's own gutter still positions the text. z-index 2 clears the
   hero's .shell (1) and its ::before (0).

   The colour rescope is in the same rule because the selector is the same.
   It is needed because the banner rescopes its tokens on ITSELF and the
   crumbs are a SIBLING, not a descendant - so they would keep the page's
   ramp and come out near-black on the navy, which on the light theme
   (--pb-fg: #000000) is invisible rather than merely dark. Same
   white-on-blue values the hero and the home nav use. */
main.page:has(> section.hero-shell.pb-hero-banner) > nav.pb-breadcrumbs {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  --pb-fg: #FFFFFF;
  --pb-muted: #FFFFFF;
  color: #FFFFFF;
}

/* Layer 0 = the top hand-off. Layers 1-3 are unchanged from the base rule
   above; they are repeated because background-image is a single property. */
.hero-shell.pb-hero-banner {
  background-image:
    linear-gradient(to bottom,
      rgba(7, 20, 42, 1)     0%,
      rgba(7, 20, 42, 0.98) 24%,
      rgba(7, 20, 42, 0.92) 30%,
      rgba(7, 20, 42, 0.78) 38%,
      rgba(7, 20, 42, 0.58) 47%,
      rgba(7, 20, 42, 0.38) 57%,
      rgba(7, 20, 42, 0.22) 68%,
      rgba(7, 20, 42, 0.10) 80%,
      rgba(7, 20, 42, 0.03) 90%,
      rgba(7, 20, 42, 0)   100%),
    linear-gradient(100deg,
      rgba(6, 20, 54, var(--pb-scrim-1, 0.42)) 0%,
      rgba(8, 28, 74, var(--pb-scrim-2, 0.26)) 34%,
      rgba(10, 40, 98, var(--pb-scrim-3, 0.10)) 58%,
      rgba(12, 50, 118, 0) 100%),
    var(--pb-hero-banner, none),
    linear-gradient(135deg, #1E5FD4 0%, #0F4FBA 55%, #0A2E6E 100%);
  /* Layer 2's vertical anchor is a variable so a single route can move its
     focal point without forking this whole four-layer stack. Defaulting
     to center = exactly the previous behaviour for all 19 other banners.
     See "BANNER FOCAL POINT + ROOMY BAND" below for who sets it and why. */
  background-position: top center, center center, right var(--pb-hero-focus, center), center center;
  background-size: 100% 150px, cover, cover, cover;
  background-repeat: no-repeat, no-repeat, no-repeat, no-repeat;
}

/* ============================================================
 * BANNER FOCAL POINT + ROOMY BAND
 *
 * Both knobs exist for the same reason: cover crops the source by the
 * ratio difference between the band and the art, and where that crop
 * lands is not a detail when the art is a PHOTOGRAPH rather than one of
 * the composed brand illustrations the banner system was built for.
 *
 * /telecommunications is the case that forced this. Its banner is now a
 * 1700x1131 contact-centre photo (ratio 1.50) in a band that measures
 * 1440x725 at desktop (ratio 1.99). Cover therefore scales to the width
 * and throws away 24% of the HEIGHT, split evenly top and bottom - and
 * the subjects' heads sit in the top ~15% of that source, so the default
 * centre anchor decapitated all three of them. Measured, not guessed.
 *
 * --pb-hero-focus moves the crop window up so the discarded 24% comes off
 * the desk and keyboards at the bottom instead of the faces at the top.
 * It is NOT a hard top anchor, which would pin the hair to the band edge with
 * no headroom. 12% keeps a little air above the heads.
 *
 * --roomy raises the band's floor, which is the only way to actually
 * REDUCE the crop rather than relocate it. At 1440 it takes the band from
 * 725px to 800px, so 84% of the photo survives instead of 76%. The cap is
 * 50rem rather than the base 42rem for the same reason the base rule
 * gives: past that the first section is pushed too far below the fold.
 * Note this cannot be pushed to "no crop at all" - that needs a 958px
 * band at 1440, which is most of a laptop viewport.
 *
 * Scaling the art BELOW cover was tried first and rejected: contain
 * fits the whole frame but leaves the art 1089px wide in a 1440px band,
 * and the 351px of exposed base gradient reads as a hard vertical seam
 * down the middle of the hero. There is no way to feather it, because the
 * art is one layer of a shared background stack and CSS filters and masks
 * apply to the whole element, not to a single layer.
 *
 * Specificity: the base min-height rule is
 * main.page:not(#page-home) > section.hero-shell.pb-hero-banner - (1,3,2)
 * because :not() contributes its ARGUMENT's specificity (an id). The
 * roomy rule matches that at (1,3,2) and wins on source order, being
 * later in the file. Same trap the base rule's own note documents.
 * ============================================================ */
main.page:not(#page-home) > section.hero-shell.pb-hero-banner--roomy {
  min-height: min(56vw, 50rem);
}

/* ── Scrim strength, for bright photographic banners ─────────
 * The left-weighted scrim (layer 2 of the stack, the 100deg gradient) is
 * tuned at 0.42 / 0.26 / 0.10 for the composed brand ILLUSTRATIONS this
 * system was built around. Those are dark navy on the copy side by
 * construction, so a light touch is all the white text needs.
 *
 * A photograph makes no such promise. hero-bg-ai-data-team.webp is the
 * case in point: the copy column lands on a lit meeting room, a subject in
 * a pale jacket and a glowing blue AI-Assistant display, and at 0.42 the
 * hero lead ran straight across the brightest passage in the frame and was
 * genuinely hard to read - not marginal, unreadable.
 *
 * The three alphas are variables rather than a restated gradient because
 * the stack is declared in THREE places in this file (base, the four-layer
 * version with the top hand-off, and the centre-composed variant). Forking
 * the whole background-image for a scrim tweak means keeping four copies in
 * sync, and the four-layer restatement above is already the bug that came
 * from doing exactly that. Overriding numbers touches one place and cannot
 * fall out of step with the geometry.
 *
 * Values: roughly double the default at the copy edge, decaying to the same
 * zero at the art edge, so the right-hand side of the photograph is as
 * bright as it was and only the text side darkens. */
main.page:not(#page-home) > section.hero-shell.pb-hero-banner[data-pb-banner-scrim="strong"] {
  --pb-scrim-1: 0.84;
  --pb-scrim-2: 0.60;
  --pb-scrim-3: 0.26;
}

/* 'deep' - one step beyond 'strong', for a photograph whose bright passage is
 * CONTIGUOUS and sits under the copy rather than scattered through the frame.
 * hero-bg-web-marketing.webp is the case: a top-down table shot whose centre is
 * a white SEO poster and a white SWOT sheet, directly beneath the second half of
 * a ten-line lead. 'strong' measures fine on the average and still reads badly,
 * which is the distinction this keyword exists for.
 *
 * /web-marketing has since swapped to hero-bg-web-marketing-code.webp and kept
 * this keyword - different frame, same shape of problem: a daylit window wall
 * down its left edge, one contiguous bright block under the copy's line-starts.
 * The measurements below are against the file this was tuned on; the other file
 * was not re-measured, it inherited the setting.
 *
 * Measured at 1440 over the copy's own bounding box (eyebrow through button),
 * sampling the backdrop with the text hidden, white text vs backdrop luminance:
 *
 *                     mean CR   worst CR   share of box under 4.5:1
 *   /telecommunications  9.4:1     2.5:1     6.5%   <- shipped reference
 *   web-marketing strong 9.8:1     2.9:1     5.3%
 *   web-marketing deep  12.2:1     3.6:1     0.6%
 *
 * So 'strong' already matched the reference on the numbers; the contiguity is
 * what the numbers miss. Stop 3 is deliberately left at 0.26, IDENTICAL to
 * 'strong', so the art edge of the frame is byte-for-byte as bright as it was
 * and only the text half darkens - the same rule the 'strong' note sets out.
 * Only stops 1 and 2 move, 0.84 -> 0.92 and 0.60 -> 0.78. */
main.page:not(#page-home) > section.hero-shell.pb-hero-banner[data-pb-banner-scrim="deep"] {
  --pb-scrim-1: 0.92;
  --pb-scrim-2: 0.78;
  --pb-scrim-3: 0.26;
}

/* Clear the floating crumbs. The factory's own top padding put the eyebrow
   ~45px into the band, which is inside the 38.5px strip; without this the
   crumbs land on the eyebrow. Matches the bottom padding's clamp shape so
   the copy block stays optically centred in the band.
   !important for the same reason as the bottom rule: Pentabay.html ~l. 88
   sets .hero-grid padding with !important and this element carries both
   .shell and .hero-grid. */
.hero-shell.pb-hero-banner > .shell {
  padding-top: clamp(3.5rem, 5vw, 5.5rem) !important;
}

/* The nav meets layer 0's opaque stop exactly, so the join is delta 0 on
   every banner route and both themes. Painted in BOTH scroll states for
   the same reason the home rule is: .scrolled flips at scrollY > 120,
   which is still inside a 672px band, so gating on :not(.scrolled) would
   snap the bar back to surface while the banner was still behind it.
   Specificity clears the (0,3,0) base rule at "TOPBAR - base" without
   !important; browsers without :has() keep the surface nav, which is
   exactly the pre-change look. */
:root:has(main.page.active > section.hero-shell.pb-hero-banner) .nav-shell,
:root:has(main.page.active > section.hero-shell.pb-hero-banner) .nav-shell.scrolled {
  background: #07142A;
}
/* Nav copy on the navy band. The row is coloured entirely from tokens, so
   rescoping them here recolours the links, the summaries, the theme
   toggle border and the mobile trigger in one place. Both themes get the
   same values because the band they sit on is the same. */
:root:has(main.page.active > section.hero-shell.pb-hero-banner) .nav-shell {
  color: #FFFFFF;
  --pb-fg: #FFFFFF;
  --pb-fg-soft: #FFFFFF;
  --pb-muted: #FFFFFF;
  --pb-hairline: rgba(255, 255, 255, 0.26);
  --pb-hairline-60: rgba(255, 255, 255, 0.20);
  --pb-hairline-40: rgba(255, 255, 255, 0.13);
}
/* The flyout panels need no rule here. The light-theme rescope under
   "TOPBAR - blue band on the home hero" (\`.nav-shell details >
   div[class*="absolute"]\`, \`.pb-nav-mobile-panel\`) is deliberately
   UNGATED - its own note says so - because it only ever puts the page ramp
   back inside a card that is --pb-elevated-1 on every route. It therefore
   already covers these routes, and duplicating it gated would be dead CSS.

   The logo does need one. .logo-img-dark is the ink wordmark the light
   theme normally shows, and it sits near-invisible on #07142A. Same swap
   the home band needs, same reason, gated to these routes and scoped to
   .nav-shell so the footer and the other two logo instances keep the
   normal theme behaviour. */
:root[data-theme="light"]:has(main.page.active > section.hero-shell.pb-hero-banner) .nav-shell .logo-img-light { display: block; }
:root[data-theme="light"]:has(main.page.active > section.hero-shell.pb-hero-banner) .nav-shell .logo-img-dark  { display: none; }

/* Phones: drop the illustration and keep the blue. These files put their
 * subject in the right ~45-55% of a wide frame, so at 390px \`cover\` +
 * \`right center\` shows nothing but the busiest part of the art directly
 * behind the headline. The scrim re-angles to top-down since there is no
 * longer a left/right split to protect, and min-height goes back to auto so
 * a tall headline + lead can't jail the first scroll line below the fold.
 *
 * The second batch of files (_hero-build-batch2.mjs) does not change this and
 * is if anything a stronger case for it: those five are centre-composed, and
 * the left-side blur that protects their copy column is baked into the LEFT of
 * the file, which is exactly the part \`right center\` throws away at 390px. */
@media (max-width: 767.98px) {
  /* Layer 0 is carried through here too - this block re-declares
     background-image, so omitting it would drop the top hand-off on phones
     and put the delta-253 light-theme step back on the smallest screens.
     Shorter (96px) for the same reason the bottom ramp is: no illustration
     to dissolve out of, and far less width for the eye to read a seam
     across. The crumb strip is the same 38.5px, so the opaque hold moves
     to 40% to keep covering it. */
  .hero-shell.pb-hero-banner {
    background-image:
      linear-gradient(to bottom,
        rgba(7, 20, 42, 1)     0%,
        rgba(7, 20, 42, 0.98) 40%,
        rgba(7, 20, 42, 0.82) 52%,
        rgba(7, 20, 42, 0.56) 64%,
        rgba(7, 20, 42, 0.32) 76%,
        rgba(7, 20, 42, 0.14) 86%,
        rgba(7, 20, 42, 0.04) 94%,
        rgba(7, 20, 42, 0)   100%),
      linear-gradient(160deg, rgba(6, 22, 60, 0.62) 0%, rgba(10, 45, 110, 0.30) 100%),
      linear-gradient(135deg, #1E5FD4 0%, #0F4FBA 55%, #0A2E6E 100%);
    background-position: top center, center center, center center;
    background-size: 100% 96px, cover, cover;
    background-repeat: no-repeat, no-repeat, no-repeat;
  }
  /* Same (1,3,2) selector as the band-height rule - a plain
   * .hero-shell.pb-hero-banner here would lose to :not(#page-home) too. */
  main.page:not(#page-home) > section.hero-shell.pb-hero-banner { min-height: 0; }
  /* Shorter ramp and less lift: the phone hero has no art to dissolve out of
   * (the illustration is dropped above), the copy runs most of the band, and
   * there is far less width for the eye to read a seam across. */
  .hero-shell.pb-hero-banner::before { height: 104px; }
  .hero-shell.pb-hero-banner > .shell { padding-bottom: 2rem !important; }
}

/* ============================================================
 * CENTRE-COMPOSED BANNER HERO  (.pb-hero-banner--center)
 *
 * One route: /low-code-platforms. Its illustration is the only one in the set
 * that is not copy-left / art-right - it puts a LOW CODE panel group on the far
 * LEFT and a NO CODE group on the far RIGHT, with a clean gap between them. So
 * there is no empty side to hand the copy, and the left-column treatment above
 * lands the headline straight on top of the left panel. (An earlier pass
 * "solved" this by clearing the entire LOW CODE panel out of the shipped file,
 * i.e. by deleting half the artwork.) This variant instead runs the copy down
 * the middle, between the two panel groups.
 *
 * The opt-in is a class, added by applyHeroBanners() from the one route -> file
 * map in public/pentabay-page-imagery.js (\`compose: 'center'\`). Everything
 * visual is here, same split as the base treatment.
 *
 * These rules are all (0,2,0)-(0,4,1) - the SAME specificity as the base
 * .pb-hero-banner rules they override - so they win on SOURCE ORDER alone and
 * must stay below that block. That includes staying below its phone media
 * query, which is why the art-drop is repeated at the bottom of this block
 * rather than inherited: without it this block would re-add the illustration on
 * phones.
 *
 * NOT cover - THIS IS THE ONE IMPORTANT DIFFERENCE. Every other banner hero
 * uses background-size: cover, and cover crops whichever axis has spare ratio.
 * This illustration has the words LOW CODE and NO CODE baked into its two outer
 * panels, ~200px in from each edge, so a horizontal crop beheads them - and
 * that is exactly what cover does here, because the band's height is set by the
 * COPY, not by min-height. This route's lead is 370 characters; measured, the
 * band runs 671-793px tall against a 52vw floor of 399-672, so below 1366px
 * cover flips to height-fit and crops the sides. At 1024 that clipped 35px off
 * the L of "LOW CODE"; at 768, 246px.
 *
 * Padding the file with more edge margin does NOT fix it - the extra width
 * scales the whole image down by the same factor, so the crop grows to match
 * (the padding cancels out of the algebra exactly). Only a much taller source
 * or dropping cover fixes it. So: 100% auto, which pins the art to the band's
 * WIDTH and makes the crop independent of band height. Horizontal clipping is
 * then structurally impossible at any copy length - which matters because the
 * lead is translated, and a longer locale is otherwise a silent regression that
 * only shows up as a beheaded word on one route in one language.
 *
 * The cost is the inverse case: when the band is taller than the width-scaled
 * art, there is leftover space at the bottom (146px at 1024, 74px at 1152, 3px
 * at 1280, none at 1366+, since the art is always 1/1.916 of the width). That
 * strip is what the flat background-color below is for. It also degrades in the
 * right direction - longer copy grows a strip of matched colour rather than
 * eating into the artwork.
 *
 * WHY THE MEASURE IS vw-BASED. With the art width-fit at every width, the clean
 * gap between the two panel groups is a constant 41.4% of the viewport, so the
 * measure can track it directly: 38vw leaves ~1.7% of viewport clear each side,
 * the 20rem floor stops it collapsing, and the 34rem cap is the same measure
 * the base treatment uses so wide screens read identically to the other
 * nineteen routes. Clearance measured per side at 1024/1280/1440/1920:
 * 17/22/26/126px. Widen it and the lines start crossing the gear icon on the
 * left and the particle fan on the right.
 * ============================================================ */
.hero-shell.pb-hero-banner--center {
  background-image:
    /* 0. The top hand-off, carried through from the base treatment. This rule
          re-declares background-image, so leaving it out drops the scrim on
          this one route and the nav's #07142A meets the art's own top edge
          instead - measured 1,33,102 here, i.e. the delta-62 seam the base
          rule had already removed everywhere else. Same stops as the base
          layer 0; see the TOP HAND-OFF note for why they are shaped this way. */
    linear-gradient(to bottom,
      rgba(7, 20, 42, 1)     0%,
      rgba(7, 20, 42, 0.98) 24%,
      rgba(7, 20, 42, 0.92) 30%,
      rgba(7, 20, 42, 0.78) 38%,
      rgba(7, 20, 42, 0.58) 47%,
      rgba(7, 20, 42, 0.38) 57%,
      rgba(7, 20, 42, 0.22) 68%,
      rgba(7, 20, 42, 0.10) 80%,
      rgba(7, 20, 42, 0.03) 90%,
      rgba(7, 20, 42, 0)   100%),
    /* 1. A centred pool instead of the base treatment's left-weighted wedge.
          An ellipse rather than a 90deg band: a horizontal gradient this wide
          reads as a soft-edged vertical STRIPE across the band, visible as a
          graphic element in its own right. It sits at 42% rather than 50%
          because hero copy is top-aligned (see the CTA-clearance note above).
          At the gap edges it is still ~0.44 alpha, which is what knocks the
          particle fan back far enough for white text to clear it, while the two
          panel groups stay at full brightness and read as bright wings around a
          darker centre column. */
    radial-gradient(ellipse 46% 78% at 50% 42%,
      rgba(6, 20, 54, 0.50) 0%,
      rgba(6, 20, 54, 0.44) 45%,
      rgba(6, 20, 54, 0.22) 72%,
      rgba(6, 20, 54, 0) 100%),
    /* 2. the illustration, width-fit and top-anchored - see the NOT cover note */
    var(--pb-hero-banner, none);
  /* THE FALLBACK GRADIENT IS GONE, replaced by a flat colour, and that is the
     other half of dropping cover. Layer 3 in the base treatment is only a
     missing-file fallback; here it would ALSO be what fills the strip below the
     art whenever the copy makes the band taller than the width-scaled
     illustration, and a gradient cannot do that job. The join between art and
     fill moves with band height - 78% of the band at 1024, 89% at 1152, 99% at
     1280, and further up again in any locale whose lead is longer - so any
     gradient is the wrong colour at the join for most widths. The first attempt
     here was a vertical ramp ending on the right colour at 100%, and the seam
     was plainly visible at 1024 as a lighter band across the full width.

     #042669 is measured: the mean of the illustration's bottom 40 rows. So the
     fill IS the art's bottom edge continued, the join is invisible at any band
     height, and the failure mode of a longer translation is a slightly taller
     strip of the same colour rather than a seam that moves into view. That is
     also why it is a literal rather than a token - it is sampled from the
     asset, and it has to be re-sampled if the art is re-exported.

     What this costs: a missing file now degrades to flat dark navy instead of
     the brand-blue gradient. Still the "intentional blue band" the base
     treatment is aiming for, just flatter - and it is the same navy this
     illustration's own background is, so it is the honest colour for this
     route. */
  background-color: #042669;
  /* Centred, not right-anchored: the gap the art is built around is centred in
     the frame to the pixel, so this is the whole horizontal alignment story.
     Top-anchored vertically, which is the correct end for both cases - it puts
     the fill strip at the bottom where the ::before ramp already is, and when
     the art instead overflows a short band (1366px and up) it crops from the
     bottom, away from the LOW CODE / NO CODE labels at y~180. Checked: the
     lowest art, the pie card at y~775, still clears the crop at 1920. Past
     ~2400px it starts losing the bottom of the two lower cards; the labels stay
     regardless, which is the part that must not be cut. */
  background-position: top center, center center, center top;
  background-size: 100% 150px, 100% 100%, 100% auto;
}
/* .hero-grid's two direct children are both flex rows (the eyebrow + optional
   mark, and the CTA pair). Targeting \`> div\` rather than their Tailwind
   utilities keeps this working if those class lists churn. */
.hero-shell.pb-hero-banner--center .hero-grid { text-align: center; }
.hero-shell.pb-hero-banner--center .hero-grid > div { justify-content: center; }
.hero-shell.pb-hero-banner--center .hero-grid h1.t-h1,
.hero-shell.pb-hero-banner--center .hero-grid .t-lead {
  max-width: clamp(20rem, 38vw, 34rem);
  margin-inline: auto;
}
/* Below 1024, drop the art and go back to the left-aligned treatment.
 *
 * This is 1023.98px and not the 767.98px every other banner hero uses, and the
 * extra 256px is not a taste call. Width-fitting the art (above) means it is
 * always 1/1.916 of the viewport tall, so the narrower the screen the smaller
 * the whole illustration gets, while the copy it has to sit between only gets
 * TALLER. At 768 the art is 401px tall inside a 731px band: the two panel groups
 * shrink to a pair of thumbnails, the centred copy overshoots them top and
 * bottom, and 330px of the band is the fallback strip. There is no measure that
 * fixes that - three columns of content do not fit in 768px. So this range gets
 * the same honest fallback phones get, which the base treatment already
 * establishes as "intentional blue band" rather than a broken hero.
 *
 * Centred copy goes back to left-aligned at the same time: it was centred to sit
 * between two panel groups, and with those gone a centred 370-character lead is
 * simply harder to read than a left-aligned one. */
@media (max-width: 1023.98px) {
  .hero-shell.pb-hero-banner--center {
    background-image:
      /* Layer 0 again - this is the third and last place the stack is
         re-declared. Note this block starts at 1023.98px, not the 767.98px the
         base mobile block uses, so 768-1023 on this route is served here and
         nowhere else; omitting the scrim would leave exactly that window
         seamed. 96px to match the base mobile treatment. */
      linear-gradient(to bottom,
        rgba(7, 20, 42, 1)     0%,
        rgba(7, 20, 42, 0.98) 40%,
        rgba(7, 20, 42, 0.82) 52%,
        rgba(7, 20, 42, 0.56) 64%,
        rgba(7, 20, 42, 0.32) 76%,
        rgba(7, 20, 42, 0.14) 86%,
        rgba(7, 20, 42, 0.04) 94%,
        rgba(7, 20, 42, 0)   100%),
      linear-gradient(160deg, rgba(6, 22, 60, 0.62) 0%, rgba(10, 45, 110, 0.30) 100%),
      linear-gradient(135deg, #1E5FD4 0%, #0F4FBA 55%, #0A2E6E 100%);
    background-position: top center, center center, center center;
    /* Three layers now - respecified so the 100% auto the desktop rule set for
       the illustration cannot land on a gradient here. */
    background-size: 100% 96px, cover, cover;
  }
  .hero-shell.pb-hero-banner--center .hero-grid { text-align: left; }
  .hero-shell.pb-hero-banner--center .hero-grid > div { justify-content: flex-start; }
  .hero-shell.pb-hero-banner--center .hero-grid h1.t-h1,
  .hero-shell.pb-hero-banner--center .hero-grid .t-lead {
    max-width: 34rem;
    margin-inline: 0;
  }
}

/* ============================================================
 * HERO VIDEO PLATE  (.hero-video-plate > video.hero-video)
 *
 * The ambient hero loop for topicPage routes that opt in with cfg.heroVideo
 * (public/pentabay-subpages.js). Currently /design + hero-laptop.mp4.
 *
 * The video element itself reuses the home hero's .hero-video class so the
 * two share one idiom (and one reduced-motion contract, below). What is new
 * is the PLATE: the home video covers the whole band because that hero's copy
 * is centred, while these banner heroes run their copy down the left in a
 * 34rem measure. So this one is confined to the right of the band and feathered
 * out, instead of being a full-bleed layer at low opacity.
 *
 * Why the shape reads as atmosphere rather than as an embedded player:
 *   - only ONE edge of the plate is interior: the left one, and it is the only
 *     one that is feathered. Top, bottom and right run full-bleed to the band's
 *     own edges, exactly like the illustration layer underneath, so they are
 *     section boundaries rather than the outline of a video box. (The bottom
 *     additionally dissolves under .pb-hero-banner::before.) The left fade is
 *     angled at 100deg to match the scrim gradient on .pb-hero-banner, so copy
 *     side and art side ramp along the same axis.
 *     A radial "feather all four edges" mask was the first attempt and it is
 *     worse, not better: an ellipse wide enough to cover the plate is still
 *     ~0.18 opaque where overflow clips it, which puts a faint straight line
 *     down the left edge - the exact artefact it was supposed to prevent.
 *   - opacity 0.78, over the band's own gradient rather than over another
 *     picture. The loop is already a dark navy scene with cyan glows - the
 *     band's palette - so it sits back on its own without being pushed as far
 *     down as the home loop (0.25), which has a headline and an SVG
 *     constellation directly on top of it.
 *
 * z-index 0, the same slot as [data-route="home"] .hero-video: above the
 * section's background layers, below .pb-hero-banner > .shell (z-index 1, the
 * copy) and below .pb-hero-banner::before (z-index 1, the bottom ramp), so the
 * ramp still dissolves the video into the page along with everything else.
 *
 * The plate stops at 52% from the left and the copy measure ends at 34rem
 * (544px) from the shell's left padding, so at the 1024px breakpoint where the
 * band is still side-by-side there is ~50px of clear gutter between the two,
 * and the mask has already faded to nothing well before it. Below 1024px the
 * plate is dropped outright rather than narrowed - see the media queries.
 * ============================================================ */
/* The illustration layer is dropped where the video actually replaces it: two
 * pieces of tech imagery in one band fight each other, and on /design they
 * fought over the same pixels - hero-bg-mobile.webp puts a phone in the right
 * ~45%, exactly where the laptop lands, so the phone's outline ran straight
 * through the laptop screen. (An earlier pass sank the art under a dark radial
 * inside the plate instead; that is gone with it, there being nothing left to
 * sink.)
 *
 * This re-declares the layer stack WITHOUT var(--pb-hero-banner) rather than
 * setting that property to none, which looks like the obvious fix and silently
 * does nothing: pentabay-page-imagery.js:575 sets it as an INLINE style, and no
 * stylesheet declaration outranks an inline one. Layers 1 and 3 are copied from
 * the .hero-shell.pb-hero-banner block above and have to stay in step with it;
 * dropping a single layer is not something the background shorthand can express,
 * which is why the whole stack is repeated (the phone media query above repeats
 * it for the same reason).
 *
 * :has(), not [data-route="design"], so the opt-in lives in exactly one place:
 * a route that sets cfg.heroVideo gets the video AND loses the illustration,
 * with no second list of route names to keep in step. Same idiom as the
 * .flex:has(> .btn ~ .btn) rule near the top of this file.
 *
 * The two conditions on the media query are the point of it. The plate is
 * display:none below 1280px and under reduced motion, and it stays in the DOM in
 * both cases (so :has() still matches) - gating on exactly the same conditions
 * hands the illustration back wherever the video is not there to take its place,
 * instead of leaving those widths with a bare blue band. */
@media (min-width: 1280px) and (prefers-reduced-motion: no-preference) {
  .hero-shell.pb-hero-banner:has(> .hero-video-plate) {
    background-image:
      linear-gradient(100deg,
        rgba(6, 20, 54, var(--pb-scrim-1, 0.42)) 0%,
        rgba(8, 28, 74, var(--pb-scrim-2, 0.26)) 34%,
        rgba(10, 40, 98, var(--pb-scrim-3, 0.10)) 58%,
        rgba(12, 50, 118, 0) 100%),
      linear-gradient(135deg, #1E5FD4 0%, #0F4FBA 55%, #0A2E6E 100%);
    background-position: center center, center center;
    background-size: cover, cover;
    background-repeat: no-repeat, no-repeat;
  }
}
.hero-shell.pb-hero-banner .hero-video-plate {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 52%;
  /* The source is 720px wide, so the plate is what decides how far it gets
     upscaled: 52% of the band is 749px at 1440 (1.04x, effectively native) but
     1229px at 2560 (1.7x, visibly soft on a video that is already compressed).
     Capping the plate keeps it flush right - the fade just starts further over -
     so ultrawide gets more blue field instead of a mushier laptop. */
  max-width: 56rem;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  /* The left feather. Long ramp - transparent through the first quarter, not
     fully opaque until nearly two thirds across - so the video arrives gradually
     instead of switching on at a line. Both spellings for WebKit.
     "to right", NOT the 100deg that would echo the scrim gradient on
     .pb-hero-banner. An angled gradient's 0% stop is placed to cover the box
     corner-to-corner, so it does not run along the box's left edge: at 100deg
     the lower-left of the plate is already ~0.3 opaque, and overflow:hidden then
     clips a partly-visible video into a hard vertical line down the bottom half
     of that edge. Only an axis-aligned ramp guarantees alpha 0 exactly where the
     clip happens - verified by sampling the rendered pixel row across the seam
     (≤3/255 of variation, i.e. no step). */
  -webkit-mask-image: linear-gradient(to right,
    rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.26) 26%, rgba(0, 0, 0, 0.78) 46%, #000 62%);
  mask-image: linear-gradient(to right,
    rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.26) 26%, rgba(0, 0, 0, 0.78) 46%, #000 62%);
}
/* Portrait source (720x1280) in a landscape-ish box: cover + a sized container
   is what keeps it undistorted, and object-position decides which horizontal
   band of that tall frame survives the crop. The laptop and its lit plinth
   occupy roughly 32-70% of the source height, so 42% from the top centres that
   subject in the visible band at every hero height instead of cropping to the
   dark ceiling above it or the floor tiles below. */
.hero-shell.pb-hero-banner .hero-video-plate > .hero-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 42%;
  opacity: 0.78;
  border: 0;
  display: block;
}
/* Same reduced-motion contract as the home hero: a moving background behind
   body copy is the textbook complaint, so drop it entirely rather than slow it
   down. The band is not left empty: the illustration comes back, because the
   rule that drops it carries this same query inverted (see above). */
@media (prefers-reduced-motion: reduce) {
  .hero-shell.pb-hero-banner .hero-video-plate { display: none; }
}
/* Tablet + small-desktop widths: the copy measure stops shrinking (34rem is a
   fixed cap) while the band does, so the gutter between text and plate closes
   from both sides. Measured, not estimated: at 1024px the copy's widest element
   (the CTA row) ends at 575px and a 52%-left plate starts at 573px - they touch.
   Rather than keep narrowing the plate until it is a sliver, hand the whole band
   back to the illustration below 1280px, which is also where a 3.5MB decoration
   stops being worth its bandwidth. */
@media (max-width: 1279.98px) {
  .hero-shell.pb-hero-banner .hero-video-plate { display: none; }
}
/* 1280-1439px: the band is real but tighter. Start the plate further right so
   the fade has more room to finish before it reaches the lead's longest lines -
   135px of clear gutter at 1280 against 82px at 1440. Opacity is deliberately
   NOT lowered here: it was, back when the video sat over the illustration and
   needed to be held back, and keeping that would put a visible step in the
   video's strength at the 1440 boundary now that it sits on plain gradient. */
@media (min-width: 1280px) and (max-width: 1439.98px) {
  .hero-shell.pb-hero-banner .hero-video-plate {
    left: 56%;
  }
}

/* ============================================================
 * FULL-BLEED HERO VIDEO PLATE  (.hero-video-plate--full)
 *
 * /industries opts in with this second modifier (still carries the base
 * .hero-video-plate class, so it inherits the :has() parent rule above that
 * drops the illustration layer, and the display:none breakpoints/reduced-
 * motion query below 1280px). What it overrides is the GEOMETRY: the base
 * plate is confined to the right ~48% because /design's copy sits in a fixed
 * 34rem column with a clear gutter beside it. /industries was asked to read
 * like /telecommunications instead, whose photo runs the full width of the
 * band (background-size: cover on the .hero-shell itself) with the scrim
 * doing the work of keeping the copy side dark - so this plate runs edge to
 * edge too, and gets its OWN scrim layer rather than borrowing the parent's.
 *
 * Why it needs its own scrim instead of just widening the mask: the parent's
 * left-weighted gradient (.hero-shell.pb-hero-banner's own background-image)
 * paints BEHIND this plate, and a positioned child always paints in front of
 * its parent's background regardless of the child's opacity. A photo can be
 * one of three stacked background-image layers on the same element, so the
 * scrim layer stays visually in front of it; a <video> cannot join that
 * stack (backgrounds don't animate), so it has to be a real element, which
 * puts it ABOVE the parent's scrim in paint order. The ::after below is the
 * scrim redone as a layer that paints after the video within this plate's own
 * stacking context, matching the same 100deg axis and roughly the same alpha
 * ramp as the parent rule for continuity between the two treatments.
 * ============================================================ */
.hero-shell.pb-hero-banner .hero-video-plate--full {
  left: 0;
  max-width: none;
  -webkit-mask-image: none;
  mask-image: none;
}
.hero-shell.pb-hero-banner .hero-video-plate--full > .hero-video {
  object-position: center center;
  opacity: 0.92;
  z-index: 0;
}
.hero-shell.pb-hero-banner .hero-video-plate--full::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-image: linear-gradient(100deg,
    rgba(6, 20, 54, 0.62) 0%,
    rgba(8, 28, 74, 0.42) 28%,
    rgba(10, 40, 98, 0.18) 52%,
    rgba(12, 50, 118, 0) 74%);
}

/* ============================================================
 * LIGHT THEME: THE FOUR FULL-BLEED BANDS GO PALE  (2026-08)
 *
 * This block deliberately reverses, FOR THE LIGHT THEME ONLY, the
 * "dark on BOTH themes" decision recorded four times above - once in each
 * of .trust-art, .whyus-art, .services-art and .frameworks-art.
 *
 * WHY THOSE NOTES WERE RIGHT AND ARE STILL RIGHT IN ISOLATION. Each band
 * was made a fixed dark backdrop so it would read like the hero rather
 * than like an inset panel, and each note says so. Taken one band at a
 * time that is correct and the alternative (a pale wash under navy copy)
 * is what those notes rejected.
 *
 * WHAT THEY MISS IS THE PAGE. Every one of them reasons about its own
 * band. Stacked, on the light theme, the homepage is:
 *
 *     hero            brand blue      L 0.05
 *     #trust          near-black      L 0.05
 *     #reviews        page white      L 0.98
 *     #services       near-black      L 0.06
 *     #services-canada page white     L 0.98
 *     #why-us         near-black      L 0.05
 *     #industries     page white      L 0.98
 *     #frameworks     near-black      L 0.06
 *     #products       page white      L 0.98
 *
 * i.e. a zebra: five near-black bands alternating with white, ~0.93
 * luminance a side, six times down one page. On the DARK theme the same
 * bands are a subtle tonal shift against a near-black page and the whole
 * device works. On the light theme it is the page's dominant visual event,
 * and it is what "bright mode is not good, the hero section and the next
 * section have huge variation, that's how all the pages are" describes.
 *
 * THE FIX IS TONE, NOT REMOVAL. The bands are load-bearing - they are what
 * gives the page its rhythm, and deleting them would flatten it into one
 * undifferentiated white scroll. So on the light theme they become PALE
 * tinted bands instead of dark ones: same geometry, same full-bleed, same
 * edge-ramp masks, same art, ~0.06 luminance step against white instead of
 * ~0.93. The page reads as one light document with soft banding.
 *
 * SUBPAGE HEROES NEED NOTHING HERE. Their banner heroes already dissolve
 * to #FFFFFF on the light theme (see the light branch of
 * .hero-shell.pb-hero-banner::before - measured 0/0/0 delta at the seam).
 * The four bands this block is named for are homepage-only, hence the
 * [data-route="home"] scope on the copy rules.
 *
 * SUBPAGE BODIES DO NOT. This block once said "subpages need nothing here"
 * full stop, and that was true when it was written: their bodies were all
 * page-surface. "Sub-page body rhythm + art bands" (l. ~6844) landed after
 * it and put TWO more full-bleed near-black bands - data-band="anchor" and
 * data-band="photo" - on every /services and /industries route, on both
 * themes, reproducing the exact zebra this block exists to kill. The
 * light-theme reversal for those two lives at the end of this file under
 * "LIGHT THEME: THE SUB-PAGE BODY BANDS GO PALE"; keep the two in step.
 * ============================================================ */

/* One pale palette for all four bands, so they cannot drift apart. Both
   stops are a brand-blue tint rather than neutral grey: the art behind
   three of the four is blue, and a grey field over blue art reads as
   dirty rather than as a tint. */
:root[data-theme="light"] {
  --pb-band-top: #F1F5FD;
  --pb-band-bot: #E4ECF9;
}

/* ── The art boxes ───────────────────────────────────────────
   The masks are NOT touched: they already fade the whole box out to
   reveal whatever the page is behind it, which is why they were written
   that way and why they need no light-theme variant. */
:root[data-theme="light"] .trust-art,
:root[data-theme="light"] .whyus-art,
:root[data-theme="light"] .frameworks-art {
  background: linear-gradient(180deg, var(--pb-band-top) 0%, var(--pb-band-bot) 100%);
}
/* .services-art carries no <img> !it is four stacked gradients, so it
   keeps its own composition and only the values come down. The three
   radials are what give this band its light-from-upper-right shape; at
   these alphas they survive as a tint instead of a glow. */
:root[data-theme="light"] .services-art {
  background:
    radial-gradient(70% 120% at 88% 6%, rgba(126, 174, 255, 0.22), transparent 58%),
    radial-gradient(95% 85% at 62% 20%, rgba(43, 110, 220, 0.14), transparent 66%),
    radial-gradient(85% 95% at 10% 80%, rgba(15, 79, 186, 0.10), transparent 70%),
    linear-gradient(155deg, var(--pb-band-top) 0%, var(--pb-band-bot) 48%, var(--pb-band-top) 100%);
}

/* The renders are light-lines-on-dark (a secure grid, a particle wave, a
   data field). invert() is what makes them usable rather than a dark
   rectangle: inverted they are dark-lines-on-light, which is exactly what
   a pale band wants. grayscale first so inverting a blue cast does not
   produce an orange one. mix-blend-mode is reset because .trust-art's img
   sets luminosity - a duotone trick that needs a saturated field to blend
   onto and has nothing to do on a pale one. */
:root[data-theme="light"] .trust-art img,
:root[data-theme="light"] .whyus-art img,
:root[data-theme="light"] .frameworks-art img {
  mix-blend-mode: normal;
  filter: grayscale(1) invert(1) contrast(0.82) brightness(1.04);
  opacity: 0.16;
}

/* The scrims held white copy on a dark field. Inverted: they now LIGHTEN,
   keeping the middle of each band open where the copy sits and closing
   slightly at both edges so the ramp still has something to dissolve. */
:root[data-theme="light"] .trust-art::after,
:root[data-theme="light"] .whyus-art::after,
:root[data-theme="light"] .frameworks-art::after,
:root[data-theme="light"] .services-art::after {
  background: linear-gradient(180deg,
    rgba(255, 255, 255, 0.36) 0%,
    rgba(255, 255, 255, 0.14) 34%,
    rgba(255, 255, 255, 0.14) 66%,
    rgba(255, 255, 255, 0.40) 100%);
}

/* ── #trust and #why-us take a deeper tint  (2026-08-06) ─────
   At the shared --pb-band-* values these two read as plain white on a bright
   screen: the blue is technically present and practically invisible, which is
   what "increase the background blue so the blue will be visible" describes.

   THEY GET THEIR OWN PAIR rather than a change to --pb-band-top/bot, even
   though the note on that pair asks for one palette so the bands cannot drift.
   That token now drives more than the four homepage bands it was written for -
   "LIGHT THEME: THE SUB-PAGE BODY BANDS GO PALE" below reuses it for
   data-band="anchor" and data-band="photo" on ~20 /services and /industries
   routes. Moving it would repaint every one of those to satisfy a request
   about two homepage bands.

   THE OTHER TWO ARE LEFT ALONE ON PURPOSE. #services already stacks its own
   three radials over the pale field and reads blue without help; #frameworks
   is pale but was not asked about, and its dotted wave carries that band's
   interest instead. If they should follow, the fix is to add them to these two
   selectors - not to move --pb-band-*.

   The step is deliberately small: ~5% luminance and ~8% chroma against white,
   which is a visible tint and nowhere near the hero's saturated field. The
   brief was "visible, not hero blue".

   Source order is load-bearing. These selectors are specificity-identical to
   the two rules above ((0,3,1) for the ::after pair), so they win only by
   sitting after them. Do not hoist this block. */
:root[data-theme="light"] {
  --pb-band-blue-top: #E3EBFA;
  --pb-band-blue-bot: #CCDCF5;
}
:root[data-theme="light"] .trust-art,
:root[data-theme="light"] .whyus-art {
  background: linear-gradient(180deg, var(--pb-band-blue-top) 0%, var(--pb-band-blue-bot) 100%);
}
/* The shared scrim lightens by up to 40% at the edges. That was free at the
   pale values; at these it would wash most of the tint straight back out, so
   these two get a gentler one. Same four stops and same shape, so the edge
   ramps still have something to dissolve into. */
:root[data-theme="light"] .trust-art::after,
:root[data-theme="light"] .whyus-art::after {
  background: linear-gradient(180deg,
    rgba(255, 255, 255, 0.24) 0%,
    rgba(255, 255, 255, 0.06) 34%,
    rgba(255, 255, 255, 0.06) 66%,
    rgba(255, 255, 255, 0.28) 100%);
}

/* The hero seam, re-measured. .trust-art's top: -3rem exists to cover the
   page-coloured gap between the hero and #trust (1px hairline + its clamp()
   margins) - see the offset's own note, which sizes it at "~47px, the widest
   the gap gets". The gap is now wider than that: measured hero-bottom to
   #trust-top at 2026-08-06, it runs 62px at 390 and 768, 65 at 1024, 69 at
   1280, 71 at 1440 and 78 at 1920 and above. So 48px of cover leaves 14-30px
   of page white showing through.

   That sliver was invisible while this band's top was #F1F5FD and is not at
   #E3EBFA - it reads as a white stripe under the hero, which is the exact
   "blue, sudden white, sudden band" seam both notes exist to prevent.

   5rem = 80px clears the widest measured gap. The overshoot is safe BECAUSE
   of the ramp change above: the hero's ::before now ends on this band's own
   top colour, so wherever the art overshoots it lands on an identical colour.
   Light theme only - the dark theme's hand-off is mask-based, its 48px shows
   no seam (verified), and it is left exactly as it shipped. */
:root[data-theme="light"][data-route="home"] .trust-art {
  top: -5rem;
}

/* ── Tokens back to the light ramp ───────────────────────────
   This one rule does most of the work. All four sections rescope the token
   ramp to white-on-dark on themselves, and everything inside them colours
   FROM those tokens - the eight .service-card surfaces (which build from
   --pb-surface), the .fw-chip plates, every .t-eyebrow (--pb-mist), every
   hairline. Restoring the tokens therefore restores the whole interior
   without touching a single component rule. Values are copied from
   :root[data-theme="light"] in Pentabay.html l. 178, not re-invented.
   Specificity (1,3,0) against the source rules' (1,2,0). */
:root[data-theme="light"][data-route="home"] #trust,
:root[data-theme="light"][data-route="home"] #why-us,
:root[data-theme="light"][data-route="home"] #services,
:root[data-theme="light"][data-route="home"] #frameworks {
  --pb-surface: #FFFFFF;
  --pb-elevated-1: #F7F8FA;
  --pb-elevated-2: #EEF1F5;
  --pb-fg: #000000;
  --pb-fg-soft: #000000;
  --pb-muted: #000000;
  --pb-mist: #000000;
  --pb-hairline: #DDE2EA;
  --pb-hairline-60: rgba(221, 226, 234, 0.85);
  --pb-hairline-40: rgba(221, 226, 234, 0.55);
  color: var(--pb-fg);
}

/* ── The runs of copy that are hard-coded white ──────────────
   These sit directly on the band and were given literals precisely
   BECAUSE the band was dark on both themes (see FULL-BLEED BAND COPY), so
   the token restore above cannot reach them. The text-shadow goes with
   them: a 0.55-alpha black drop shadow under black text on a pale field is
   just a smudge. */
:root[data-theme="light"][data-route="home"] #trust > div > div > .t-eyebrow,
:root[data-theme="light"][data-route="home"] #trust > div > div > .t-h2,
:root[data-theme="light"][data-route="home"] #trust > div > div > .t-lead,
:root[data-theme="light"][data-route="home"] #trust > div > div > .btn-link,
:root[data-theme="light"][data-route="home"] #trust .cc-name,
:root[data-theme="light"][data-route="home"] #trust .cc-meta,
:root[data-theme="light"][data-route="home"] #trust > .trust-tally .trust-tally-num,
:root[data-theme="light"][data-route="home"] #trust > .trust-tally .trust-tally-label,
:root[data-theme="light"][data-route="home"] #why-us > div > .t-eyebrow,
:root[data-theme="light"][data-route="home"] #why-us > div > .t-h2,
:root[data-theme="light"][data-route="home"] #why-us > div > .t-lead {
  color: #000000;
  text-shadow: none;
}

/* ── The dark card plates ────────────────────────────────────
   #why-us' four proof cards and five .pb-diff plates were turned into dark
   translucent plates so they would stop reading as white boxes on a dark
   band. With the band pale, white boxes are correct again - that is what
   every other card on the light page is. The token rescope inside them is
   undone for the same reason as the section-level one above. */
:root[data-theme="light"][data-route="home"] #why-us .card,
:root[data-theme="light"][data-route="home"] #why-us .pb-diff {
  background: rgba(255, 255, 255, 0.86);
  border-color: var(--pb-hairline);
  --pb-hairline: #DDE2EA;
  --pb-fg: #000000;
  --pb-muted: #000000;
  --pb-mist: #000000;
  color: var(--pb-fg);
}
/* #7FA9FF was picked to read on a dark plate; on white it is a pale wash.
   Back to the brand token, which is what every other number on the light
   page uses. */
:root[data-theme="light"][data-route="home"] #why-us .pb-diff .pb-diff-num {
  color: var(--pb-blue);
}
/* backdrop-filter: blur() on a near-opaque white plate over a pale band
   costs a compositor layer and buys nothing visible. */
:root[data-theme="light"][data-route="home"] #why-us .card,
:root[data-theme="light"][data-route="home"] #why-us .pb-diff,
:root[data-theme="light"][data-route="home"] #frameworks .fw-chip {
  backdrop-filter: none;
}

/* ── #services card accents: mix toward ink, not toward white ──
   Three rules on .service-card lift their accent by mixing it toward
   #F7F8FB, because a deep #0F4FBA "is too dark to read against the plate" -
   true of a dark plate. On the light plate the mix runs the wrong way: the
   S/01 badge label came out near-white on a pale blue chip and vanished
   completely (this is the one thing the token restore could not fix, because
   the near-white end of each mix is a literal, not a token).

   Reversed here rather than made conditional at the source: those rules are
   correct for the dark theme and are the third batch of accent tuning in that
   block, so they are left exactly as they are.

   The badge lands on the same 10%-fill / 55%-ring pair the light theme
   already uses for the nav's current-page pill, so the two agree. */
:root[data-theme="light"][data-route="home"] #services .service-card .svc-num {
  color: color-mix(in oklab, var(--acc) 88%, #000000);
  background: color-mix(in oklab, var(--acc) 10%, transparent);
  border-color: color-mix(in oklab, var(--acc) 45%, transparent);
}
:root[data-theme="light"][data-route="home"] #services .service-card .card-foot a.btn-link {
  color: var(--acc);
}
/* The unconditional blue lift on card titles existed for the dark plate. On
   white, #000000 is what every other card title on the page uses. */
:root[data-theme="light"][data-route="home"] #services .service-card .svc-head h3 {
  color: var(--pb-fg);
}

/* ── The home hero's hand-off ────────────────────────────────
 * [data-route="home"] .hero-shell::before ramps to #060A14 on BOTH themes,
 * and its note explains why: what follows the hero is #trust, a band that
 * was dark on both themes, so ending on Deep Ink made hero -> #trust one
 * continuous dark passage. That note also says what to do if the premise
 * ever changes - "if #trust ever stops following the hero, change this
 * back to a ramp ending on var(--pb-surface)". The premise has now changed
 * on the light theme: #trust is pale. Ending on Deep Ink there would put a
 * 160px black ramp between a blue hero and a pale band, which is the
 * single worst seam on the light page.
 *
 * It ends on the colour .trust-art's top actually paints, not --pb-surface,
 * because .trust-art starts at top: -3rem and is what covers the 48px
 * directly under the hero. Same nine eased stops, so the curve this was tuned
 * to is unchanged; only the colour it resolves to moves. The dark theme is
 * untouched - there the mask-based hand-off in "HERO -> PAGE HAND-OFF, DARK
 * THEME" switches this ::before off entirely.
 *
 * The colour is --pb-band-blue-top (#E3EBFA), NOT --pb-band-top: #trust took
 * the deeper tint on 2026-08-06 and this ramp has to land on whatever that
 * band's top edge is, or the last 7% of the hero steps onto a different
 * colour and the seam it exists to hide reappears. The eight rgba stops are
 * the same value in numeric form - they must be kept in step by hand, which
 * is why the hex is repeated in the comment above. */
:root[data-theme="light"][data-route="home"] .hero-shell::before {
  background: linear-gradient(to bottom,
    rgba(227, 235, 250, 0) 0%,
    rgba(227, 235, 250, 0.015) 22%,
    rgba(227, 235, 250, 0.05) 38%,
    rgba(227, 235, 250, 0.12) 52%,
    rgba(227, 235, 250, 0.24) 64%,
    rgba(227, 235, 250, 0.42) 75%,
    rgba(227, 235, 250, 0.63) 85%,
    rgba(227, 235, 250, 0.84) 93%,
    var(--pb-band-blue-top) 100%);
}
/* .trust-art's top edge is solid on purpose (it meets the hero). On the
   light theme it now meets the END of that pale ramp instead of Deep Ink,
   which is the same colour - so nothing is needed here beyond leaving the
   solid top alone. Recorded so a future pass does not "fix" it. */

/* ============================================================
 * LIGHT THEME: THE SUB-PAGE BODY BANDS GO PALE  (2026-08)
 *
 * The companion to the homepage block above, for the two full-bleed roles
 * that "Sub-page body rhythm + art bands" (l. ~6844) stamps on every
 * /services and /industries route: data-band="anchor" and
 * data-band="photo". Those were built dark on BOTH themes, copying the
 * token set from the homepage #services band - which by then had already
 * been given a pale light-theme branch. The copy took the dark half only,
 * so twenty routes shipped with the zebra the homepage no longer has:
 * measured on /custom-software at 1440, page white L 0.98 -> anchor band
 * #08101F L 0.05 -> page white, twice down the page.
 *
 * SAME FIX, SAME REASONING as the homepage: tone, not removal. The bands
 * keep their geometry, their full-bleed break-out, their edge-ramp masks
 * and their art. Only the palette flips. Nothing here touches the dark
 * theme, where the dark bands are a subtle tonal shift and work as built.
 *
 * The pale palette is --pb-band-top / --pb-band-bot, reused rather than
 * re-picked, so the sub-pages cannot drift away from the homepage.
 *
 * SPECIFICITY. The source rules are (1,2,2) - .page, :not(#page-home),
 * [data-band], two elements. Prefixing :root[data-theme="light"] makes
 * these (1,4,2), so they win on specificity and not on source order.
 * ============================================================ */

/* ── Tokens back to the light ramp ───────────────────────────
   The same single-rule trick the homepage block uses. Both bands rescope
   the whole token ramp to white-on-dark on themselves (l. ~6889) and
   everything inside colours FROM those tokens - the stack section's cells
   are markup-level bg-[var(--pb-surface)], .svc-cap-card reads
   --pb-elevated-2, .brand-grid reads --pb-elevated-1, every .t-eyebrow
   reads --pb-mist. Restoring the tokens fixes the whole interior without
   touching one component rule. Values copied from :root[data-theme="light"]
   in Pentabay.html l. 178 and the alias block at l. 158, not re-invented:
   --pb-haze* alias --pb-hairline*, --pb-mist aliases --pb-muted and
   --pb-paper aliases --pb-fg, so on this theme all three text tokens are
   #000000 by design ("no grey on the light theme").
   The color declaration is explicit for the reason the source rule gives: html/body
   declare color: var(--pb-fg), which resolved to black AT THE BODY and
   inherited down, so a token rescope alone cannot re-resolve it. */
:root[data-theme="light"] main.page:not(#page-home) > section[data-band="anchor"],
:root[data-theme="light"] main.page:not(#page-home) > section[data-band="photo"] {
  --pb-surface: #FFFFFF;
  --pb-elevated-1: #F7F8FA;
  --pb-elevated-2: #EEF1F5;
  --card-hover-bg: #FFFFFF;
  --frame-bg: linear-gradient(180deg, #FFFFFF, #F4F6FA);
  --pb-fg: #000000;
  --pb-fg-soft: #000000;
  --pb-muted: #000000;
  --pb-mist: #000000;
  --pb-paper: #000000;
  --pb-hairline: #DDE2EA;
  --pb-hairline-60: rgba(221, 226, 234, 0.85);
  --pb-hairline-40: rgba(221, 226, 234, 0.55);
  --pb-haze: #DDE2EA;
  --pb-haze-60: rgba(221, 226, 234, 0.85);
  --pb-haze-40: rgba(221, 226, 234, 0.55);
  color: var(--pb-fg);
}

/* ── The anchor band ─────────────────────────────────────────
   Flat Deep Ink becomes the same top-to-bottom pale blue the homepage
   bands use. background-color is cleared so the gradient is the only
   layer; the mask is untouched, exactly as in the homepage block - it
   already fades the box out at both edges to reveal the page behind it,
   which is what makes the ramp work on any backdrop colour. */
:root[data-theme="light"] main.page:not(#page-home) > section[data-band="anchor"]::before {
  background-color: transparent;
  background-image: linear-gradient(180deg, var(--pb-band-top) 0%, var(--pb-band-bot) 100%);
}

/* ── The photo band ──────────────────────────────────────────
   The dark version is a duotone: [dark scrim, art] with the art blended
   in luminosity against a --pb-blue field, so the art contributes light
   and shade only and the hue comes from the field. That construction
   CANNOT be dialled pale - luminosity blending takes its lightness from
   the art, so a dark render over a pale field stays dark whatever the
   field is. The homepage solves the same problem with
   grayscale(1) invert(1) + opacity 0.16 on an <img>, but this art is a
   background LAYER and CSS filter cannot address one layer of a stack.
   So the light branch drops the blend entirely and goes back to the
   plainest thing that works: a near-opaque pale scrim over the art at
   normal blend, which leaves the render showing through at roughly 8-12%
   as a soft blue ghost. Same read as the homepage's 0.16 <img>, reached a
   different way because the mechanism is different.
   178deg (not 180) is kept from the source so the two edges stay very
   slightly asymmetric, matching the dark version's composition. */
:root[data-theme="light"] main.page:not(#page-home) > section[data-band="photo"]::before {
  background-color: #FFFFFF;
  background-image:
    linear-gradient(178deg,
      rgba(241, 245, 253, 0.95) 0%,
      rgba(228, 236, 249, 0.91) 45%,
      rgba(241, 245, 253, 0.95) 100%),
    var(--pb-body-art, none);
  background-blend-mode: normal, normal;
}

/* ── Cards on the two bands ──────────────────────────────────
   Dark translucent plates existed because a near-white .card on a dark
   band was the wrong object. With the band pale, a white plate is right
   again - it is what every other card on the light page is. The blur goes
   with them for the reason the homepage block gives: backdrop-filter on a
   near-opaque white plate over a pale band costs a compositor layer and
   buys nothing visible. */
:root[data-theme="light"] main.page:not(#page-home) > section[data-band="anchor"] .card,
:root[data-theme="light"] main.page:not(#page-home) > section[data-band="photo"] .card,
:root[data-theme="light"] main.page:not(#page-home) > section[data-band="anchor"] article.card,
:root[data-theme="light"] main.page:not(#page-home) > section[data-band="photo"] article.card {
  background: rgba(255, 255, 255, 0.86);
  border-color: var(--pb-hairline);
  backdrop-filter: none;
}

/* =============================================================
 * LIGHT THEME PAGE WASH  (2026-08)
 *
 * The ~15 routes with no BODY_ART entry - /about, /trust, /compliance,
 * /careers, /contact-us, /blog, /our-work, /our-clients, the three
 * location pages, the two policy pages and every blog article - have no
 * full-bleed art anywhere on them. On the light theme that leaves them solid
 * white end to end: measured on /about, body #FFFFFF, --pb-surface #FFFFFF,
 * the page element itself transparent. White page, white cards, hairline
 * borders, and the only colour on 5000px of scroll is the closing CTA band.
 * It does not read as minimal, it reads as a page that failed to load.
 *
 * Those pages are stamped [data-page-wash="1"] by applyBodyBands() in
 * public/pentabay-page-imagery.js - see the long note there for why the flag is
 * positive rather than a :not() on the art pages.
 *
 * ── Why a background on the element and not a ::before layer ──────────────
 * A z-index:-1 pseudo-element is the obvious way to lay art behind a page, and
 * it is the one that already cost this codebase a real bug: a negative z-index
 * escapes to the nearest ANCESTOR STACKING CONTEXT, so the layer sinks behind
 * the page background and measures as a 0/255 contribution (see the isolation
 * note in src/CtaReveal.jsx). main.page is transparent, so painting straight
 * onto it needs no stacking games and cannot sink: every section, card and
 * .frame inside it has its own background and composites on top naturally.
 *
 * ── Why the white scrim on top of the image ───────────────────────────────
 * The asset is a saturated blue at its left edge. Unscrimmed behind a page whose
 * cards are 86-100% white, the gaps between cards would read as a much stronger
 * blue than the cards, i.e. the background would out-shout the content. The
 * two-stop white gradient holds it to a wash. Tune THESE numbers to change the
 * intensity - do not re-export the asset. */
:root[data-theme="light"] main.page[data-page-wash="1"] {
  background-image:
    linear-gradient(180deg, rgba(255,255,255,0.68) 0%, rgba(255,255,255,0.80) 55%, rgba(255,255,255,0.86) 100%),
    url('/assets/page-wash-blue.webp');
  background-repeat: no-repeat, no-repeat;
  background-size: cover, cover;
  background-position: center top, center top;
}
/* Fixed attachment keeps the wash still while the copy scrolls, so one 1672px
   asset covers a 5000px page without being stretched 5x vertically. Gated on a
   hover-capable pointer because iOS Safari mis-renders background-attachment:
   fixed (it sizes against the document, not the viewport) and because a fixed
   background repaints on every scroll frame, which is the wrong trade on a
   phone. Touch devices fall back to the default \`scroll\`, where \`cover\` stretches
   the gradient down the page - acceptable precisely because it is a soft
   gradient with no detail to smear. */
@media (min-width: 1024px) and (hover: hover) {
  :root[data-theme="light"] main.page[data-page-wash="1"] {
    background-attachment: fixed, fixed;
  }
}
/* The dark theme is deliberately untouched. These same pages already read rich
   on dark (the surface is near-black and every card is an elevated plate), and
   this asset is a LIGHT gradient - forcing it in would either wash the page out
   or need a second, differently-toned export for no measured benefit. */

/* =============================================================
 * LIGHT THEME SECTION WASH  (2026-08)
 *
 * The companion to the page wash above, for the OTHER 20 routes - every service
 * and industry page. Those pages do have art, but only on 3-4 sections: one
 * \`anchor\` band, one \`photo\` band and the alternating \`panel\` bands. Every
 * section in between was left background-transparent, so on the light theme it
 * shows the white page through. Measured on /cloud-devops light: sections 2, 3,
 * 6 and 8 all compute rgba(0, 0, 0, 0) against a #FFFFFF page, while panel
 * computes rgb(247, 248, 250). So the page alternates styled band, white gap,
 * styled band, white gap.
 *
 * applyBodyBands() in public/pentabay-page-imagery.js stamps those gaps
 * [data-band-wash="1"] - hero and CTA excluded, since neither is white.
 *
 * ── Full-bleed, via the same breakout the real bands use ──────────────────
 * These sections ARE .shell (max-width + auto margins). A background on the
 * element would stop at the text column and read as a floating tinted rectangle
 * with white margins either side - which is worse than leaving it white, because
 * it fights the full-bleed bands directly above and below it. So the wash goes
 * on a ::before using the 50% / 100vw / translateX(-50%) breakout copied from
 * section[data-band="anchor"]. html and body already carry overflow-x: clip, so
 * 100vw cannot raise a scrollbar.
 *
 * ── The ramp is load-bearing, and it is a PERCENTAGE ──────────────────────
 * A flat inset:0 wash starts and stops on a single pixel row. Subtle as this
 * tint is, that still draws a visible horizontal line where it meets the white
 * hairline divider between sections. The mask fades both edges so consecutive
 * sections blend instead of banding - same reasoning as --band-ramp on the real
 * bands.
 * The real bands use a px ramp because they are tall (694-1379px measured).
 * These sections are not: they run 187-615px, and a fixed 56px ramp at each end
 * spent 112 of a 217px section on fade, leaving 105px of actual wash - measured
 * on /healthcare "Tools we trust." and it read as a smudge rather than a band.
 * A percentage scales with the section instead: ~19px of ramp on a 187px strip,
 * ~61px on a 615px section, correct at both ends with one rule and no
 * height-sniffing special case. */
:root[data-theme="light"] main.page > section[data-band-wash="1"] {
  position: relative;
  isolation: isolate;
}
:root[data-theme="light"] main.page > section[data-band-wash="1"] > * {
  position: relative;
  z-index: 1;
}
:root[data-theme="light"] main.page > section[data-band-wash="1"]::before {
  content: "";
  position: absolute;
  top: 0; bottom: 0; left: 50%;
  width: 100vw;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  background-image:
    linear-gradient(180deg, rgba(255,255,255,0.74) 0%, rgba(255,255,255,0.84) 100%),
    url('/assets/page-wash-blue.webp');
  background-repeat: no-repeat, no-repeat;
  background-size: cover, cover;
  background-position: center center, center center;
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 10%, #000 90%, transparent 100%);
          mask-image: linear-gradient(180deg, transparent 0, #000 10%, #000 90%, transparent 100%);
}
`

export default function StyleOverrides() {
  useEffect(() => {
    const el = document.createElement('style')
    el.setAttribute('data-pb-style-overrides', '')
    el.textContent = OVERRIDES
    document.head.appendChild(el)
    return () => el.remove()
  }, [])
  return null
}
